import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import L from "leaflet";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";

L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl
});

import GoogleSignIn from "./auth/GoogleSignIn.jsx";
import PropertyMap from "./components/PropertyMap.jsx";
import { LISTINGS } from "./data/listings";
import { useSellerValuation, formatComparable, getConfidenceExplanation } from "./hooks/useSellerValuation.js";
import { normalisePostcode, verifyPostcode } from "./lib/postcodes";
import { isEmail, isNumber, notEmpty, isUKPostcode } from "./lib/validators";
import { saveSection, getSection } from "./lib/store";
import { Roles, getSession, clearSession, signInSession, SESSION_CHANGED_EVENT, cleanupLegacyStorage } from "./auth/session.js";
import { MOCK_ACCOUNTS } from "./auth/mockAccounts.js";
import ComingSoon from "./pages/ComingSoon.jsx";
import SellerCompliance from "./routes/SellerCompliance.jsx";
import { FAQ_BLOCK } from "./data/faqs.js";
import { getProfile, getAIRecommendations, updateProfile, getListings, getPublicListings, submitValuationRequest, createViewing, createOffer, postAIValuation } from "./api/client.js";
import { createPaymentIntent, getPaymentStatus } from "./api/payments.js";
import { syncSentryUser } from "./monitoring/sentry.js";
import CrmLayout, {
  CrmDashboardPage,
  CrmContactsPage,
  CrmContactCreatePage,
  CrmPipelinePage,
  CrmViewingsPage,
  CrmOffersPage,
  CrmSalesPage,
  CrmLettersPage,
  CrmSettingsPage,
  CrmListingsPage,
  CrmListingDetailPage,
  CrmDeletedContactsPage,
  CrmAmlReviewPage,
  CrmAuditLogPage,
  CrmAiLogsPage,
  CrmGdprPage,
  CrmPaymentHistoryPage
} from "./crm/CrmLayout.jsx";
import AIChatModal from "./components/AIChatModal.jsx";

/* ----------------------- auth helpers ----------------------- */
function useCurrentSession() {
  const [session, setSession] = useState(() => getSession());
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const setAndSync = (value) => {
      setSession(value);
      syncSentryUser(value);
    };
    setAndSync(getSession());

    // Try to refresh session from API on mount
    const init = async () => {
      try {
        const { refreshSessionFromAPI } = await import("./auth/session.js");
        const refreshed = await refreshSessionFromAPI();
        if (refreshed) {
          setAndSync(refreshed);
        } else {
          setAndSync(getSession());
        }
      } catch (error) {
        console.error("Failed to refresh session:", error);
        setAndSync(getSession());
      } finally {
        setLoading(false);
      }
    };
    
    init();
    
    const update = () => setAndSync(getSession());
    window.addEventListener(SESSION_CHANGED_EVENT, update);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, update);
  }, []);
  
  return { session, loading };
}

/* ----------------------- CRM Portals ----------------------- */
function PortalShell({ session, title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-16">
      <div className="max-w-6xl mx-auto px-6 pt-16 space-y-8">
        <div className="flex flex-col items-center text-center gap-3">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-500">{title}</p>
          <h1 className="text-3xl font-semibold text-white">
            Welcome back, {session?.name || session?.email || "member"}
          </h1>
          {subtitle && <p className="text-sm text-slate-300 max-w-2xl">{subtitle}</p>}
          <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.3em] text-slate-300">
            {deriveSessionLabel(session)}
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

function BuyerOverviewCards({ search, upcoming }) {
  const budget = search?.budgetMax ? formatAmount(Number(search.budgetMax)) : "Set a budget";
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Active search</p>
          <span className="text-[10px] uppercase tracking-wider text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded-full" title="Saved locally on this device only">
            Local
          </span>
        </div>
        <h3 className="mt-2 text-lg font-semibold text-white">
          {search?.area || "Add target area"}
        </h3>
        <p className="mt-1 text-sm text-slate-300">
          Budget up to <span className="font-medium text-white">{budget}</span>
        </p>
        <p className="mt-3 text-xs text-slate-500">
          Bedrooms: {search?.bedsMin ?? "n/a"} – {search?.bedsMax ?? "∞"} • Type: {search?.type || "Any"}
        </p>
      </div>
      <div className="rounded-3xl border border-white/10 bg-emerald-500/10 p-5">
        <p className="text-xs uppercase tracking-[0.3em] text-emerald-200">Next viewing</p>
        <h3 className="mt-2 text-lg font-semibold text-white">
          {upcoming[0]?.listing?.title || "No viewing booked"}
        </h3>
        <p className="mt-1 text-sm text-emerald-100">
          {upcoming[0]
            ? `${upcoming[0].date} • ${upcoming[0].listing?.area || ""}`
            : "Book or request a viewing to see it here"}
        </p>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Status</p>
        <h3 className="mt-2 text-lg font-semibold text-white">Mortgage in principle</h3>
        <p className="mt-1 text-sm text-slate-300">Upload documentation to accelerate offer approval.</p>
      </div>
    </div>
  );
}

function Toast({ open, onClose, message, type = "success" }) {
  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [open, onClose]);

  if (!open) return null;

  const styles = {
    success: "bg-emerald-500 border-emerald-600",
    error: "bg-red-500 border-red-600",
    info: "bg-blue-500 border-blue-600"
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
      <div className={`${styles[type]} text-white px-6 py-3 rounded-xl border shadow-lg flex items-center gap-3`}>
        <span className="text-xl">{type === "success" ? "✓" : type === "error" ? "✕" : "ℹ"}</span>
        <span className="font-medium">{message}</span>
      </div>
    </div>
  );
}

function BuyerCardsGrid({ title, listings, emptyCopy }) {
  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          {emptyCopy && listings.length === 0 && (
            <p className="text-sm text-slate-400">{emptyCopy}</p>
          )}
        </div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {listings.slice(0, 6).map((listing) => (
          <div key={listing.id} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
            <div className="h-36">
              <img
                src={listing.hero || listing.photos?.[0]}
                alt={listing.title}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-4 space-y-2">
              <h3 className="text-base font-medium text-white line-clamp-2 min-h-[44px]">{listing.title}</h3>
              <p className="text-sm text-slate-300">{listing.area} • {listing.postcode}</p>
              <div className="text-lg font-semibold text-white">{formatAmount(listing.price)}</div>
              <p className="text-xs text-slate-500">
                {listing.beds} beds • {listing.baths ?? "—"} baths • {listing.type || "Property"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ActiveSearchModal({ open, value, onSave, onClose }) {
  const [form, setForm] = useState(value);

  useEffect(() => {
    setForm(value);
  }, [value]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-[440px] rounded-2xl bg-slate-950 p-6">
        <h3 className="text-lg font-semibold text-white">Edit active search</h3>
        <div className="mt-2 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-200">
          ✓ This search is saved to your HOME AI account and synced across devices. You'll receive email alerts when new matching properties are listed.
        </div>
        <form
          className="mt-4 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(form);
          }}
        >
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            Target area
            <input
              value={form.area || ""}
              onChange={(event) => setForm((prev) => ({ ...prev, area: event.target.value }))}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
              Budget min
              <input
                type="number"
                value={form.budgetMin ?? ""}
                onChange={(event) => setForm((prev) => ({ ...prev, budgetMin: Number(event.target.value) || "" }))}
                className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400"
              />
            </label>
            <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
              Budget max
              <input
                type="number"
                value={form.budgetMax ?? ""}
                onChange={(event) => setForm((prev) => ({ ...prev, budgetMax: Number(event.target.value) || "" }))}
                className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400"
              />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
              Beds min
              <input
                type="number"
                value={form.bedsMin ?? ""}
                onChange={(event) => setForm((prev) => ({ ...prev, bedsMin: Number(event.target.value) || "" }))}
                className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400"
              />
            </label>
            <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
              Beds max
              <input
                type="number"
                value={form.bedsMax ?? ""}
                onChange={(event) => setForm((prev) => ({ ...prev, bedsMax: Number(event.target.value) || "" }))}
                className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400"
              />
            </label>
          </div>
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            Property type
            <input
              value={form.type || ""}
              onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400"
            />
          </label>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-2xl border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/5">
              Cancel
            </button>
            <button type="submit" className="rounded-2xl border border-emerald-400/40 bg-emerald-500/80 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500/70">
              Save search
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProfileSettingsModal({ open, onClose, onSaved }) {
  const session = getSession();
  const [form, setForm] = useState({
    name: "",
    avatarUrl: "",
    phone: "",
    notes: "",
    additionalEmails: "",
    associatedMembers: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && session) {
      setForm({
        name: session.user?.name || "",
        avatarUrl: session.user?.avatarUrl || "",
        phone: session.user?.phone || "",
        notes: session.user?.notes || "",
        additionalEmails: "",
        associatedMembers: ""
      });
    }
  }, [open, session]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Add API call here to save profile settings
      console.log("Saving profile:", form);
      
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-[440px] rounded-2xl bg-slate-950 p-6">
        <h3 className="text-lg font-semibold text-white">Profile Settings</h3>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            Name
            <input
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Your name"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            Phone
            <input
              value={form.phone}
              onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
              placeholder="+44 7..."
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            Additional email
            <input
              value={form.additionalEmails}
              onChange={(event) => setForm((prev) => ({ ...prev, additionalEmails: event.target.value }))}
              placeholder="partner@example.com"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            Associated members
            <input
              value={form.associatedMembers}
              onChange={(event) => setForm((prev) => ({ ...prev, associatedMembers: event.target.value }))}
              placeholder="Spouse, partner names"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="block text-xs uppercase tracking-[0.35em] text-slate-400">
            About you
            <textarea
              rows={3}
              value={form.notes}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>
          {error && <p className="text-xs text-rose-400">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-2xl border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/5">
              Cancel
            </button>
            <button type="submit" className="rounded-2xl border border-emerald-400/40 bg-emerald-500/80 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500" disabled={loading}>
              {loading ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BuyerPortal({ session }) {
  const navigate = useNavigate();
  const [activeSearch, setActiveSearch] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(true);
  const [searchError, setSearchError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });

  // Load latest saved search from API on mount
  useEffect(() => {
    async function loadLatestSearch() {
      try {
        setSearchLoading(true);
        const latest = await latestSearch();
        if (latest?.filters) {
          setActiveSearch(latest.filters);
        }
      } catch (error) {
        console.error("Failed to load saved search:", error);
        setSearchError("Could not load saved searches");
      } finally {
        setSearchLoading(false);
      }
    }
    loadLatestSearch();
  }, []);

  const handleSaveSearch = async (next) => {
    try {
      await saveSearch(next);
      setActiveSearch(next);
      setLogEntries((prev) => [
        ...prev,
        { timestamp: Date.now(), detail: `Saved search for ${next.area || "broad area"}` }
      ]);
      setModalOpen(false);
      setToast({ open: true, message: "Search saved! You'll be notified of new matches.", type: "success" });
    } catch (error) {
      console.error("Failed to save search:", error);
      setToast({ open: true, message: "Failed to save search. Please try again.", type: "error" });
    }
  };

  const [nextViewing, setNextViewing] = useState({
    title: "Elegant 3-bed in Kensington",
    schedule: "Thu 14 Nov • 15:00",
    area: "Kensington"
  });

  const tasks = [
    "Upload proof of funds",
    "Confirm mortgage advisor details",
    "Review Agent Apex notes"
  ];

  const leisure = useMemo(() => {
    if (!activeSearch || Object.keys(activeSearch).length === 0) return LISTINGS.slice(0, 6);
    const matches = LISTINGS.filter((listing) => listingMatches(activeSearch, listing));
    return matches.length ? matches.slice(0, 6) : LISTINGS.slice(0, 6);
  }, [activeSearch]);

  const activeMatches = useMemo(() => {
    return LISTINGS.filter((listing) => {
      const areaMatch = activeSearch.area ? (listing.address?.city || "").toLowerCase().includes(activeSearch.area.toLowerCase()) : true;
      const bedsMinMatch = activeSearch.bedsMin ? listing.beds >= activeSearch.bedsMin : true;
      const bedsMaxMatch = activeSearch.bedsMax ? listing.beds <= activeSearch.bedsMax : true;
      const budgetMinMatch = activeSearch.budgetMin ? listing.price >= activeSearch.budgetMin : true;
      const budgetMaxMatch = activeSearch.budgetMax ? listing.price <= activeSearch.budgetMax : true;
      return areaMatch && bedsMinMatch && bedsMaxMatch && budgetMinMatch && budgetMaxMatch;
    });
  }, [activeSearch]);

  const favourites = useMemo(() => {
    const favs = loadJSON(KEYS.FAVS, {});
    return LISTINGS.filter((listing) => favs[listing.id]);
  }, []);

  return (
    <>
      <ActiveSearchModal open={modalOpen} value={activeSearch} onSave={handleSaveSearch} onClose={() => setModalOpen(false)} />
      <PortalShell
        session={session}
        title="Buyer workspace"
        subtitle="Track viewings, shortlist homes, and monitor offer progress in one place."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <button onClick={() => setModalOpen(true)} className="rounded-3xl border border-white/10 bg-white/5 p-5 text-left text-sm text-white transition hover:border-emerald-400">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Active search</p>
            <p className="text-lg font-semibold text-white mt-2">{activeSearch.area || "Add target area"}</p>
            <p className="text-xs text-slate-400 mt-1">{activeSearch.budgetMax ? `Budget £${activeSearch.budgetMax}` : "Budget up to Set a budget"}</p>
            <p className="text-xs text-slate-500 mt-2">
              Bedrooms {activeSearch.bedsMin || "n/a"}–{activeSearch.bedsMax || "∞"} • {activeSearch.type || "Any"}
            </p>
          </button>
          <button
            onClick={() => navigate("/buyer/my-offers")}
            className="rounded-3xl border border-white/10 bg-blue-500/10 p-5 text-left text-sm text-white transition hover:border-blue-400"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">My Offers</p>
            <p className="text-lg font-semibold text-white mt-2">Track your offers</p>
            <p className="text-xs text-slate-400 mt-1">View status & next steps</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-full border border-white/10 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-slate-200">Dashboard</span>
            </div>
          </button>
          <button
            onClick={() => navigate("/crm/viewings")}
            className="rounded-3xl border border-white/10 bg-emerald-500/10 p-5 text-left text-sm text-white"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Viewings</p>
            <p className="text-lg font-semibold text-white mt-2">{nextViewing.title}</p>
            <p className="text-xs text-slate-400 mt-1">{nextViewing.schedule}</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-full border border-white/10 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-slate-200">Calendar</span>
              <span className="text-[10px] text-slate-400">Click to open schedule</span>
            </div>
          </button>
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 text-sm text-white">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">AI assistant</p>
            <p className="text-lg font-semibold text-white mt-2">Outstanding tasks</p>
            <ul className="mt-3 space-y-1 text-xs text-slate-300">
              {tasks.map((task) => (
                <li key={task}>• {task}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setProfileModalOpen(true)}
              className="mt-3 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.35em] text-white hover:border-emerald-400 hover:text-white hover:bg-emerald-500/10"
            >
              Profile settings
            </button>
          </div>
        </div>
        {activeMatches.length > 0 && (
          <BuyerCardsGrid
            title="Active search matches"
            listings={activeMatches.slice(0, 6)}
            emptyCopy="No properties match your active search yet."
          />
        )}
        <BuyerCardsGrid
          title="Recommended for you"
          listings={leisure}
          emptyCopy="Add filters or favourite homes to personalise this feed."
        />
        <BuyerCardsGrid
          title="Saved homes"
          listings={favourites}
          emptyCopy="No favourites yet. Tap the heart on listings to save them."
        />
      </PortalShell>
      <ProfileSettingsModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} onSaved={() => setProfileModalOpen(false)} />
      <Toast open={toast.open} onClose={() => setToast({ ...toast, open: false })} message={toast.message} type={toast.type} />
    </>
  );
}

function BuyerMyOffers({ session }) {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const email = session?.email;

  const formatListingAddress = (address) => {
    if (!address) return "Property";
    if (typeof address === "string") return address;
    const parts = [address.line1 || address.address, address.line2, address.city, address.postcode]
      .filter(Boolean)
      .join(", ");
    return parts || "Property";
  };

  useEffect(() => {
    async function loadOffers() {
      if (!email) return;
      try {
        setLoading(true);
        const response = await fetch(`/api/offers`);
        if (!response.ok) throw new Error("Failed to load offers");
        const data = await response.json();
        setOffers(data.items || data.offers || []);
      } catch (err) {
        console.error("Error loading offers:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadOffers();
  }, [email]);

  if (!session) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Please log in to view your offers</p>
          <button
            onClick={() => navigate("/buyer")}
            className="px-6 py-3 bg-black text-white rounded-xl hover:bg-black/90"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-neutral-50 p-6">
      <HomeButton />
      <div className="max-w-5xl mx-auto mt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-neutral-900">My Offers</h1>
          <p className="text-neutral-600 mt-2">Track all your property offers in one place</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            <p className="mt-4 text-neutral-600">Loading your offers...</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-300 bg-red-50 text-red-900 p-6">
            <p className="font-medium">Error loading offers</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-12 rounded-2xl border-2 border-dashed border-neutral-300 bg-white">
            <p className="text-lg font-medium text-neutral-900">No offers yet</p>
            <p className="text-neutral-600 mt-2">When you make an offer on a property, it will appear here</p>
            <button
              onClick={() => navigate("/buyer/discover")}
              className="mt-6 px-6 py-3 bg-black text-white rounded-xl hover:bg-black/90"
            >
              Browse Properties
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {offers.map((offer) => (
              <div key={offer.id} className="rounded-2xl border border-neutral-200 bg-white p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-neutral-900">
                        {offer.listing?.refCode || formatListingAddress(offer.listing?.address)}
                      </h3>
                      <StatusBadge status={offer.status} showIcon />
                    </div>
                    <p className="text-sm text-neutral-600 mb-3">
                      Offer made {new Date(offer.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric"
                      })}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-600">Your offer</p>
                        <p className="font-semibold text-neutral-900">
                          £{offer.amount?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-600">Asking price</p>
                        <p className="font-medium text-neutral-700">
                          £{offer.listing?.priceGuide?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {offer.conditions && (
                      <div className="mt-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
                        <p className="text-xs text-neutral-600 mb-1">Your message</p>
                        <p className="text-sm text-neutral-900">{offer.conditions}</p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (offer.listingId) {
                        navigate(`/listing/${offer.listingId}`);
                      } else {
                        navigate("/buyer/discover");
                      }
                    }}
                    className="px-4 py-2 rounded-xl border border-neutral-300 hover:bg-neutral-100 text-sm"
                  >
                    View Property Details
                  </button>
                </div>
                
                {/* Next steps guidance */}
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <p className="text-xs font-medium text-neutral-900 mb-2">Next steps:</p>
                  {offer.status === "NEGOTIATING" && (
                    <ul className="text-xs text-neutral-600 space-y-1">
                      <li>• The seller is reviewing your offer (typically within 48 hours)</li>
                      <li>• You'll receive an email when they accept, decline, or counter-offer</li>
                      <li>• The agent will contact you if additional information is needed</li>
                    </ul>
                  )}
                  {offer.status === "ACCEPTED" && (
                    <ul className="text-xs text-neutral-600 space-y-1">
                      <li>✓ Your offer has been accepted!</li>
                      <li>• The agent will contact you within 24 hours</li>
                      <li>• Prepare your proof of funds documentation</li>
                      <li>• Arrange a mortgage in principle if needed</li>
                    </ul>
                  )}
                  {offer.status === "REJECTED" && (
                    <ul className="text-xs text-neutral-600 space-y-1">
                      <li>• This offer was declined by the seller</li>
                      <li>• You can submit a revised offer if interested</li>
                      <li>• Browse more properties on the Discover page</li>
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function SellerValuationPanel({ insights, valuation, comparables, valuationLoading, valuationError }) {
  const { subject, matched } = insights;

  // Show loading state or error
  if (valuationLoading) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-6">
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Estimated range</p>
          </div>
          <p className="text-slate-300 mt-4">Computing valuation from Land Registry data...</p>
        </div>
      </div>
    );
  }

  if (valuationError) {
    return (
      <div className="rounded-3xl border border-white/10 bg-red-500/10 p-6 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-red-300">Valuation unavailable</p>
          <p className="text-slate-300 mt-4">{valuationError}</p>
          <p className="text-xs text-red-200/80 bg-red-500/10 border border-red-400/20 rounded-lg p-2 mt-3">
            We couldn't find enough nearby sold properties. An agent will review your valuation.
          </p>
        </div>
      </div>
    );
  }

  // Real data - no "Demo data" badge
  return (
    <div className="grid gap-6 lg:grid-cols-[1.05fr,0.95fr] items-start">
      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-6">
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Estimated range</p>
          </div>
          <h3 className="mt-2 text-3xl font-semibold text-white">
            {formatAmount(valuation?.lower)} – {formatAmount(valuation?.upper)}
          </h3>
          <p className="mt-2 text-sm text-slate-300">
            Midpoint {formatAmount(valuation?.midpoint)} • confidence {valuation?.confidence ?? 82}%
          </p>
          <p className="mt-3 text-xs text-emerald-200/80 bg-emerald-500/10 border border-emerald-400/20 rounded-lg p-2">
            ✓ Based on {valuation?.comparablesCount || 0} recent Land Registry sales. Real comparable sales data.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3 text-sm text-slate-300">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Address</p>
            <p className="mt-2 text-white">{subject?.address || matched?.address || "Awaiting details"}</p>
            <p className="text-xs text-slate-500 mt-1">{subject?.postcode || matched?.postcode || "—"}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Property</p>
            <p className="mt-2 text-white">{matched?.type || "TBC"}</p>
            <p className="text-xs text-slate-500 mt-1">
              {subject?.beds ?? matched?.beds ?? "—"} beds • {subject?.baths ?? matched?.baths ?? "—"} baths
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Data source</p>
            <p className="mt-2 text-white text-xs">HM Land Registry</p>
            <p className="text-xs text-slate-500 mt-1">Price Paid Data</p>
          </div>
        </div>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/5 overflow-hidden">
        <PropertyMap
          latitude={matched?.lat || subject?.lat}
          longitude={matched?.lng || subject?.lng}
          address={matched?.address || subject?.address}
          postcode={matched?.postcode || subject?.postcode}
          zoom={16}
        />
        <div className="p-5 space-y-2 text-sm text-slate-300">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Snapshot</p>
          <p>
            Tenure: {matched?.tenure || "TBC"} • Internal area:{" "}
            {matched?.internal_sqft
              ? `${matched.internal_sqft.toLocaleString()} sq ft`
              : matched?.internal_m2
              ? `${matched.internal_m2} m²`
              : "Awaiting data"}
          </p>
          {matched?.epc_link ? (
            <a href={matched.epc_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-emerald-200 hover:text-emerald-100">
              View EPC certificate →
            </a>
          ) : (
            <p className="text-xs text-slate-500">Upload EPC to complete the record.</p>
          )}
        </div>
      </div>
      <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/5 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold text-white">Comparable sales</h3>
            <p className="text-sm text-slate-300">
              {comparables.length} Land Registry sales used to derive this valuation.
            </p>
          </div>
          <button className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-200 hover:bg-white/10 transition">
            Manage data
          </button>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {comparables.slice(0, 6).map((comp) => (
            <div key={comp.id} className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
              <div className="h-32">
                <img
                  src={comp.hero || comp.photos?.[0]}
                  alt={comp.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-4 space-y-1 text-sm text-slate-200">
                <div className="font-medium text-white line-clamp-2">{comp.title}</div>
                <div className="text-slate-400">{comp.area} • {comp.postcode}</div>
                <div className="font-semibold text-white">{formatAmount(comp.price)}</div>
                <div className="text-xs text-slate-500">
                  {comp.beds} beds • {comp.baths ?? "—"} baths
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SellerPortal({ session }) {
  const navigate = useNavigate();
  const [sellerProfile, setSellerProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(() => getUserProfile());
  const [loading, setLoading] = useState(true);
  const [photographyForm, setPhotographyForm] = useState({ date: "", time: "" });
  const [viewingSlotForm, setViewingSlotForm] = useState({ date: "", time: "" });
  const [solicitorEmail, setSolicitorEmail] = useState("");
  const [stageNotes, setStageNotes] = useState({});
  const [openStage, setOpenStage] = useState(0);
  
  // Modal states for UX improvements
  const [acceptOfferModal, setAcceptOfferModal] = useState({ open: false, offer: null });
  const [declineOfferModal, setDeclineOfferModal] = useState({ open: false, offer: null });
  const [counterOfferModal, setCounterOfferModal] = useState({ open: false, offer: null });
  const [successModal, setSuccessModal] = useState({ open: false, title: "", message: "", actions: [] });
  const [comingSoonModal, setComingSoonModal] = useState({ open: false, feature: "", workaround: "", expectedDate: "" });
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });

  // Load seller profile from API on mount
  useEffect(() => {
    let mounted = true;
    
    async function loadProfiles() {
      try {
        setLoading(true);
        const profile = await getSellerProfileAsync();
        if (mounted) {
          setSellerProfile(profile);
          setStageNotes({ ...(profile?.salesNotes || {}) });
        }
      } catch (error) {
        console.error("Failed to load seller profile:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    
    loadProfiles();
    setUserProfile(getUserProfile());
    
    return () => { mounted = false; };
  }, [session]);

  useEffect(() => {
    if (sellerProfile) {
      setStageNotes({ ...(sellerProfile?.salesNotes || {}) });
    }
  }, [sellerProfile]);

  const insights = useMemo(() => computeSellerInsights(sellerProfile), [sellerProfile]);
  const status = sellerProfile?.status || SellerStatus.PROSPECT;
  const sellerPlanLabel =
    sellerProfile?.planKey === "premier"
      ? "Premier marketing package"
      : sellerProfile?.planKey === "core"
      ? "Core marketing package"
      : null;

  const baseListing =
    sellerProfile?.activeListingId
      ? LISTINGS.find((listing) => listing.id === sellerProfile.activeListingId)
      : insights.matched;

  const fallbackProperties = baseListing
    ? [
        {
          id: baseListing.id || "primary",
          label: baseListing.title || sellerProfile?.address || "Primary listing",
          address: baseListing.address || sellerProfile?.address || "Address pending",
          status: status === SellerStatus.ACTIVE ? "Live" : "Draft",
          portals: status === SellerStatus.ACTIVE ? ["HOME AI", "Rightmove", "Zoopla"] : ["Not yet syndicated"],
          issues: sellerProfile?.uploadIssues || [],
          listing: baseListing,
          isLive: status === SellerStatus.ACTIVE
        }
      ]
    : [];

  const propertyOptions = (sellerProfile?.properties && sellerProfile.properties.length
    ? sellerProfile.properties
    : fallbackProperties
  ).map((property, index) => {
    const listing =
      property.listing ||
      (property.listingId ? LISTINGS.find((item) => item.id === property.listingId) : null) ||
      baseListing;
    const address =
      property.address ||
      listing?.address ||
      sellerProfile?.address ||
      "Address pending";
    return {
      id: property.id || `property-${index}`,
      label: property.label || listing?.title || `Property ${index + 1}`,
      address,
      status: property.status || (property.isLive ? "Live" : "Draft"),
      portals: property.portals || (property.isLive ? ["HOME AI"] : []),
      issues: property.issues || [],
      listing,
      isLive:
        property.isLive ??
        (!!property.status && property.status.toLowerCase().includes("live")),
      icon:
        property.icon ||
        (address ? address.trim().charAt(0).toUpperCase() : `${index + 1}`)
    };
  });

  const [selectedPropertyId, setSelectedPropertyId] = useState(propertyOptions[0]?.id);

  useEffect(() => {
    if (!propertyOptions.find((property) => property.id === selectedPropertyId)) {
      setSelectedPropertyId(propertyOptions[0]?.id);
    }
  }, [propertyOptions, selectedPropertyId]);

  const selectedProperty =
    propertyOptions.find((property) => property.id === selectedPropertyId) ||
    propertyOptions[0] ||
    null;

  const activeListing = selectedProperty?.listing || baseListing;

  const adjustments = insights.valuation?.adjustments || {};
  const adjustmentFeaturesSummary = adjustments.features?.length
    ? adjustments.features.map((key) => FEATURE_LABELS[key] || key).join(", ")
    : "None declared";
  const adjustmentConditionLabel =
    CONDITION_LABELS[adjustments.condition || "average"] || CONDITION_LABELS.average;
  const adjustmentFloorArea = adjustments.floorArea;
  const adjustmentRenovationYear = adjustments.renovationYear;

  const photographySlots = sellerProfile?.photographySlots || [];
  const viewingSlots = sellerProfile?.viewingSlots || [];
  const boardPreference =
    sellerProfile?.boardPreference || userProfile.boardPreference || "undecided";
  const boardUpdatedAt = sellerProfile?.boardRequestUpdatedAt;
  const solicitorInvite = sellerProfile?.solicitorInvite;
  const chain =
    sellerProfile?.chain || [
      {
        role: "Your buyer",
        status: "Mortgage offer issued",
        notes: "Bank of London valuation booked for 18 Nov."
      },
      {
        role: "Seller onward purchase",
        status: "Offer accepted subject to survey",
        notes: "Survey scheduled 22 Nov."
      }
    ];

  const viewings = [
    {
      id: "vw-1",
      date: "2025-11-14",
      time: "15:00",
      viewerEmail: "luca@buyers.com",
      status: "Confirmed",
      feedback: "Loved the terrace.",
      offerId: "of-101"
    },
    {
      id: "vw-2",
      date: "2025-11-16",
      time: "11:30",
      viewerEmail: "priya.dan@buyers.com",
      status: "Awaiting feedback",
      feedback: null,
      offerId: null
    },
    {
      id: "vw-3",
      date: "2025-11-18",
      time: "13:00",
      viewerEmail: "corporate@relocators.com",
      status: "Proposed",
      feedback: null,
      offerId: null
    }
  ];

  const offers = [
    {
      id: "of-101",
      buyerEmail: "luca@buyers.com",
      amount: 1825000,
      stage: "Heads of terms",
      status: "Negotiating",
      updated: "2025-11-12T14:20:00Z",
      outstanding: ["Awaiting proof of funds"],
      actions: ["Prepare draft heads", "Confirm solicitor details"]
    },
    {
      id: "of-099",
      buyerEmail: "priya.dan@buyers.com",
      amount: 1790000,
      stage: "Declined",
      status: "Rejected",
      updated: "2025-11-10T09:15:00Z",
      outstanding: ["Send polite decline follow-up"],
      actions: ["Provide feedback to buyers"]
    }
  ];

  const buyerProofs = [
    {
      buyerEmail: "luca@buyers.com",
      status: "Verified",
      documents: ["Proof of funds (11 Nov)", "Mortgage in principle"],
      score: "A",
      lastUpdated: "2025-11-12T10:10:00Z"
    },
    {
      buyerEmail: "priya.dan@buyers.com",
      status: "In review",
      documents: ["Mortgage in principle uploaded"],
      score: "B",
      lastUpdated: "2025-11-11T15:00:00Z"
    },
    {
      buyerEmail: "corporate@relocators.com",
      status: "Pending",
      documents: ["Awaiting proof of funds"],
      score: "B-",
      lastUpdated: "2025-11-10T09:00:00Z"
    }
  ];

  const notifications = [
    { id: "nt-1", title: "New enquiry from HOME AI buyer", time: "2 hours ago" },
    { id: "nt-2", title: "Viewing feedback pending from Priya & Dan", time: "6 hours ago" },
    { id: "nt-3", title: "Drone shoot scheduled for 19 Nov 10:00", time: "Yesterday" }
  ];

  const aiThread = [
    { sender: "AI", message: "Reminder: upload proof of ownership to release marketing spend.", time: "09:10" },
    { sender: "You", message: "Docs uploaded this morning – please confirm received.", time: "09:12" },
    { sender: "AI", message: "Confirmed. Analyst reviewing now – expect update within 1 hour.", time: "09:13" }
  ];

  const pipeline = [
    {
      key: "instruction",
      title: "Instruction",
      complete: true,
      description: "Agreement signed & onboarding complete.",
      tasks: [
        { label: "Agency agreement signed", done: true },
        { label: "AML checks approved", done: true }
      ],
      outstanding: []
    },
    {
      key: "marketing",
      title: "Marketing live",
      complete: true,
      description: "Listing syndicated to portals 4 days ago.",
      tasks: [
        { label: "Photography assets uploaded", done: Boolean(activeListing?.photos?.length) },
        { label: "Portal descriptions complete", done: true }
      ],
      outstanding: activeListing?.photos?.length ? [] : ["Confirm photography slot"]
    },
    {
      key: "viewings",
      title: "Viewings",
      complete: false,
      description: "3 viewings booked this week.",
      tasks: [
        { label: "Send viewing confirmations", done: true },
        { label: "Collect feedback same-day", done: false }
      ],
      outstanding: ["Feedback from Priya & Dan"]
    },
    {
      key: "offers",
      title: "Offer accepted",
      complete: false,
      description: "Awaiting best & final by 18 Nov.",
      tasks: [
        { label: "Prepare heads of terms template", done: false },
        { label: "Confirm buyer financing status", done: false }
      ],
      outstanding: ["Proof of funds from lead buyer"]
    },
    {
      key: "exchange",
      title: "Exchange",
      complete: false,
      description: "Conveyancers not yet instructed.",
      tasks: [
        { label: "Solicitor invitation sent", done: Boolean(solicitorInvite) },
        { label: "Draft contracts issued", done: false }
      ],
      outstanding: ["Invite solicitor", "Supply management pack"]
    }
  ];

  const firstIncomplete = pipeline.findIndex((stage) => !stage.complete);
  useEffect(() => {
    setOpenStage(firstIncomplete === -1 ? 0 : firstIncomplete);
  }, [sellerProfile, firstIncomplete]);

  const calendarDays = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, []);

  const combinedViewingSlots = [
    ...viewingSlots,
    ...(userProfile.viewingAvailability || [])
  ];

  const slotsByDate = combinedViewingSlots.reduce((acc, slot) => {
    if (!slot.date) return acc;
    acc[slot.date] = acc[slot.date] || [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const handleAddPhotographySlot = async () => {
    if (!photographyForm.date || !photographyForm.time) return;
    const slot = {
      id: crypto.randomUUID(),
      date: photographyForm.date,
      time: photographyForm.time
    };
    try {
      const updated = await updateSellerProfileAsync({
        photographySlots: [...photographySlots, slot]
      });
      setSellerProfile(updated);
      setPhotographyForm({ date: "", time: "" });
    } catch (error) {
      console.error("Failed to add photography slot:", error);
      setToast({ open: true, message: "Failed to save. Please try again.", type: "error" });
    }
  };

  const handleMarkPhotographyBooked = async (slot) => {
    try {
      const updated = await updateSellerProfileAsync({
        photographyBookedAt: { ...slot, confirmedAt: Date.now() },
        photographySlots: photographySlots.filter((s) => s.id !== slot.id)
      });
      setSellerProfile(updated);
    } catch (error) {
      console.error("Failed to book photography:", error);
      setToast({ open: true, message: "Failed to save. Please try again.", type: "error" });
    }
  };

  const handleBoardPreferenceChange = async (value) => {
    try {
      const updated = await updateSellerProfileAsync({
        boardPreference: value,
        boardRequestUpdatedAt: Date.now()
      });
      setSellerProfile(updated);
      setUserProfile((prev) => ({ ...prev, boardPreference: value }));
    } catch (error) {
      console.error("Failed to update board preference:", error);
      setToast({ open: true, message: "Failed to save. Please try again.", type: "error" });
    }
  };

  const handleInviteSolicitor = async () => {
    if (!solicitorEmail.trim()) return;
    try {
      const updated = await updateSellerProfileAsync({
        solicitorInvite: {
          email: solicitorEmail.trim(),
          sentAt: Date.now(),
          status: "Invitation sent"
        }
      });
      setSellerProfile(updated);
      setSolicitorEmail("");
    } catch (error) {
      console.error("Failed to invite solicitor:", error);
      setToast({ open: true, message: "Failed to send invitation. Please try again.", type: "error" });
    }
  };

  const handleAddViewingSlot = async () => {
    if (!viewingSlotForm.date || !viewingSlotForm.time) return;
    const slot = {
      id: crypto.randomUUID(),
      date: viewingSlotForm.date,
      time: viewingSlotForm.time
    };
    try {
      const updated = await updateSellerProfileAsync({
        viewingSlots: [...viewingSlots, slot]
      });
      setSellerProfile(updated);
      setViewingSlotForm({ date: "", time: "" });
    } catch (error) {
      console.error("Failed to add viewing slot:", error);
      setToast({ open: true, message: "Failed to save. Please try again.", type: "error" });
    }
  };

  const handleRemoveViewingSlot = async (id) => {
    try {
      const updated = await updateSellerProfileAsync({
        viewingSlots: viewingSlots.filter((slot) => slot.id !== id)
      });
      setSellerProfile(updated);
    } catch (error) {
      console.error("Failed to remove viewing slot:", error);
      setToast({ open: true, message: "Failed to save. Please try again.", type: "error" });
    }
  };

  const handleNotificationToggle = (field) => {
    const updated = updateUserProfile({ [field]: !userProfile[field] });
    setUserProfile(updated);
  };

  const handleChannelTogglePortal = (channel) => {
    const updated = updateUserProfile({
      notificationChannels: {
        ...(userProfile.notificationChannels || {}),
        [channel]: !userProfile.notificationChannels?.[channel]
      }
    });
    setUserProfile(updated);
  };

  const handleOpenHouseToggle = () => {
    const updated = updateUserProfile({
      openHouseOnly: !userProfile.openHouseOnly
    });
    setUserProfile(updated);
  };

  const handleStageNoteChange = (key, value) => {
    setStageNotes((prev) => ({ ...prev, [key]: value }));
  };

  const handleStageNoteSave = async (key) => {
    try {
      const updated = await updateSellerProfileAsync({
        salesNotes: {
          ...(sellerProfile?.salesNotes || {}),
          [key]: stageNotes[key] || ""
        }
      });
      setSellerProfile(updated);
    } catch (error) {
      console.error("Failed to save stage notes:", error);
      setToast({ open: true, message: "Failed to save notes. Please try again.", type: "error" });
    }
  };

  const listingMedia =
    activeListing && (activeListing.photos?.length || activeListing.video)
      ? [
          ...(activeListing.video ? [{ kind: "video", src: activeListing.video }] : []),
          ...(activeListing.photos || []).map((src) => ({ kind: "img", src }))
        ]
      : [];

  return (
    <>
    <PortalShell
      session={session}
      title="Seller workspace"
      subtitle="Monitor valuation updates, marketing activity, and offer momentum."
    >
      {status === SellerStatus.ACTIVE ? (
        <div className="space-y-6">
          {selectedProperty && (
            <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2 text-left">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/50">Selected property</p>
                  <h2 className="text-2xl font-semibold text-white">
                    {selectedProperty.address}
                  </h2>
                  <p className="text-sm text-white/70">
                    Status:{" "}
                    <span className={selectedProperty.isLive ? "text-emerald-300" : "text-amber-200"}>
                      {selectedProperty.status || (selectedProperty.isLive ? "Live" : "Draft")}
                    </span>
                    {selectedProperty.portals?.length ? (
                      <> • Live on {selectedProperty.portals.join(", ")} </>
                    ) : (
                      <span className="text-white/40"> • Not yet syndicated</span>
                    )}
                  </p>
                  {selectedProperty.issues?.length ? (
                    <p className="text-xs text-amber-200">
                      Issues: {selectedProperty.issues.join(", ")}
                    </p>
                  ) : (
                    <p className="text-xs text-white/40">No upload issues reported.</p>
                  )}
                </div>
                {propertyOptions.length > 1 && (
                  <div className="flex flex-wrap gap-2">
                    {propertyOptions.map((property) => (
                      <button
                        key={property.id}
                        onClick={() => setSelectedPropertyId(property.id)}
                        className={
                          "flex flex-col items-center justify-center rounded-2xl border px-4 py-3 text-xs transition w-24 " +
                          (property.id === selectedPropertyId
                            ? "border-emerald-400/60 bg-emerald-500/10 text-white"
                            : "border-white/10 bg-black/30 text-white/70 hover:border-white/30")
                        }
                      >
                        <span className="text-[11px] text-center leading-tight">
                          {property.label}
                        </span>
                        <span className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
                          {property.isLive ? "Live" : "Draft"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    if (selectedProperty?.listing?.id) {
                      navigate("/buyer/discover", { state: { focusListingId: selectedProperty.listing.id } });
                    } else {
                      navigate("/buyer/discover");
                    }
                  }}
                  className="rounded-xl bg-white text-black px-4 py-2 text-sm font-medium hover:bg-white/90 transition"
                >
                  View listing
                </button>
                <button
                  onClick={() => setComingSoonModal({
                    open: true,
                    feature: "Multi-property Management",
                    workaround: "Your agent can add additional property instructions via the CRM. Contact them directly for multi-property coordination.",
                    expectedDate: "Q2 2026"
                  })}
                  className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10 transition"
                >
                  Manage another sale
                </button>
              </div>
            </section>
          )}

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-white">Marketing assets</h2>
                <p className="text-sm text-white/70">
                  Keep photography, video, and signage on track. Updates sync with the agent dashboard.
                </p>
              </div>
              {sellerPlanLabel && (
                <span className="rounded-full border border-white/10 bg-black/20 px-4 py-1 text-xs uppercase tracking-[0.3em] text-white/60">
                  {sellerPlanLabel}
                </span>
              )}
            </div>

            <div className="grid gap-6 lg:grid-cols-[1.4fr,1fr] items-start">
              <div className="space-y-4">
                {listingMedia.length ? (
                  <MediaCarousel
                    photos={activeListing?.photos || []}
                    video={activeListing?.video}
                    onOpenFloor={() => setComingSoonModal({
                      open: true,
                      feature: "Floorplan Viewer",
                      workaround: "Floorplans are available in the gallery section. Full interactive viewer launching soon.",
                      expectedDate: "Q1 2026"
                    })}
                    onOpenMap={() => navigate("/seller/valuation")}
                    onOpenGallery={() =>
                      window.open(activeListing?.hero || activeListing?.photos?.[0] || "#", "_blank")
                    }
                  />
                ) : (
                  <div className="rounded-2xl border border-dashed border-white/15 bg-black/30 p-8 text-center text-sm text-white/60">
                    Photography not uploaded yet. Propose photography slots below to help the media team schedule.
                  </div>
                )}

                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3">
                  <p className="text-sm font-semibold text-white">Photography scheduling</p>
                  {sellerProfile?.photographyBookedAt ? (
                    <p className="text-xs text-emerald-200">
                      Booked for {sellerProfile.photographyBookedAt.date} at {sellerProfile.photographyBookedAt.time}
                    </p>
                  ) : (
                    <p className="text-xs text-white/60">
                      Suggest multiple slots and the media team will confirm the best fit.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="date"
                      className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                      value={photographyForm.date}
                      onChange={(e) => setPhotographyForm((prev) => ({ ...prev, date: e.target.value }))}
                    />
                    <input
                      type="time"
                      className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                      value={photographyForm.time}
                      onChange={(e) => setPhotographyForm((prev) => ({ ...prev, time: e.target.value }))}
                    />
                    <button
                      type="button"
                      onClick={handleAddPhotographySlot}
                      className="rounded-xl bg-white text-black px-3 py-2 text-xs font-medium hover:bg-white/90"
                    >
                      Save slot
                    </button>
                  </div>
                  <ul className="space-y-2 text-xs text-white/70">
                    {photographySlots.length === 0 && (
                      <li className="text-white/40">No availability saved yet.</li>
                    )}
                    {photographySlots.map((slot) => (
                      <li
                        key={slot.id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/40 px-3 py-2"
                      >
                        <span>
                          {slot.date} • {slot.time}
                        </span>
                        <button
                          onClick={() => handleMarkPhotographyBooked(slot)}
                          className="text-xs text-emerald-300 hover:text-emerald-200"
                        >
                          Confirm
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3 text-sm text-white/80">
                  <p className="font-semibold text-white">For sale board preference</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "yes", label: "Yes, install board" },
                      { value: "no", label: "No board" },
                      { value: "undecided", label: "Undecided" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleBoardPreferenceChange(option.value)}
                        className={
                          "rounded-xl border px-3 py-2 text-xs transition " +
                          (boardPreference === option.value
                            ? "border-emerald-400/60 bg-emerald-500/10 text-white"
                            : "border-white/15 bg-black/30 text-white/70 hover:border-white/30")
                        }
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-white/60">
                    Request automatically routes to your agent dashboard. Current preference: {boardPreference}.
                    {boardUpdatedAt && (
                      <> {" "}
                        <span className="text-white/40">
                          Updated {new Date(boardUpdatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </>
                    )}
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-xs text-white/70 space-y-2">
                  <p className="font-semibold text-white/80">Valuation assumptions</p>
                  <ul className="space-y-1">
                    <li>Condition: {adjustmentConditionLabel}</li>
                    <li>Features: {adjustmentFeaturesSummary}</li>
                    {adjustmentFloorArea ? (
                      <li>
                        Floor area: {adjustmentFloorArea.value}
                        {adjustmentFloorArea.unit === "sqm" ? " m²" : " sq ft"}
                      </li>
                    ) : null}
                    {adjustmentRenovationYear ? (
                      <li>Renovated: {adjustmentRenovationYear}</li>
                    ) : null}
                    <li>Valuation multiplier: ×{(adjustments.multiplier || 1).toFixed(3)}</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-white">Contracts & Legal</h2>
                <p className="text-sm text-white/70">
                  Keep agency agreements, terms, and compliance docs at your fingertips.
                </p>
              </div>
              <button
                onClick={() => {
                  if (sellerProfile?.contractUrl) {
                    window.open(sellerProfile.contractUrl, "_blank", "noopener");
                  } else {
                    setComingSoonModal({
                      open: true,
                      feature: "Contract Viewer",
                      workaround: "Your signed agreement is stored securely with HOME AI. For immediate access, contact your agent or support.",
                      expectedDate: "Q1 2026"
                    });
                  }
                }}
                className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10 transition"
              >
                View signed contract
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-white/80">
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Agreement</p>
                <p className="text-white font-semibold">
                  {sellerProfile?.contractSignedAt
                    ? `Signed ${new Date(sellerProfile.contractSignedAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric"
                      })}`
                    : "Awaiting signature"}
                </p>
                <p className="text-xs text-white/60">
                  Reference: {sellerProfile?.paymentReference || "Pending"}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Terms & obligations</p>
                <ul className="text-xs text-white/70 space-y-1 list-disc pl-5">
                  <li>1% success fee due on exchange, payable via completion statement.</li>
                  <li>Upfront marketing package includes photography, floorplans, and portal syndication.</li>
                  <li>Seller to disclose material facts and provide AML documentation within 5 working days.</li>
                </ul>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2 text-xs text-white/70">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Compliance checklist</p>
              <ul className="space-y-1 list-disc pl-5">
                <li>EPC document on file ({sellerProfile?.epcDocument ? "Yes" : "Pending"})</li>
                <li>Proof of ownership received {sellerProfile?.ownershipDocReceived ? "✔︎" : "✗"}</li>
                <li>Client money protection and anti-money-laundering policy acknowledged.</li>
              </ul>
            </div>
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-white">Sales progression</h2>
                <p className="text-sm text-white/70">
                  Expand each stage to see outstanding tasks, notes, and collaborator access.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-white/70">
                <input
                  type="email"
                  placeholder="Invite solicitor (email)"
                  className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                  value={solicitorEmail}
                  onChange={(e) => setSolicitorEmail(e.target.value)}
                />
                <button
                  onClick={handleInviteSolicitor}
                  className="rounded-xl bg-white text-black px-3 py-2 text-xs font-medium hover:bg-white/90"
                >
                  Invite
                </button>
                {solicitorInvite && (
                  <span className="text-emerald-200">
                    Invitation sent to {solicitorInvite.email}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {pipeline.map((stage, idx) => (
                <div
                  key={stage.key}
                  className="rounded-2xl border border-white/10 bg-black/30"
                >
                  <button
                    onClick={() => setOpenStage(idx === openStage ? -1 : idx)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left"
                  >
                    <div>
                      <p className="text-sm font-semibold text-white">{stage.title}</p>
                      <p className="text-xs text-white/60">{stage.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          stage.complete ? "bg-emerald-400" : idx === openStage ? "bg-amber-400" : "bg-slate-600"
                        }`}
                      />
                      <span className="text-xs text-white/50">{openStage === idx ? "−" : "+"}</span>
                    </div>
                  </button>
                  {openStage === idx && (
                    <div className="px-4 pb-4 space-y-3 text-sm text-white/80">
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/50">Tasks</p>
                        <ul className="mt-2 space-y-1 text-xs">
                          {stage.tasks.map((task, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  task.done ? "bg-emerald-400" : "bg-slate-500"
                                }`}
                              />
                              {task.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {stage.outstanding.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-[0.3em] text-white/50">Outstanding</p>
                          <ul className="mt-2 space-y-1 text-xs text-amber-200">
                            {stage.outstanding.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div>
                        <label className="text-xs uppercase tracking-[0.3em] text-white/50">
                          Private Notes for this stage
                          <span className="text-white/40 text-[10px] normal-case block mt-1">
                            (Your private notes - not shared with buyer or agent)
                          </span>
                        </label>
                        <textarea
                          className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                          rows={3}
                          value={stageNotes[stage.key] || ""}
                          onChange={(e) => handleStageNoteChange(stage.key, e.target.value)}
                        />
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={() => handleStageNoteSave(stage.key)}
                            className="rounded-xl bg-white text-black px-3 py-2 text-xs font-medium hover:bg-white/90"
                          >
                            Save notes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-xs text-white/70 space-y-2">
              <p className="font-semibold text-white/80">Chain summary</p>
              <ul className="space-y-2">
                {chain.map((link, idx) => (
                  <li key={idx}>
                    <span className="text-white">{link.role}:</span> {link.status}{" "}
                    <span className="text-white/50">— {link.notes}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-[1.35fr,0.65fr]">
            <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-white">Viewings & availability</h2>
                  <p className="text-sm text-white/70">
                    Keep availability current, switch on open-house mode, and review upcoming guests.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-white/70" title="When enabled, buyers can only book advertised open-house slots. Individual viewing requests are automatically declined.">
                  <input
                    type="checkbox"
                    checked={userProfile.openHouseOnly}
                    onChange={handleOpenHouseToggle}
                  />
                  Open-house only
                  <span className="text-white/50 cursor-help">ⓘ</span>
                </label>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Availability (next 7 days)</p>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs text-white/80">
                  {calendarDays.map((day) => {
                    const key = day.toISOString().slice(0, 10);
                    const slots = slotsByDate[key] || [];
                    return (
                      <div key={key} className="rounded-xl border border-white/10 bg-black/40 p-3 space-y-1">
                        <p className="font-semibold text-white">
                          {day.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                        </p>
                        {slots.length === 0 ? (
                          <p className="text-white/40">No slots</p>
                        ) : (
                          slots.map((slot) => (
                            <p key={slot.id || `${slot.date}-${slot.time}`}>{slot.time}</p>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <input
                    type="date"
                    className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                    value={viewingSlotForm.date}
                    onChange={(e) => setViewingSlotForm((prev) => ({ ...prev, date: e.target.value }))}
                  />
                  <input
                    type="time"
                    className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                    value={viewingSlotForm.time}
                    onChange={(e) => setViewingSlotForm((prev) => ({ ...prev, time: e.target.value }))}
                  />
                  <button
                    onClick={handleAddViewingSlot}
                    className="rounded-xl bg-white text-black px-3 py-2 text-xs font-medium hover:bg-white/90"
                  >
                    Save availability
                  </button>
                </div>
                {viewingSlots.length > 0 && (
                  <ul className="mt-3 space-y-2 text-xs text-white/70">
                    {viewingSlots.map((slot) => (
                      <li
                        key={slot.id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/40 px-3 py-2"
                      >
                        <span>
                          {slot.date} • {slot.time}
                        </span>
                        <button
                          onClick={() => handleRemoveViewingSlot(slot.id)}
                          className="text-red-300 hover:text-red-200"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Upcoming viewings</p>
                {viewings.map((viewing) => {
                  const viewer = MOCK_MEMBER_PROFILES[viewing.viewerEmail];
                  const start = new Date(`${viewing.date}T${viewing.time}`);
                  return (
                    <div
                      key={viewing.id}
                      className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 flex flex-wrap items-center gap-4 justify-between text-sm text-white/80"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full overflow-hidden border border-white/10">
                          {viewer?.avatar ? (
                            <img src={viewer.avatar} alt={viewer?.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-white/10 flex items-center justify-center text-xs text-white/60">
                              {viewer?.name?.[0] || "?"}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {start.toLocaleDateString("en-GB", {
                              weekday: "short",
                              day: "numeric",
                              month: "short"
                            })}
                            {" • "}
                            {start.toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-white/60">{viewer?.name || "Buyer"}</p>
                            <StatusBadge status={viewing.status} />
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-[160px] text-xs text-white/60">
                        {viewer?.bio || "Buyer profile forthcoming."}
                      </div>
                      <div className="text-xs text-white/70">
                        {viewing.feedback ? `Feedback: “${viewing.feedback}”` : "Awaiting feedback"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <div className="space-y-6">
              <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-white">Offers</h2>
                    <p className="text-sm text-white/70">Stage tracking and outstanding actions.</p>
                  </div>
                  <div className="text-xs uppercase tracking-[0.3em] text-emerald-200">Live</div>
                </div>
                <div className="space-y-3 text-sm text-white/80">
                  {offers.map((offer) => {
                    const viewer = MOCK_MEMBER_PROFILES[offer.buyerEmail];
                    const canAccept = offer.status !== "Rejected" && offer.status !== "Accepted";
                    const canReject = offer.status === "Negotiating";
                    
                    return (
                      <div key={offer.id} className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-white">
                              {viewer?.name || offer.buyerEmail}
                            </p>
                            <p className="text-xs text-white/60">
                              {new Date(offer.updated).toLocaleString("en-GB", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-base font-semibold text-white">{formatAmount(offer.amount)}</p>
                            <StatusBadge status={offer.status} showIcon className="mt-1 justify-end" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-white/60">Stage:</p>
                          <StatusBadge status={offer.stage} />
                        </div>
                        {offer.outstanding.length > 0 && (
                          <div className="text-xs text-amber-200">
                            Outstanding: {offer.outstanding.join(", " )}
                          </div>
                        )}
                        <ul className="text-xs text-white/60 list-disc pl-5">
                          {offer.actions.map((action, idx) => (
                            <li key={idx}>{action}</li>
                          ))}
                        </ul>
                        
                        {(canAccept || canReject) && (
                          <div className="flex gap-2 pt-2">
                            {canAccept && (
                              <button
                                onClick={() => setAcceptOfferModal({ open: true, offer })}
                                className="px-3 py-1.5 text-xs rounded-xl bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 hover:bg-emerald-500/30 transition"
                              >
                                ✓ Accept
                              </button>
                            )}
                            {canReject && (
                              <button
                                onClick={() => setDeclineOfferModal({ open: true, offer })}
                                className="px-3 py-1.5 text-xs rounded-xl bg-rose-500/20 text-rose-200 border border-rose-400/40 hover:bg-rose-500/30 transition"
                              >
                                ✕ Decline
                              </button>
                            )}
                            <button
                              onClick={() => setCounterOfferModal({ 
                                open: true, 
                                offer,
                                feature: "Counter-offer",
                                workaround: `For now, please contact ${viewer?.name || offer.buyerEmail} directly via email to propose a counter-offer. Copy their email: ${offer.buyerEmail}`,
                                expectedDate: "Q2 2026"
                              })}
                              className="px-3 py-1.5 text-xs rounded-xl bg-white/10 text-white/80 border border-white/20 hover:bg-white/15 transition"
                            >
                              Counter
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-white">Buyer verification</h2>
                    <p className="text-sm text-white/70">Proof of funds and finance checks synced with the CRM.</p>
                  </div>
                  <button
                    onClick={() => navigate("/profile")}
                    className="rounded-xl border border-white/20 bg-white/5 px-3 py-1.5 text-xs text-white hover:bg-white/10 transition"
                  >
                    Request update
                  </button>
                </div>
                <div className="space-y-3 text-sm text-white/80">
                  {buyerProofs.map((proof) => {
                    const viewer = MOCK_MEMBER_PROFILES[proof.buyerEmail];
                    return (
                      <div key={proof.buyerEmail} className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-white font-semibold">
                            {viewer?.name || proof.buyerEmail}
                          </p>
                          <StatusBadge status={proof.status} showIcon />
                        </div>
                        <p className="text-xs text-white/60">
                          Score: {proof.score} • Updated{" "}
                          {new Date(proof.lastUpdated).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                        <ul className="text-xs text-white/70 list-disc pl-5">
                          {proof.documents.map((doc, idx) => (
                            <li key={idx}>{doc}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
                <h2 className="text-xl font-semibold text-white">Notification settings</h2>
                <div className="space-y-3 text-sm text-white/80">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={userProfile.emailNotifications}
                      onChange={() => handleNotificationToggle("emailNotifications")}
                    />
                    Email updates on important milestones
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={userProfile.platformNotifications}
                      onChange={() => handleNotificationToggle("platformNotifications")}
                    />
                    In-app alerts
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={userProfile.notificationChannels?.sms || false}
                      onChange={() => handleChannelTogglePortal("sms")}
                    />
                    SMS summaries
                  </label>
                </div>
                <p className="text-xs text-white/50">
                  Fine-grained controls live in your profile. Email notifications can be disabled here but transactional emails (e.g. onboarding confirmations) will still send.
                </p>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold text-white">AI assistant</h2>
                  <button
                    onClick={() => setComingSoonModal({
                      open: true,
                      feature: "Live Messaging",
                      workaround: "For now, communicate with buyers via email or phone. Your agent can facilitate introductions.",
                      expectedDate: "Q2 2026"
                    })}
                    className="rounded-xl border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-200 hover:bg-white/10 transition"
                  >
                    Open thread
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  {aiThread.map((msg, idx) => (
                    <div key={idx} className="rounded-2xl border border-white/10 bg-black/40 px-3 py-2">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{msg.sender}</p>
                      <p className="text-white mt-1">{msg.message}</p>
                      <p className="text-[10px] text-slate-500 mt-1">{msg.time}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
                <h2 className="text-xl font-semibold text-white">Latest notifications</h2>
                <ul className="space-y-3 text-sm text-slate-200">
                  {notifications.map((note) => (
                    <li key={note.id} className="flex items-center justify-between gap-3">
                      <span>{note.title}</span>
                      <span className="text-xs text-slate-500">{note.time}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <h2 className="text-xl font-semibold text-white">Marketing summary</h2>
            <p className="text-sm text-white/70">
              Performance snapshots pulled from HOME AI analytics.
            </p>
            <div className="grid gap-4 md:grid-cols-4 text-sm text-white">
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Portal views</p>
                <p className="mt-2 text-2xl font-semibold">4,182</p>
                <p className="text-xs text-emerald-300 mt-1">+18% vs last week</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Enquiries</p>
                <p className="mt-2 text-2xl font-semibold">27</p>
                <p className="text-xs text-slate-400 mt-1">3 qualified buyers pending</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-400">AI match score</p>
                <p className="mt-2 text-2xl font-semibold">91%</p>
                <p className="text-xs text-slate-400 mt-1">Top decile in W8 segment</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Next milestone</p>
                <p className="mt-2 text-2xl font-semibold">Best & final</p>
                <p className="text-xs text-slate-400 mt-1">Due 18 Nov 17:00</p>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <>
          <SellerValuationPanel insights={insights} valuation={valuation} comparables={comparables} valuationLoading={valuationLoading} valuationError={valuationError} />
          <section className="grid gap-4 md:grid-cols-3">
            {[
              {
                label: "Marketing status",
                value: "Pre-launch",
                note: "Photography booked for 18 Nov."
              },
              {
                label: "Viewings scheduled",
                value: "3 this week",
                note: "Latest feedback pending."
              },
              {
                label: "Offer readiness",
                value: "Proof of funds outstanding",
                note: "Remind buyer #2846 to upload documents."
              }
            ].map((item) => (
              <div key={item.label} className="rounded-3xl border border-white/10 bg-white/5 p-5">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{item.label}</p>
                <p className="mt-2 text-lg font-semibold text-white">{item.value}</p>
                <p className="mt-1 text-sm text-slate-300">{item.note}</p>
              </div>
            ))}
          </section>
        </>
      )}
    </PortalShell>
    
    {/* Modal components for UX improvements */}
    <ConfirmationModal
      open={acceptOfferModal.open}
      onClose={() => setAcceptOfferModal({ open: false, offer: null })}
      onConfirm={() => {
        setAcceptOfferModal({ open: false, offer: null });
        setSuccessModal({
          open: true,
          title: "Offer Accepted! 🎉",
          message: "The buyer and agents have been notified. Next steps:",
          actions: [
            "1. Solicitor will contact you within 24 hours",
            "2. Review draft contract when ready",
            "3. Track progress in your dashboard"
          ]
        });
      }}
      title="Accept this offer?"
      message={acceptOfferModal.offer ? `Accept £${acceptOfferModal.offer.offerAmount?.toLocaleString()} offer from ${acceptOfferModal.offer.buyerName}?\n\nThis will:\n• Notify the buyer and agent immediately\n• Your solicitor will be contacted to start paperwork\n• No fees are charged until exchange of contracts\n\nYou can still withdraw before exchange if circumstances change.` : ""}
      confirmText="Accept Offer"
      confirmStyle="success"
    />
    
    <ConfirmationModal
      open={declineOfferModal.open}
      onClose={() => setDeclineOfferModal({ open: false, offer: null })}
      onConfirm={() => {
        setDeclineOfferModal({ open: false, offer: null });
        setSuccessModal({
          open: true,
          title: "Offer Declined",
          message: "The buyer has been notified politely. You can:",
          actions: [
            "1. Wait for revised offers",
            "2. Send a counter-offer (coming soon)",
            "3. Review other pending offers"
          ]
        });
      }}
      title="Decline this offer?"
      message={declineOfferModal.offer ? `Decline the £${declineOfferModal.offer.offerAmount?.toLocaleString()} offer from ${declineOfferModal.offer.buyerName}? They will be notified professionally.` : ""}
      confirmText="Decline Offer"
      confirmStyle="danger"
    />
    
    <ComingSoonModal
      open={counterOfferModal.open}
      onClose={() => setCounterOfferModal({ open: false, offer: null })}
      feature="Counter-offer System"
      workaround={counterOfferModal.offer ? `For now, email the buyer directly at ${counterOfferModal.offer.buyerEmail} to propose a different price. Full counter-offer UI coming Q2 2026.` : "For now, contact the buyer via email to propose a different price. Full counter-offer UI coming Q2 2026."}
      expectedDate="Q2 2026"
    />
    
    <SuccessModal
      open={successModal.open}
      onClose={() => setSuccessModal({ open: false, title: "", message: "", actions: [] })}
      title={successModal.title}
      message={successModal.message}
      actions={successModal.actions}
    />
    
    <ComingSoonModal
      open={comingSoonModal.open}
      onClose={() => setComingSoonModal({ open: false, feature: "", workaround: "", expectedDate: "" })}
      feature={comingSoonModal.feature}
      workaround={comingSoonModal.workaround}
      expectedDate={comingSoonModal.expectedDate}
    />
    
    <Toast 
      open={toast.open} 
      onClose={() => setToast({ ...toast, open: false })} 
      message={toast.message} 
      type={toast.type} 
    />
    </>
  );
}


function BuyerSellerPortal({ session }) {
  const [search, setSearch] = useState(null);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [latest, profile] = await Promise.all([
          latestSearch(),
          getSellerProfileAsync()
        ]);
        if (latest?.filters) {
          setSearch(latest.filters);
        }
        setSellerDetails(profile);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const recommendations = useMemo(() => {
    if (!search) return LISTINGS.slice(0, 4);
    const matches = LISTINGS.filter((listing) => listingMatches(search, listing));
    return matches.length ? matches.slice(0, 4) : LISTINGS.slice(0, 4);
  }, [search]);

  const favourites = useMemo(() => {
    const favs = loadJSON(KEYS.FAVS, {});
    return LISTINGS.filter((listing) => favs[listing.id]).slice(0, 4);
  }, []);

  const insights = useMemo(() => computeSellerInsights(sellerDetails), [sellerDetails]);

  const upcoming = useMemo(
    () =>
      recommendations.slice(0, 2).map((listing, idx) => ({
        id: `duo-${idx}`,
        date: idx === 0 ? "Fri 15 Nov • 14:00" : "Sun 17 Nov • 12:30",
        listing,
        status: idx === 0 ? "Confirmed" : "Draft"
      })),
    [recommendations]
  );

  return (
    <PortalShell
      session={session}
      title="Buyer & seller workspace"
      subtitle="Track your onward purchase and sale pipeline together."
    >
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <BuyerOverviewCards search={search} upcoming={upcoming} />
          <BuyerCardsGrid title="Buyer matches" listings={recommendations} />
          <BuyerCardsGrid title="Saved shortlist" listings={favourites} />
        </div>
        <div className="space-y-6">
          <SellerValuationPanel insights={insights} valuation={valuation} comparables={comparables} valuationLoading={valuationLoading} valuationError={valuationError} />
        </div>
      </div>
      
      {/* Seller Offer Action Modals */}
      <ConfirmationModal
        open={acceptOfferModal.open}
        onClose={() => setAcceptOfferModal({ open: false, offer: null })}
        onConfirm={() => {
          const offer = acceptOfferModal.offer;
          const viewer = MOCK_MEMBER_PROFILES[offer?.buyerEmail];
          setSuccessModal({
            open: true,
            title: "Offer Accepted",
            message: `You've accepted the offer of ${formatAmount(offer?.amount)} from ${viewer?.name || offer?.buyerEmail}. Both the buyer and their agent have been notified.`,
            actions: [
              "Heads of Terms will be prepared by your solicitor",
              "Buyer's financing will be verified",
              "Exchange timeline will be confirmed within 48 hours"
            ]
          });
        }}
        title="Accept Offer"
        message={`Accept offer of ${formatAmount(acceptOfferModal.offer?.amount)} from ${MOCK_MEMBER_PROFILES[acceptOfferModal.offer?.buyerEmail]?.name || acceptOfferModal.offer?.buyerEmail}?`}
        confirmText="Accept Offer"
        confirmStyle="success"
      />
      
      <ConfirmationModal
        open={declineOfferModal.open}
        onClose={() => setDeclineOfferModal({ open: false, offer: null })}
        onConfirm={() => {
          const offer = declineOfferModal.offer;
          const viewer = MOCK_MEMBER_PROFILES[offer?.buyerEmail];
          setSuccessModal({
            open: true,
            title: "Offer Declined",
            message: `The offer from ${viewer?.name || offer?.buyerEmail} has been declined. They will be notified politely with feedback if provided.`,
            actions: [
              "Buyer will receive notification",
              "You can provide feedback via the CRM",
              "Other offers remain active"
            ]
          });
        }}
        title="Decline Offer"
        message={`Decline offer from ${MOCK_MEMBER_PROFILES[declineOfferModal.offer?.buyerEmail]?.name || declineOfferModal.offer?.buyerEmail}? This action will notify the buyer.`}
        confirmText="Decline"
        confirmStyle="danger"
      />
      
      <ComingSoonModal
        open={counterOfferModal.open}
        onClose={() => setCounterOfferModal({ open: false, offer: null, feature: "", workaround: "", expectedDate: "" })}
        feature={counterOfferModal.feature}
        workaround={counterOfferModal.workaround}
        expectedDate={counterOfferModal.expectedDate}
      />
      
      <SuccessModal
        open={successModal.open}
        onClose={() => setSuccessModal({ open: false, title: "", message: "", actions: [] })}
        title={successModal.title}
        message={successModal.message}
        actions={successModal.actions}
      />
      
      <ComingSoonModal
        open={comingSoonModal.open}
        onClose={() => setComingSoonModal({ open: false, feature: "", workaround: "", expectedDate: "" })}
        feature={comingSoonModal.feature}
        workaround={comingSoonModal.workaround}
        expectedDate={comingSoonModal.expectedDate}
      />
    </PortalShell>
  );
}

/* ----------------------- Profile Page ----------------------- */
function ProfilePage() {
  const { session } = useCurrentSession();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [docForm, setDocForm] = useState({ name: "", type: "Contract", isPrivate: true });
  const [availabilityForm, setAvailabilityForm] = useState({ date: "", time: "" });
  const [saved, setSaved] = useState(null);

  // Load profile from API on mount or when session changes
  useEffect(() => {
    if (!session) return;
    
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const { getProfile } = await import("./api/client.js");
        const data = await getProfile();
        setProfile({
          displayName: data?.name || session.email || "",
          phone: data?.phones?.[0] || "",
          summary: data?.notes || "",
          avatar: data?.avatarUrl || "",
          shareContactDetails: true,
          uploadedDocs: [],
          viewingAvailability: [],
          emailNotifications: true,
          platformNotifications: true,
          notificationChannels: { email: true },
          openHouseOnly: false,
          ...data
        });
      } catch (err) {
        console.error("Failed to load profile:", err);
        // Provide default empty profile on error
        setProfile({
          displayName: session?.name || session?.email || "",
          phone: "",
          summary: "",
          avatar: "",
          shareContactDetails: true,
          uploadedDocs: [],
          viewingAvailability: [],
          emailNotifications: true,
          platformNotifications: true,
          notificationChannels: { email: true },
          openHouseOnly: false
        });
        setError("Could not load profile. Changes will be saved to backend.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [session]);

  if (!session) {
    return (
      <main className="min-h-screen bg-neutral-950 text-white grid place-items-center px-6">
        <HomeButton />
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-3xl font-semibold">Sign in to manage your profile</h1>
          <p className="text-sm text-white/70">
            Your profile controls how other members and agents see you across HOME AI. Please sign in using the button on the landing page.
          </p>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-neutral-950 text-white grid place-items-center px-6">
        <HomeButton />
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-3xl font-semibold">Loading your profile...</h1>
          <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-white/30 animate-pulse"></div>
          </div>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="min-h-screen bg-neutral-950 text-white grid place-items-center px-6">
        <HomeButton />
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-3xl font-semibold">Profile unavailable</h1>
          <p className="text-sm text-white/70">
            {error || "Could not load your profile. Please refresh and try again."}
          </p>
        </div>
      </main>
    );
  }

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleChannelToggle = (channel) => {
    setProfile((prev) => ({
      ...prev,
      notificationChannels: {
        ...prev.notificationChannels,
        [channel]: !prev.notificationChannels?.[channel]
      }
    }));
  };

  const handleAddDocument = () => {
    if (!docForm.name.trim()) return;
    const doc = {
      id: crypto.randomUUID(),
      name: docForm.name.trim(),
      type: docForm.type,
      isPrivate: docForm.isPrivate,
      addedAt: Date.now()
    };
    setProfile((prev) => ({
      ...prev,
      uploadedDocs: [...(prev.uploadedDocs || []), doc]
    }));
    setDocForm({ name: "", type: docForm.type, isPrivate: docForm.isPrivate });
  };

  const handleRemoveDocument = (id) => {
    setProfile((prev) => ({
      ...prev,
      uploadedDocs: prev.uploadedDocs.filter((doc) => doc.id !== id)
    }));
  };

  const handleAddAvailability = () => {
    if (!availabilityForm.date || !availabilityForm.time) return;
    const entry = {
      id: crypto.randomUUID(),
      date: availabilityForm.date,
      time: availabilityForm.time
    };
    setProfile((prev) => ({
      ...prev,
      viewingAvailability: [...(prev.viewingAvailability || []), entry]
    }));
    setAvailabilityForm({ date: "", time: "" });
  };

  const handleRemoveAvailability = (id) => {
    setProfile((prev) => ({
      ...prev,
      viewingAvailability: prev.viewingAvailability.filter((slot) => slot.id !== id)
    }));
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    
    try {
      setSaving(true);
      setError(null);
      const { updateProfile } = await import("./api/client.js");
      
      // Prepare data for API
      const payload = {
        name: profile.displayName,
        phone: profile.phone,
        notes: profile.summary,
        avatarUrl: profile.avatar,
        phones: profile.phone ? [profile.phone] : [],
        additionalEmails: profile.additionalEmails || [],
        associatedMembers: profile.associatedMembers || []
      };
      
      await updateProfile(payload);
      setSaved(Date.now());
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaved(null), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setError(err?.message || "Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const financeDocs = (profile.uploadedDocs || []).filter((doc) => {
    const text = `${doc.type} ${doc.name}`.toLowerCase();
    return text.includes("proof") || text.includes("fund") || text.includes("mortgage") || text.includes("finance");
  });
  const financeStatus = financeDocs.length >= 2 ? "Ready" : financeDocs.length === 1 ? "Partial" : "Missing";
  const lastFinanceUpdate = financeDocs.length
    ? new Date(financeDocs[financeDocs.length - 1].addedAt)
    : null;

  return (
    <main className="min-h-screen bg-neutral-950 text-white pb-16">
      <HomeButton />
      <div className="max-w-5xl mx-auto px-6 pt-16 space-y-8">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.32em] text-white/50">Profile</p>
          <h1 className="text-3xl font-semibold">Your HOME identity</h1>
          <p className="text-sm text-white/60 max-w-2xl">
            Manage how you appear to buyers, sellers, agents, and collaborators. Sensitive data stays private by default—only agents you authorise can see confidential documents or contact details.
          </p>
        </header>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Basics</h2>
          <div className="grid md:grid-cols-[160px,1fr] gap-6">
            <div className="flex flex-col items-center gap-3">
              <div className="h-32 w-32 rounded-full border border-white/15 bg-black/40 overflow-hidden flex items-center justify-center">
                {profile.avatar ? (
                  <img src={profile.avatar} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-white/40 text-sm text-center px-2">
                    Add image URL to personalise
                  </span>
                )}
              </div>
              <input
                className="w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white focus:border-white/40 focus:outline-none"
                placeholder="Image URL"
                value={profile.avatar}
                onChange={(e) => handleProfileChange("avatar", e.target.value)}
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-white/50">Display name</label>
                <input
                  className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  placeholder="How should we address you?"
                  value={profile.displayName}
                  onChange={(e) => handleProfileChange("displayName", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-white/50">About you</label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  rows={4}
                  placeholder="Share a short bio so agents understand your goals."
                  value={profile.summary}
                  onChange={(e) => handleProfileChange("summary", e.target.value)}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.3em] text-white/50">Phone number</label>
                  <input
                    className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                    placeholder="+44 7..."
                    value={profile.phone}
                    onChange={(e) => handleProfileChange("phone", e.target.value)}
                  />
                  <label className="mt-2 inline-flex items-center gap-2 text-xs text-white/70">
                    <input
                      type="checkbox"
                      checked={profile.shareContactDetails}
                      onChange={(e) => handleProfileChange("shareContactDetails", e.target.checked)}
                    />
                    Allow my assigned agent to see my phone number
                  </label>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-xs text-white/70">
                  <p className="font-semibold text-white/80">Confidentiality</p>
                  <p className="mt-1">
                    Uploaded documents stay private to your HOME team unless you explicitly share them. Contact details are only visible to agents you authorise.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Notifications</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-white/80">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={profile.emailNotifications}
                onChange={(e) => handleProfileChange("emailNotifications", e.target.checked)}
              />
              Email updates on important activity
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={profile.platformNotifications}
                onChange={(e) => handleProfileChange("platformNotifications", e.target.checked)}
              />
              In-app notifications
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={profile.notificationChannels?.sms || false}
                onChange={() => handleChannelToggle("sms")}
              />
              SMS summaries
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={profile.notificationChannels?.email ?? true}
                onChange={() => handleChannelToggle("email")}
              />
              Transactional emails (cannot be fully disabled)
            </label>
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Finance readiness</h2>
          <p className="text-sm text-white/70">
            Upload proof of funds, mortgage agreements, or lender letters to help agents assess buying power.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-white/80">
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Status</p>
              <p className="text-white font-semibold">{financeStatus}</p>
              <p className="text-xs text-white/60">
                {financeDocs.length || "No"} finance document{financeDocs.length === 1 ? "" : "s"} on file.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Last update</p>
              <p className="text-white font-semibold">
                {lastFinanceUpdate
                  ? lastFinanceUpdate.toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit"
                    })
                  : "Pending"}
              </p>
              <p className="text-xs text-white/60">Agents see the latest timestamp automatically.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Quick actions</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl bg-white text-black px-3 py-2 text-xs font-medium hover:bg-white/90"
                  onClick={() => setDocForm((prev) => ({ ...prev, type: "Proof of funds" }))}
                >
                  Add proof of funds
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-white/20 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10 transition"
                  onClick={() => setDocForm((prev) => ({ ...prev, type: "Mortgage in principle" }))}
                >
                  Add mortgage doc
                </button>
              </div>
              <p className="text-xs text-white/60">
                Verified buyers are highlighted for sellers inside HOME AI.
              </p>
            </div>
          </div>
          {financeDocs.length > 0 && (
            <ul className="text-xs text-white/70 space-y-1 list-disc pl-5">
              {financeDocs.map((doc) => (
                <li key={doc.id || doc.name}>
                  {doc.name} • {doc.type} • Added{" "}
                  {new Date(doc.addedAt).toLocaleDateString("en-GB")}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Documents</h2>
          <div className="grid md:grid-cols-[1fr,200px] gap-4 text-sm">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/50">Document name</label>
              <input
                className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                value={docForm.name}
                onChange={(e) => setDocForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/50">Type</label>
              <select
                className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                value={docForm.type}
                onChange={(e) => setDocForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                <option>Contract</option>
                <option>ID</option>
                <option>Proof of address</option>
                <option>Proof of funds</option>
                <option>Mortgage in principle</option>
                <option>Land Registry</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-white/70">
            <input
              type="checkbox"
              checked={docForm.isPrivate}
              onChange={(e) => setDocForm((prev) => ({ ...prev, isPrivate: e.target.checked }))}
            />
            Keep private (only visible to authorised agents)
          </label>
          <button
            type="button"
            onClick={handleAddDocument}
            className="rounded-xl bg-white text-black px-4 py-2 text-sm font-medium hover:bg-white/90"
          >
            Add document record
          </button>
          <ul className="space-y-2 text-sm text-white/80">
            {(profile.uploadedDocs || []).length === 0 && (
              <li className="text-white/50 text-xs">No documents logged yet.</li>
            )}
            {(profile.uploadedDocs || []).map((doc) => (
              <li
                key={doc.id}
                className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 flex items-center justify-between gap-3"
              >
                <div>
                  <p className="font-medium text-white">{doc.name}</p>
                  <p className="text-xs text-white/60">
                    {doc.type} • {doc.isPrivate ? "Private" : "Shared"} •{" "}
                    {new Date(doc.addedAt).toLocaleDateString("en-GB")}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveDocument(doc.id)}
                  className="text-xs text-red-300 hover:text-red-200"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Viewing preferences</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                checked={profile.openHouseOnly}
                onChange={(e) => handleProfileChange("openHouseOnly", e.target.checked)}
              />
              Prefer open-house format only
            </label>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Availability slots</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <input
                  type="date"
                  className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={availabilityForm.date}
                  onChange={(e) => setAvailabilityForm((prev) => ({ ...prev, date: e.target.value }))}
                />
                <input
                  type="time"
                  className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={availabilityForm.time}
                  onChange={(e) => setAvailabilityForm((prev) => ({ ...prev, time: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={handleAddAvailability}
                  className="rounded-xl bg-white text-black px-3 py-2 text-sm font-medium hover:bg-white/90"
                >
                  Save slot
                </button>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-white/80">
                {(profile.viewingAvailability || []).length === 0 && (
                  <li className="text-xs text-white/50">No times saved yet.</li>
                )}
                {(profile.viewingAvailability || []).map((slot) => (
                  <li
                    key={slot.id}
                    className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 flex items-center justify-between gap-3"
                  >
                    <span>
                      {slot.date} • {slot.time}
                    </span>
                    <button
                      className="text-xs text-red-300 hover:text-red-200"
                      onClick={() => handleRemoveAvailability(slot.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="rounded-xl bg-white text-black px-5 h-12 text-sm font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save profile"}
          </button>
          {saved && (
            <span className="text-xs text-green-400 self-center">
              ✓ Last saved {new Date(saved).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
      </div>
    </main>
  );
}

function sessionHasRole(session, role) {
  return !!session?.roles?.includes(role);
}

function deriveSessionLabel(session) {
  if (!session) return "guest";
  if (sessionHasRole(session, Roles.ADMIN)) return "admin";
  if (sessionHasRole(session, Roles.AGENT)) return "agent";
  if (sessionHasRole(session, Roles.SELLER) && sessionHasRole(session, Roles.BUYER)) return "buyer & seller";
  if (sessionHasRole(session, Roles.SELLER)) return "seller";
  if (sessionHasRole(session, Roles.BUYER)) return "buyer";
  return "member";
}

function RequireRoles({ allowed = [], children }) {
  const { session } = useCurrentSession();
  if (!session) return <Navigate to="/" replace />;
  if (allowed.length && !allowed.some((role) => sessionHasRole(session, role))) {
    return <Navigate to="/" replace />;
  }
  return children;
}

const SellerStatus = {
  PROSPECT: "prospect",
  CHECKOUT: "checkout",
  ACTIVE: "active"
};

function TopNav() {
  const navigate = useNavigate();
  const { session } = useCurrentSession();

  const handleSignOut = () => {
    clearSession();
    window.location.href = "/";
  };

  if (!session) {
    return null;
  }
  if (!["google", "test"].includes(session.provider)) {
    return null;
  }

  return (
    <nav className="sticky top-0 z-[70] border-b border-white/5 bg-black/80 backdrop-blur-2xl">
      <div className="max-w-6xl mx-auto px-8 py-4 flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-white/70">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/login")}
            className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-semibold text-[0.7rem] text-white transition hover:border-emerald-400 hover:text-white hover:bg-emerald-500/10"
          >
            Home
          </button>
          <button
            onClick={() => navigate("/profile")}
            className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-semibold text-[0.7rem] text-white transition hover:border-emerald-400 hover:text-white hover:bg-emerald-500/10"
          >
            Profile
          </button>
          <button
            onClick={() => navigate("/seller")}
            className="hidden sm:inline-flex rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-semibold text-[0.7rem] text-white transition hover:border-emerald-400 hover:text-white hover:bg-emerald-500/10"
          >
            Sell
          </button>
          <button
            onClick={() => navigate("/buyer")}
            className="hidden sm:inline-flex rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-semibold text-[0.7rem] text-white transition hover:border-emerald-400 hover:text-white hover:bg-emerald-500/10"
          >
            Buy
          </button>
        </div>
        <div className="flex-1" />
        <button
          onClick={handleSignOut}
          className="rounded-full border border-rose-500/60 bg-rose-500/20 px-4 py-1.5 font-semibold text-[0.7rem] text-white transition hover:bg-rose-500/40"
        >
          Sign out
        </button>
      </div>
    </nav>
  );
}

const SELLER_PREMIUM_THRESHOLD = 1_500_000;

const SELLER_PROFILES_KEY = "home_seller_profiles_v1";

const FEATURE_OPTIONS = [
  {
    key: "premiumFinish",
    label: "High-spec interiors",
    description: "Designer kitchen, premium bathrooms, bespoke joinery",
    adjustment: 0.04
  },
  {
    key: "extendedOrLoft",
    label: "Extended / loft converted",
    description: "Recent extension or loft conversion adding usable space",
    adjustment: 0.035
  },
  {
    key: "gardenOrTerrace",
    label: "Private garden or terrace",
    description: "Significant exclusive outdoor space",
    adjustment: 0.025
  },
  {
    key: "secureParking",
    label: "Secure parking",
    description: "Allocated off-street parking or private garage",
    adjustment: 0.02
  },
  {
    key: "chainFree",
    label: "Chain-free sale",
    description: "No onward purchase required",
    adjustment: 0.01
  },
  {
    key: "highEnergyRating",
    label: "High energy efficiency",
    description: "EPC A/B or recent eco upgrades",
    adjustment: 0.015
  }
];

const FEATURE_ADJUSTMENTS = Object.fromEntries(
  FEATURE_OPTIONS.map(({ key, adjustment }) => [key, adjustment])
);
const FEATURE_LABELS = Object.fromEntries(
  FEATURE_OPTIONS.map(({ key, label }) => [key, label])
);

const CONDITION_OPTIONS = [
  {
    key: "needs_work",
    label: "Needs modernisation",
    description: "Requires refurbishment",
    adjustment: -0.05,
    spread: 0.015
  },
  {
    key: "average",
    label: "Good / average",
    description: "Well-maintained, typical condition",
    adjustment: 0,
    spread: 0
  },
  {
    key: "excellent",
    label: "Turn-key / recently refurbished",
    description: "Move-in ready with recent upgrades",
    adjustment: 0.03,
    spread: -0.01
  }
];

const CONDITION_ADJUSTMENTS = Object.fromEntries(
  CONDITION_OPTIONS.map(({ key, adjustment }) => [key, adjustment])
);
const CONDITION_LABELS = Object.fromEntries(
  CONDITION_OPTIONS.map(({ key, label }) => [key, label])
);
const CONDITION_SPREAD_ADJUSTMENTS = Object.fromEntries(
  CONDITION_OPTIONS.map(({ key, spread }) => [key, spread])
);

const SQM_TO_SQFT = 10.7639;

function createRefinementState(profile, subject = {}) {
  return {
    condition: profile?.condition || subject?.condition || "average",
    features: Array.isArray(profile?.valuationFeatures) ? profile.valuationFeatures : [],
    floorArea: profile?.floorArea ? String(profile.floorArea) : "",
    floorAreaUnit: profile?.floorAreaUnit || subject?.floorAreaUnit || "sqft",
    renovationYear: profile?.renovationYear ? String(profile.renovationYear) : "",
    specialFeatureNotes: profile?.specialFeatureNotes || ""
  };
}

function determineSellerPlan(valuation, fallbackPrice) {
  const anchor = valuation?.midpoint ?? valuation?.baseline ?? fallbackPrice ?? 0;
  const premium = anchor >= SELLER_PREMIUM_THRESHOLD;
  if (premium) {
    return {
      key: "premier",
      label: "Premier marketing package",
      description: "For homes £1.5m+ including enhanced cinematography and international reach.",
      upfrontFee: 3000,
      successFeePercent: 1,
      benefits: [
        "Full photography & videography inc. drone capture",
        "Bespoke brochure & floorplans",
        "Rightmove, Zoopla, HOME AI & global syndication",
        "Dedicated sales progression concierge",
        "AI-assisted reporting & weekly strategy clinics"
      ]
    };
  }
  return {
    key: "core",
    label: "Core marketing package",
    description: "For homes under £1.5m with turn-key marketing and AI support.",
    upfrontFee: 1000,
    successFeePercent: 1,
    benefits: [
        "Professional photography & floorplans",
        "For sale board & portal syndication",
        "Buyer qualification & offer handling",
        "AI progress assistant & milestone alerts",
        "Weekly performance reporting"
    ]
  };
}

/* ----------------------- tiny store helpers ----------------------- */
const KEYS = {
  FAVS: "home_favs_v1",
  ALERTS: "home_alerts_v1",
  VIEWS: "home_views_v1"
};
const loadJSON = (k, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(k) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
};
const saveJSON = (k, v) => localStorage.setItem(k, JSON.stringify(v));

// ============================================================================
// SELLER PROFILE - DATABASE-BACKED (NEW)
// ============================================================================

import {
  getSellerOnboarding,
  createOrUpdateOnboarding,
  updateOnboardingStatus,
  migrateLocalStorageData
} from "./api/sellerOnboarding";

// In-memory cache for seller onboarding data
let sellerOnboardingCache = null;
let sellerOnboardingPromise = null;

/**
 * Load seller onboarding from API (with caching)
 */
async function loadSellerOnboardingFromAPI() {
  // Return cached data if available
  if (sellerOnboardingCache) {
    return sellerOnboardingCache;
  }

  // Return existing promise if already loading
  if (sellerOnboardingPromise) {
    return sellerOnboardingPromise;
  }

  // Fetch from API
  sellerOnboardingPromise = getSellerOnboarding()
    .then(contact => {
      sellerOnboardingCache = contact;
      sellerOnboardingPromise = null;
      return contact;
    })
    .catch(error => {
      console.error("Failed to load seller onboarding:", error);
      sellerOnboardingPromise = null;
      return null;
    });

  return sellerOnboardingPromise;
}

/**
 * Clear seller onboarding cache
 */
function clearSellerOnboardingCache() {
  sellerOnboardingCache = null;
}

/**
 * Migrate localStorage data on login
 */
async function migrateSellerDataIfNeeded() {
  try {
    const localStorageData = loadSellerProfiles();
    
    // Check if there's any data to migrate
    if (Object.keys(localStorageData).length === 0) {
      return { migrated: false, message: "No data to migrate" };
    }

    const result = await migrateLocalStorageData(localStorageData);
    
    if (result.migrated) {
      // Clear localStorage after successful migration
      localStorage.removeItem(SELLER_PROFILES_KEY);
      clearSellerOnboardingCache();
      console.log("Successfully migrated seller data from localStorage");
    }
    
    return result;
  } catch (error) {
    console.error("Failed to migrate seller data:", error);
    return { migrated: false, error: error.message };
  }
}

/**
 * Get seller profile (API only - NO localStorage fallback)
 */
async function getSellerProfileAsync() {
  try {
    // Try to load from API
    const onboarding = await loadSellerOnboardingFromAPI();
    
    if (onboarding) {
      // Map database fields back to UI format
      return {
        id: onboarding.id,
        status: onboarding.status,
        address: onboarding.address,
        postcode: onboarding.postcode,
        beds: onboarding.beds,
        baths: onboarding.baths,
        sqft: onboarding.sqft,
        propertyType: onboarding.propertyType,
        condition: onboarding.condition,
        valuationFeatures: onboarding.features || [],
        floorArea: onboarding.floorArea,
        floorAreaUnit: onboarding.floorAreaUnit,
        renovationYear: onboarding.renovationYear,
        specialFeatureNotes: onboarding.specialFeatureNotes,
        valuation: {
          baseline: onboarding.baselineValuation,
          midpoint: onboarding.adjustedValuation,
          estimate: onboarding.adjustedValuation,
          low: onboarding.valuationLow,
          high: onboarding.valuationHigh
        },
        planKey: onboarding.planKey,
        upfrontFee: onboarding.upfrontFee,
        successFeePercent: onboarding.successFeePercent,
        paymentReference: onboarding.paymentReference,
        onboardedAt: onboarding.onboardedAt ? new Date(onboarding.onboardedAt).getTime() : null,
        contractSignedAt: onboarding.contractSignedAt ? new Date(onboarding.contractSignedAt).getTime() : null,
        activeListingId: onboarding.activeListingId,
        _fromAPI: true,
        _updatedAt: new Date(onboarding.updatedAt).getTime()
      };
    }
    
    // No data found - return null (don't fall back to localStorage)
    return null;
  } catch (error) {
    console.error("❌ CRITICAL: Failed to load seller profile from API:", error);
    
    // DO NOT fall back to localStorage
    // If the API is down, we cannot guarantee data integrity
    throw new Error(
      "Unable to load your property details. Please check your internet connection and try again."
    );
  }
}

/**
 * Update seller profile (saves to API)
 */
async function updateSellerProfileAsync(patch) {
  try {
    // Get current profile (may be null for new anonymous users)
    let current = null;
    try {
      current = await getSellerProfileAsync();
    } catch (error) {
      // Expected for anonymous users - they don't have a profile yet
      console.log("No existing profile (expected for new users):", error.message);
    }
    
    // Prepare data for API - use patch values directly if no current profile
    const apiData = {
      address: patch.address ?? current?.address,
      postcode: patch.postcode ?? current?.postcode,
      beds: patch.beds ?? current?.beds,
      baths: patch.baths ?? current?.baths,
      sqft: patch.sqft ?? current?.sqft,
      propertyType: patch.propertyType ?? current?.propertyType,
      condition: patch.condition ?? current?.condition,
      features: patch.valuationFeatures ?? current?.valuationFeatures,
      floorArea: patch.floorArea ?? current?.floorArea,
      floorAreaUnit: patch.floorAreaUnit ?? current?.floorAreaUnit,
      renovationYear: patch.renovationYear ?? current?.renovationYear,
      specialFeatureNotes: patch.specialFeatureNotes ?? current?.specialFeatureNotes,
      baselineValuation: patch.valuation?.baseline ?? current?.valuation?.baseline,
      adjustedValuation: patch.valuation?.midpoint ?? patch.valuation?.estimate ?? current?.valuation?.midpoint,
      valuationLow: patch.valuation?.low ?? current?.valuation?.low,
      valuationHigh: patch.valuation?.high ?? current?.valuation?.high,
      planKey: patch.planKey ?? current?.planKey,
      upfrontFee: patch.upfrontFee ?? current?.upfrontFee,
      successFeePercent: patch.successFeePercent ?? current?.successFeePercent
    };

    // Remove undefined values
    Object.keys(apiData).forEach(key => {
      if (apiData[key] === undefined) {
        delete apiData[key];
      }
    });

    // Save to API
    const response = await createOrUpdateOnboarding(apiData);
    
    // Update cache
    clearSellerOnboardingCache();
    
    // If status change, use separate endpoint (only when we have a real onboarding record)
    if (patch.status && current?.id && current?.status) {
      const statusData = {
        status: patch.status,
        paymentReference: patch.paymentReference,
        contractSignedAt: patch.contractSignedAt ? new Date(patch.contractSignedAt).toISOString() : undefined,
        onboardedAt: patch.onboardedAt ? new Date(patch.onboardedAt).toISOString() : undefined,
        activeListingId: patch.activeListingId
      };
      
      await updateOnboardingStatus(current.id, statusData);
    }
    
    // Use the response directly if available, otherwise fetch from API
    const responseContact = response?.contact || response?.data?.contact;
    if (responseContact) {
      // Map the contact response to profile format
      const contact = responseContact;
      sellerOnboardingCache = contact;
      return {
        id: contact.id,
        status: patch.status || current?.status,
        address: apiData.address,
        postcode: apiData.postcode,
        beds: apiData.beds,
        baths: apiData.baths,
        sqft: apiData.sqft,
        propertyType: apiData.propertyType,
        condition: apiData.condition,
        valuationFeatures: apiData.features || [],
        floorArea: apiData.floorArea,
        floorAreaUnit: apiData.floorAreaUnit,
        renovationYear: apiData.renovationYear,
        specialFeatureNotes: apiData.specialFeatureNotes,
        valuation: {
          baseline: apiData.baselineValuation,
          midpoint: apiData.adjustedValuation,
          estimate: apiData.adjustedValuation,
          low: apiData.valuationLow,
          high: apiData.valuationHigh
        },
        planKey: apiData.planKey,
        upfrontFee: apiData.upfrontFee,
        successFeePercent: apiData.successFeePercent,
        _fromAPI: true,
        _updatedAt: new Date().getTime()
      };
    }
    
    // Fallback: Return updated profile from API
    return await getSellerProfileAsync();
  } catch (error) {
    console.error("❌ CRITICAL: Failed to save property details to database:", error);
    
    // Check if it's an authentication error
    if (error?.message?.includes("Authentication required") || error?.message?.includes("401")) {
      throw new Error(
        "Please sign in to continue. We need to create an account to save your property details securely."
      );
    }
    
    // DO NOT fall back to localStorage - this is business-critical data
    // If the API fails, the user MUST know their data is not saved
    throw new Error(
      "We couldn't save your property details. Please check your internet connection and try again. " +
      "Your information has NOT been saved."
    );
  }
}

// ============================================================================
// SELLER PROFILE - LEGACY FUNCTIONS REMOVED
// ============================================================================
// All seller data persistence now goes through API-only functions:
// - getSellerProfileAsync() for reads
// - updateSellerProfileAsync() for writes
//
// localStorage business data paths have been permanently disabled.
// ============================================================================

/**
 * @deprecated Use getSellerProfileAsync() instead
 * @throws {Error} Always - this function is permanently disabled
 */
function loadSellerProfiles() {
  throw new Error(
    "[PRODUCTION BLOCKER] loadSellerProfiles() is deprecated.\n" +
    "Use getSellerProfileAsync() for API-backed data retrieval.\n" +
    "localStorage business data access is forbidden in production."
  );
}

/**
 * @deprecated Use updateSellerProfileAsync() instead
 * @throws {Error} Always - this function is permanently disabled
 */
function saveSellerProfiles(profiles) {
  throw new Error(
    "[PRODUCTION BLOCKER] saveSellerProfiles() is deprecated.\n" +
    "Use updateSellerProfileAsync() for API-backed data persistence.\n" +
    "localStorage business data writes are forbidden in production."
  );
}

function getCurrentUserEmail() {
  const session = getSession();
  return session?.email ? session.email.toLowerCase() : null;
}

/**
 * @deprecated Use getSellerProfileAsync() instead
 * @throws {Error} Always - this function is permanently disabled
 */
function getSellerProfile() {
  throw new Error(
    "[PRODUCTION BLOCKER] getSellerProfile() is deprecated.\n" +
    "Use getSellerProfileAsync() for API-backed data retrieval.\n" +
    "All seller data must be loaded from the database via API."
  );
}

/**
 * @deprecated Use updateSellerProfileAsync() instead
 * @throws {Error} Always - this function is permanently disabled
 */
function updateSellerProfile(patch) {
  throw new Error(
    "[PRODUCTION BLOCKER] updateSellerProfile() is deprecated.\n" +
    "Use updateSellerProfileAsync() for API-backed data persistence.\n" +
    "\n" +
    "This function wrote to localStorage, violating API-only architecture.\n" +
    "All seller data MUST be persisted to the database via API calls.\n" +
    "\n" +
    "Migration guide:\n" +
    "  OLD: updateSellerProfile({ address: '123 Main St' })\n" +
    "  NEW: await updateSellerProfileAsync({ address: '123 Main St' })\n" +
    "\n" +
    "If you see this error in production, the codebase contains a critical bug."
  );
}

const USER_PROFILES_KEY = "home_user_profiles_v1";

const DEFAULT_USER_PROFILE = {
  avatar: "",
  displayName: "",
  summary: "",
  phone: "",
  shareContactDetails: false,
  emailNotifications: true,
  platformNotifications: true,
  docVisibility: "private",
  uploadedDocs: [],
  viewingAvailability: [],
  boardPreference: "undecided",
  openHouseOnly: false,
  notificationChannels: {
    email: true,
    sms: false,
    inApp: true
  },
  updatedAt: null
};

// Member profiles removed - use API /api/profile/me exclusively
// Stub to prevent runtime crashes until buyer portal is rebuilt
const MOCK_MEMBER_PROFILES = {};

function loadUserProfiles() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(USER_PROFILES_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveUserProfiles(profiles) {
  if (typeof window === "undefined") return;
  saveJSON(USER_PROFILES_KEY, profiles);
}

function getUserProfile() {
  const email = getCurrentUserEmail();
  if (!email) return { ...DEFAULT_USER_PROFILE };

  const profiles = loadUserProfiles();
  if (profiles[email]) {
    return { ...DEFAULT_USER_PROFILE, ...profiles[email] };
  }

  const session = getSession();
  return {
    ...DEFAULT_USER_PROFILE,
    displayName: session?.name || email
  };
}

function updateUserProfile(patch) {
  const email = getCurrentUserEmail();
  if (!email) return { ...DEFAULT_USER_PROFILE, ...patch };

  const profiles = loadUserProfiles();
  const existing = profiles[email] || getUserProfile();
  const updated = {
    ...existing,
    ...patch,
    updatedAt: Date.now()
  };
  profiles[email] = updated;
  saveUserProfiles(profiles);
  return updated;
}

/* ---------- currency helpers ---------- */
const CURRENCY_KEY = "home_currency_v1";
const DEFAULT_CCY = "GBP";

/* ----------------------- Helper Components ----------------------- */

// Confirmation Modal Component
function ConfirmationModal({ open, onClose, onConfirm, title, message, confirmText = "Confirm", confirmStyle = "primary" }) {
  if (!open) return null;
  
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
        <p className="text-sm text-neutral-600 mb-4">{message}</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-neutral-300 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={
              confirmStyle === "danger"
                ? "px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700"
                : confirmStyle === "success"
                ? "px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
                : "px-4 py-2 rounded-xl bg-black text-white hover:bg-black/90"
            }
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Coming Soon Modal Component
function ComingSoonModal({ open, onClose, feature, workaround, expectedDate }) {
  if (!open) return null;
  
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl">🚧</span>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-neutral-900">{feature} - Coming Soon</h3>
            {expectedDate && (
              <p className="text-xs text-neutral-500 mt-1">Expected: {expectedDate}</p>
            )}
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800">
            ✕
          </button>
        </div>
        {workaround && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 mb-4">
            <div className="font-medium mb-1">Meanwhile:</div>
            <div>{workaround}</div>
          </div>
        )}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 rounded-xl bg-black text-white hover:bg-black/90"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

// Success Notification Modal
function SuccessModal({ open, onClose, title, message, actions }) {
  if (!open) return null;
  
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl">✓</span>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-emerald-900">{title}</h3>
            <p className="text-sm text-neutral-600 mt-2">{message}</p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800">
            ✕
          </button>
        </div>
        {actions && actions.length > 0 && (
          <div className="space-y-2 mb-4">
            <div className="text-xs font-medium text-neutral-700 uppercase tracking-wide">Next Steps:</div>
            <ul className="space-y-1">
              {actions.map((action, idx) => (
                <li key={idx} className="text-sm text-neutral-700 flex items-start gap-2">
                  <span className="text-emerald-600">→</span>
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// User-Friendly Status Badge Component
const STATUS_MAPPING = {
  // Offer statuses
  "Negotiating": { label: "Under Review", color: "amber", icon: "🔄" },
  "Accepted": { label: "Accepted", color: "emerald", icon: "✓" },
  "Rejected": { label: "Declined", color: "red", icon: "✕" },
  "NEGOTIATING": { label: "Under Review", color: "amber", icon: "🔄" },
  "ACCEPTED": { label: "Accepted", color: "emerald", icon: "✓" },
  "REJECTED": { label: "Declined", color: "red", icon: "✕" },
  "WITHDRAWN": { label: "Withdrawn", color: "gray", icon: "↩" },
  "RECEIVED": { label: "Received", color: "blue", icon: "📨" },
  
  // Viewing statuses
  "Confirmed": { label: "Scheduled", color: "emerald", icon: "📅" },
  "Proposed": { label: "Awaiting Confirmation", color: "amber", icon: "⏳" },
  "Awaiting feedback": { label: "Feedback Pending", color: "blue", icon: "💬" },
  
  // Legal stages
  "Heads of terms": { label: "Agreement Phase", color: "blue", icon: "📋" },
  "Exchange": { label: "Legal Exchange", color: "purple", icon: "⚖️" },
  "Declined": { label: "Declined", color: "red", icon: "✕" },
  
  // Verification
  "Verified": { label: "Verified", color: "emerald", icon: "✓" },
  "In review": { label: "Under Review", color: "amber", icon: "🔍" },
  "Pending": { label: "Awaiting Upload", color: "gray", icon: "📄" }
};

function StatusBadge({ status, showIcon = true, className = "" }) {
  const friendly = STATUS_MAPPING[status] || { label: status, color: "gray", icon: "•" };
  
  const colorClasses = {
    emerald: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
    amber: "bg-amber-500/20 text-amber-200 border-amber-400/40",
    red: "bg-rose-500/20 text-rose-200 border-rose-400/40",
    blue: "bg-blue-500/20 text-blue-200 border-blue-400/40",
    purple: "bg-purple-500/20 text-purple-200 border-purple-400/40",
    gray: "bg-white/10 text-white/60 border-white/20"
  };
  
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] border ${colorClasses[friendly.color]} ${className}`}
    >
      {showIcon && `${friendly.icon} `}{friendly.label}
    </span>
  );
}

// Demo rates (1 GBP = X). Replace with live rates later.
const FX = { GBP: 1, USD: 1.27, EUR: 1.17, AED: 4.66 };

function convertAmount(amount, from = "GBP", to = "GBP") {
  if (!amount || from === to) return amount || 0;
  const fr = FX[from] ?? 1;
  const tr = FX[to] ?? 1;
  return Math.round((amount / fr) * tr);
}

function formatAmount(amount, currency = "GBP") {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount || 0);
}

/** Returns { amount, currency, text } for display */
function displayPrice(listing, prefCurrency) {
  const src = (listing && listing.currency) || "GBP";
  const out = prefCurrency || DEFAULT_CCY;
  const amt = convertAmount(listing?.price || 0, src, out);
  return { amount: amt, currency: out, text: formatAmount(amt, out) };
}

function useCurrency() {
  const [ccy, setCcy] = useState(localStorage.getItem(CURRENCY_KEY) || DEFAULT_CCY);
  useEffect(() => localStorage.setItem(CURRENCY_KEY, ccy), [ccy]);
  return [ccy, setCcy];
}

/* ----------------------- shared UI bits ----------------------- */
function HomeButton() {
  const nav = useNavigate();
  const session = getSession();
  if (session && ["google", "test"].includes(session.provider)) {
    return null;
  }
  return (
    <button
      aria-label="Go home"
      onClick={() => nav("/")}
      className="fixed left-4 top-4 z-[60] rounded-full bg-black/70 backdrop-blur text-white p-2 border border-white/15 hover:bg-black/85"
      title="Home"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 11.5L12 4l9 7.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 10.5V20h5v-5h4v5h5v-9.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function GlowWord({ children }) {
  return (
    <span className="relative inline-block">
      <span className="text-white font-semibold">{children}</span>
      <span className="absolute inset-0 blur-[6px] opacity-70 bg-white/60 rounded pointer-events-none" />
    </span>
  );
}

function TalkToHomeAIButton({ onClick, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={
        "rounded-xl px-5 h-12 text-sm font-medium bg-black text-white " +
        "hover:bg-black/90 transition inline-flex items-center justify-center " +
        className
      }
    >
      Talk to <span className="mx-1 font-semibold shine-text">home</span> AI
    </button>
  );
}

function CurrencySwitcher({ value, onChange }) {
  const options = ["GBP", "USD", "EUR", "AED"];
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-neutral-600">Currency</span>
      <div className="flex rounded-xl border border-neutral-300 overflow-hidden">
        {options.map((c) => (
          <button
            key={c}
            onClick={() => onChange(c)}
            className={
              "px-3 py-1.5 text-sm " + (value === c ? "bg-black text-white" : "bg-white hover:bg-neutral-100")
            }
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

/* minimalist system icon (stroke only) */
function Icon({ path, size = 18, className = "text-neutral-900" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
}
const IBed = <path d="M3 18v-6a2 2 0 0 1 2-2h7a3 3 0 0 1 3 3v5H3zm0-7h16a2 2 0 0 1 2 2v5H3" />;
const IBath = (
  <>
    <path d="M7 10V6a3 3 0 0 1 6 0v4" />
    <path d="M5 10h12v3a5 5 0 0 1-5 5h-2a5 5 0 0 1-5-5v-3" />
  </>
);
const IArea = <path d="M4 4h16v16H4z M9 4v16 M4 9h16" />;
const IMapPin = (
  <>
    <path d="M12 21s-6-5.5-6-10a6 6 0 1 1 12 0c0 4.5-6 10-6 10z" />
    <circle cx="12" cy="11" r="2.5" />
  </>
);
const IPrice = <path d="M12 2v20M17 5H9a3 3 0 0 0 0 6h6a3 3 0 1 1 0 6H7" />;
const IShare = (
  <>
    <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
    <path d="M12 16V4" />
    <path d="M8 8l4-4 4 4" />
  </>
);
const IHeart = <path d="M20.8 7.6a5.5 5.5 0 0 0-9.8-3.6A5.5 5.5 0 1 0 4 16l8 6 8-6a5.5 5.5 0 0 0 .8-8.4z" />;

/* ----------------------- Landing ----------------------- */
function Landing() {
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const faqRef = useRef(null);
  const navigate = useNavigate();
  const { session, loading } = useCurrentSession();

  // Redirect authenticated users to appropriate dashboard
  useEffect(() => {
    // Wait for session to load before redirecting
    if (loading) return;
    
    console.log("[Landing] Current session:", session, "loading:", loading);
    
    if (session?.roles?.length) {
      console.log("[Landing] Session has roles:", session.roles);
      if (sessionHasRole(session, Roles.ADMIN) || sessionHasRole(session, Roles.AGENT)) {
        console.log("[Landing] Redirecting to /crm");
        navigate("/crm", { replace: true });
      } else {
        console.log("[Landing] Session roles don't match ADMIN or AGENT:", session.roles, "ADMIN=", Roles.ADMIN, "AGENT=", Roles.AGENT);
      }
    } else {
      console.log("[Landing] No roles in session");
    }
  }, [session, loading, navigate]);

  const handleScrollToFAQ = () => {
    faqRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <main className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6">
        <motion.h1
          className="mt-6 text-4xl md:text-6xl font-light tracking-tight"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <span className="font-extralight">welcome</span>{" "}
          <span className="font-semibold shine-text">home</span>
        </motion.h1>

        <motion.div
          className="mt-12 grid grid-cols-2 gap-4 w-full max-w-xl"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <button
            onClick={() => navigate("/seller")}
            className="group w-full border border-white/20 rounded-2xl px-6 py-4 backdrop-blur hover:border-white/40 transition flex items-center justify-between"
          >
            <span className="text-lg">
              I am a property <strong>SELLER</strong>
            </span>
            <span className="transition group-hover:translate-x-1">→</span>
          </button>

          <button
            onClick={() => navigate("/buyer")}
            className="group w-full border border-white/20 rounded-2xl px-6 py-4 backdrop-blur hover:border-white/40 transition flex items-center justify-between"
          >
            <span className="text-lg">
              I am a property <strong>BUYER</strong>
            </span>
            <span className="transition group-hover:translate-x-1">→</span>
          </button>
        </motion.div>

        <button
          onClick={() => setOpenSignIn(true)}
          className="mt-6 underline underline-offset-4 text-white/70 hover:text-white/90"
        >
          Existing members: click here
        </button>
        <GoogleSignIn
          open={openSignIn}
          onClose={() => setOpenSignIn(false)}
          onSignedIn={async (session) => {
            if (session?.roles?.length) {
              // Use navigate instead of window.location for better React Router handling
              navigate("/crm", { replace: true });
            } else {
              // If no roles, try to refresh session from API
              try {
                const { refreshSessionFromAPI } = await import("./auth/session.js");
                const refreshed = await refreshSessionFromAPI();
                if (refreshed?.roles?.length) {
                  navigate("/crm", { replace: true });
                }
              } catch (error) {
                console.error("Failed to refresh session:", error);
              }
            }
          }}
        />

        <div className="mt-10 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Home. All rights reserved.</span>
        </div>
        <div className="mt-6 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={handleScrollToFAQ}
            className="text-white/70 hover:text-white transition flex flex-col items-center gap-2"
          >
            <span className="text-xs tracking-[0.5em] uppercase">More</span>
            <span className="animate-bounce">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M6 10l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        </div>
      </main>

      <section ref={faqRef} className="bg-black text-white border-t border-white/5 px-6 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-[0.5em] text-white/40">Home AI — FAQs</p>
            <h2 className="text-3xl font-light">Frequently Asked Questions</h2>
            <p className="text-white/70 text-sm max-w-2xl mx-auto">
              Scroll through the essentials on valuations, launch timelines, developer briefs and buyer workflows.
            </p>
          </div>
          <div className="divide-y divide-white/10 rounded-3xl border border-white/10 bg-white/5 backdrop-blur">
            {FAQ_BLOCK.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <button
                  key={item.q}
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  className="w-full text-left px-6 py-5 flex flex-col gap-2 hover:bg-white/5 transition"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-base font-medium">{item.q}</span>
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/30 text-xs transition ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </div>
                  <div
                    className={`text-sm text-white/70 leading-relaxed transition-[max-height,opacity] duration-300 ${
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                    }`}
                  >
                    {item.a}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ----------------------- Seller ----------------------- */
function SellerStart() {
  const [form, setForm] = useState({
    postcode: "",
    address: "",
    beds: "",
    baths: "",
    email: "",
    phone: ""
  });
  const [pcStatus, setPcStatus] = useState({ checked: false, ok: false, note: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [geocode, setGeocode] = useState(null);
  const navigate = useNavigate();
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  async function onPostcodeBlur() {
    const pc = normalisePostcode(form.postcode);
    if (!pc) return;
    update("postcode", pc);
    const res = await verifyPostcode(pc);
    if (res.ok) {
      setPcStatus({ checked: true, ok: true, note: `${res.admin_district || "Verified"} ✓` });
      setGeocode({ lat: res.lat, lng: res.lng });
    } else {
      setPcStatus({ checked: true, ok: false, note: res.error || "Invalid postcode" });
      setGeocode(null);
    }
  }

  const validate = () => {
    const e = {};
    if (!isUKPostcode(form.postcode)) e.postcode = "Enter a valid UK postcode (e.g., W8 5TT).";
    if (!notEmpty(form.address)) e.address = "Address is required.";
    if (!isNumber(form.beds)) e.beds = "Beds must be a number.";
    if (!isNumber(form.baths)) e.baths = "Baths must be a number.";
    // Email and phone are optional for initial valuation request
    if (form.email && !isEmail(form.email)) e.email = "Enter a valid email.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    console.debug("[SellerStart] Submit clicked", {
      postcode: form.postcode,
      address: form.address,
      beds: form.beds,
      baths: form.baths,
      email: form.email,
      phone: form.phone
    });
    if (!pcStatus.checked) await onPostcodeBlur();
    if (!pcStatus.ok) {
      setErrors((p) => ({ ...p, postcode: "Please enter a valid UK postcode." }));
      return;
    }
    if (!validate()) return;

    setLoading(true);
    setSubmitError(null);

    try {
      // Submit to API - this creates the seller onboarding record
      const payload = {
        address: form.address,
        postcode: form.postcode,
        beds: Number.parseInt(form.beds, 10),
        baths: Number.parseInt(form.baths, 10),
        email: form.email || undefined,
        phone: form.phone || undefined,
        lat: geocode?.lat,
        lng: geocode?.lng
      };

      console.debug("[SellerStart] Submitting onboarding payload", payload);

      // Get AI valuation from Land Registry data
      let valuationData = null;
      try {
        console.debug("[SellerStart] Requesting AI valuation");
        const valuationResponse = await postAIValuation({
          address: {
            line1: payload.address,
            postcode: payload.postcode
          },
          beds: payload.beds,
          baths: payload.baths,
          propertyType: "unknown" // Will be refined in future
        });
        
        if (valuationResponse?.estimate) {
          valuationData = {
            baselineValuation: valuationResponse.estimate.mid,
            adjustedValuation: valuationResponse.estimate.mid,
            valuationLow: valuationResponse.estimate.low,
            valuationHigh: valuationResponse.estimate.high,
            valuationConfidence: valuationResponse.confidence,
            valuationReasoning: valuationResponse.reasoning,
            valuationComparables: valuationResponse.comparables
          };
          console.debug("[SellerStart] AI valuation received", valuationData);
        }
      } catch (valuationError) {
        console.error("[SellerStart] Valuation request failed, continuing with manual estimate", valuationError);
        // Continue without valuation - will use comparable-based estimate
      }

      await updateSellerProfileAsync({ 
        ...payload, 
        ...valuationData,
        status: SellerStatus.PROSPECT 
      });

      console.debug("[SellerStart] Onboarding saved, navigating to /seller/valuation");
      
      // Navigate to valuation page
      navigate("/seller/valuation");
    } catch (error) {
      console.error("Failed to submit valuation request:", error);
      setSubmitError(error?.message || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-neutral-950 text-white p-6 flex items-center justify-center">
      <HomeButton />
      <form onSubmit={onSubmit} className="w-full max-w-2xl space-y-4">
        <h2 className="text-3xl font-semibold">Sell your home</h2>
        <p className="text-white/70">
          Start with a quick snapshot and we’ll follow up to book your appraisal.
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-white/70">Postcode</label>
            <input
              name="postcode"
              className="mt-1 w-full bg-neutral-900 border border-white/10 rounded-xl px-4 py-3"
              placeholder="W8 5TT"
              value={form.postcode}
              onChange={(e) => update("postcode", e.target.value)}
              onBlur={onPostcodeBlur}
            />
            {pcStatus.checked && (
              <p className={`mt-1 text-sm ${pcStatus.ok ? "text-emerald-400" : "text-red-400"}`}>
                {pcStatus.ok ? pcStatus.note : `Postcode error: ${pcStatus.note}`}
              </p>
            )}
            {errors.postcode && <p className="text-red-400 text-sm mt-1">{errors.postcode}</p>}
          </div>

          <div>
            <label className="text-sm text-white/70">Address</label>
            <input
              name="address"
              className="mt-1 w-full bg-neutral-900 border border-white/10 rounded-xl px-4 py-3"
              placeholder="123 Kensington High St"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
            />
            {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
          </div>

          <div>
            <label className="text-sm text-white/70">Beds</label>
            <input
              name="beds"
              className="mt-1 w-full bg-neutral-900 border border-white/10 rounded-xl px-4 py-3"
              placeholder="3"
              value={form.beds}
              onChange={(e) => update("beds", e.target.value)}
            />
            {errors.beds && <p className="text-red-400 text-sm mt-1">{errors.beds}</p>}
          </div>

          <div>
            <label className="text-sm text-white/70">Baths</label>
            <input
              name="baths"
              className="mt-1 w-full bg-neutral-900 border border-white/10 rounded-xl px-4 py-3"
              placeholder="2"
              value={form.baths}
              onChange={(e) => update("baths", e.target.value)}
            />
            {errors.baths && <p className="text-red-400 text-sm mt-1">{errors.baths}</p>}
          </div>

          <div>
            <label className="text-sm text-white/70">Email (optional)</label>
            <input
              name="email"
              type="email"
              className="mt-1 w-full bg-neutral-900 border border-white/10 rounded-xl px-4 py-3"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
            {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="text-sm text-white/70">Phone (optional)</label>
            <input
              name="phone"
              type="tel"
              className="mt-1 w-full bg-neutral-900 border border-white/10 rounded-xl px-4 py-3"
              placeholder="+44 7..."
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
            />
            {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
          </div>
        </div>

        {submitError && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-xl px-4 py-3 text-red-400 text-sm">
            <strong>Error:</strong> {submitError}
          </div>
        )}

        <button 
          type="submit" 
          disabled={loading}
          className="mt-2 bg-white text-black rounded-xl px-5 py-3 font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? "Submitting..." : "Get Valuation"}
        </button>
      </form>
    </main>
  );
}

function computeSellerInsights(raw) {
  if (!raw) {
    return {
      subject: null,
      matched: null,
      comparables: [],
      valuation: null
    };
  }

  const normalisedPostcode = normalisePostcode(raw.postcode || "");
  const comparablePool = LISTINGS.filter(
    (listing) => normalisePostcode(listing.postcode) === normalisedPostcode
  );

  const fallbackPool = comparablePool.length >= 3
    ? comparablePool
    : LISTINGS.filter((listing) => listing.postcode?.slice(0, 2) === (raw.postcode || "").slice(0, 2));

  const comparables = (comparablePool.length ? comparablePool : fallbackPool.length ? fallbackPool : LISTINGS).slice(0, 6);

  const addressToken = (raw.address || "").split(" ")[0]?.toLowerCase();
  const matched =
    comparablePool.find(
      (listing) =>
        addressToken && listing.address?.toLowerCase().includes(addressToken)
    ) || comparablePool[0] || comparables[0] || null;

  const parsedBeds = Number.parseInt(raw.beds, 10);
  const parsedBaths = Number.parseInt(raw.baths, 10);

  const subjectBeds = Number.isFinite(parsedBeds) ? parsedBeds : matched?.beds ?? 3;
  const subjectBaths = Number.isFinite(parsedBaths) ? parsedBaths : matched?.baths ?? 2;

  const features = Array.isArray(raw?.valuationFeatures)
    ? raw.valuationFeatures.filter((key) => Object.prototype.hasOwnProperty.call(FEATURE_ADJUSTMENTS, key))
    : [];
  const conditionKey = Object.prototype.hasOwnProperty.call(CONDITION_ADJUSTMENTS, raw?.condition)
    ? raw?.condition
    : "average";
  const specialFeatureNotes = raw?.specialFeatureNotes || "";
  const renovationYear = Number.parseInt(raw?.renovationYear, 10);
  const yearsSinceRenovation = Number.isFinite(renovationYear)
    ? new Date().getFullYear() - renovationYear
    : null;
  const floorAreaValue = Number.parseFloat(raw?.floorArea);
  const hasFloorArea = Number.isFinite(floorAreaValue) && floorAreaValue > 0;
  const floorAreaUnit = raw?.floorAreaUnit === "sqm" ? "sqm" : "sqft";
  const floorAreaSqft = hasFloorArea
    ? floorAreaUnit === "sqm"
      ? floorAreaValue * SQM_TO_SQFT
      : floorAreaValue
    : null;

  const pricePool = comparables.map((l) => l.price).filter(Boolean);
  // Use API valuation if available, otherwise use comparables
  const baseline = raw?.baselineValuation || 
    (pricePool.length
      ? pricePool.reduce((acc, price) => acc + price, 0) / pricePool.length
      : 850000);

  const averageBeds = comparables.length
    ? comparables.reduce((acc, listing) => acc + (listing.beds || subjectBeds), 0) / comparables.length
    : subjectBeds;

  const bedVariance = subjectBeds - averageBeds;
  const adjustedBaseline = baseline + baseline * 0.045 * bedVariance;
  const spread = comparables.length > 2 ? 0.06 : 0.1;

  let featureMultiplier = 1;
  features.forEach((key) => {
    featureMultiplier += FEATURE_ADJUSTMENTS[key] || 0;
  });
  featureMultiplier += CONDITION_ADJUSTMENTS[conditionKey] || 0;
  if (Number.isFinite(yearsSinceRenovation)) {
    if (yearsSinceRenovation <= 5) featureMultiplier += 0.025;
    else if (yearsSinceRenovation <= 10) featureMultiplier += 0.015;
    else if (yearsSinceRenovation > 25) featureMultiplier -= 0.02;
  }

  let sizeMultiplier = 1;
  const matchedAreaSqft =
    matched?.internal_sqft || (matched?.internal_m2 ? matched.internal_m2 * SQM_TO_SQFT : null);
  if (floorAreaSqft && matchedAreaSqft) {
    const ratio = floorAreaSqft / matchedAreaSqft;
    if (Number.isFinite(ratio) && ratio > 0) {
      sizeMultiplier = Math.min(Math.max(ratio, 0.8), 1.3);
    }
  } else if (floorAreaSqft) {
    const reference = 1200;
    const delta = (floorAreaSqft - reference) / reference;
    sizeMultiplier = Math.min(Math.max(1 + delta * 0.4, 0.85), 1.25);
  }

  const totalMultiplier = Math.max(0.7, featureMultiplier * sizeMultiplier);
  const refinedBaseline = adjustedBaseline * totalMultiplier;

  let effectiveSpread =
    spread + (CONDITION_SPREAD_ADJUSTMENTS[conditionKey] || 0) - Math.min(features.length, 3) * 0.008;
  if (Number.isFinite(yearsSinceRenovation) && yearsSinceRenovation <= 5) effectiveSpread -= 0.005;
  if (floorAreaSqft) effectiveSpread -= 0.004;
  effectiveSpread = Math.min(Math.max(effectiveSpread, 0.02), 0.12);

  // Use stored valuation range if available from AI valuation
  const midpoint = raw?.adjustedValuation || Math.round(refinedBaseline);
  const lower = raw?.valuationLow || Math.round(midpoint * (1 - effectiveSpread));
  const upper = raw?.valuationHigh || Math.round(midpoint * (1 + effectiveSpread));

  return {
    subject: {
      ...raw,
      postcode: normalisedPostcode || raw.postcode,
      beds: subjectBeds,
      baths: subjectBaths,
      valuationFeatures: features,
      condition: conditionKey,
      renovationYear: Number.isFinite(renovationYear) ? renovationYear : raw?.renovationYear || null,
      floorArea: hasFloorArea ? Number(floorAreaValue.toFixed(2)) : raw?.floorArea || null,
      floorAreaUnit,
      specialFeatureNotes
    },
    matched,
    comparables,
    valuation: {
      baseline: midpoint,
      lower,
      upper,
      midpoint,
      spread: effectiveSpread,
      confidence: Math.max(60, Math.min(90, Math.round(82 - effectiveSpread * 120))),
      adjustments: {
        features,
        condition: conditionKey,
        renovationYear: Number.isFinite(renovationYear) ? renovationYear : null,
        floorArea: hasFloorArea
          ? { value: Number(floorAreaValue.toFixed(2)), unit: floorAreaUnit }
          : null,
        multiplier: Number(totalMultiplier.toFixed(3))
      }
    }
  };
}

function SellerValuation() {
  const navigate = useNavigate();
  const [activeListing, setActiveListing] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiChatOpen, setAiChatOpen] = useState(false);

  // Load seller profile from API on mount
  useEffect(() => {
    let mounted = true;
    
    async function loadProfile() {
      try {
        setLoading(true);
        console.debug("[SellerValuation] Loading seller profile");
        const profile = await getSellerProfileAsync();
        if (mounted) {
          console.debug("[SellerValuation] Loaded seller profile", profile);
          setSellerDetails(profile);
          if (!profile) {
            console.debug("[SellerValuation] No profile found, redirecting to /seller");
            navigate("/seller", { replace: true });
          }
        }
      } catch (error) {
        console.error("Failed to load seller profile:", error);
        if (mounted) {
          console.debug("[SellerValuation] Error loading profile, redirecting to /seller");
          navigate("/seller", { replace: true });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    
    loadProfile();
    
    return () => { mounted = false; };
  }, []);

  // Use real valuation from Land Registry data
  const { valuation, comparables: realComparables, loading: valuationLoading, error: valuationError } = useSellerValuation(sellerDetails);
  
  // Fallback to legacy logic only if no API valuation
  const insights = useMemo(() => computeSellerInsights(sellerDetails), [sellerDetails]);
  const { subject, matched } = insights;

  // Use real comparables from Land Registry, fallback to legacy
  const comparables = realComparables && realComparables.length > 0 ? realComparables.map(formatComparable) : insights.comparables;

  const currentProfile = sellerDetails ?? null;
  const featureSummary =
    currentProfile?.valuationFeatures?.length
      ? currentProfile.valuationFeatures
          .map((key) => FEATURE_LABELS[key] || key)
          .join(", ")
      : null;
  const conditionLabel =
    CONDITION_LABELS[currentProfile?.condition || subject?.condition || "average"] ||
    CONDITION_LABELS.average;
  const floorAreaDisplay = subject?.floorArea
    ? `${subject.floorArea}${subject.floorAreaUnit === "sqm" ? " m²" : " sq ft"}`
    : matched?.internal_sqft
    ? `${matched.internal_sqft.toLocaleString()} sq ft`
    : matched?.internal_m2
    ? `${matched.internal_m2} m²`
    : null;

  const propertyFacts = [
    { label: "Address", value: subject?.address || "Awaiting address" },
    { label: "Postcode", value: subject?.postcode || "—" },
    { label: "Bedrooms", value: subject?.beds ? `${subject.beds}` : "—" },
    { label: "Bathrooms", value: subject?.baths ? `${subject.baths}` : "—" },
    { label: "Property type", value: matched?.type || "To be confirmed" },
    {
      label: "Floor area",
      value: floorAreaDisplay || "Add floor area to tighten the estimate"
    },
    { label: "Tenure", value: matched?.tenure || "Unknown" },
    {
      label: "Property condition",
      value: conditionLabel
    },
    {
      label: "Feature highlights",
      value: featureSummary || "Add special features to refine value"
    },
    {
      label: "EPC certificate",
      value: matched?.epc_link ? (
        <a
          href={matched.epc_link}
          target="_blank"
          rel="noreferrer"
          className="underline text-white/80 hover:text-white"
        >
          View EPC
        </a>
      ) : (
        "No EPC located"
      )
    },
    {
      label: "Seller notes",
      value: currentProfile?.specialFeatureNotes || "No additional notes yet"
    }
  ];

  const soldNotes = matched?.sold_nearby || [];
  const plan = determineSellerPlan(valuation, matched?.price);
  const isActiveSeller = currentProfile?.status === SellerStatus.ACTIVE;
  const adjustments = valuation?.adjustments || {};
  const adjustmentFeaturesSummary = adjustments.features?.length
    ? adjustments.features.map((key) => FEATURE_LABELS[key] || key).join(", ")
    : "None declared";
  const adjustmentConditionLabel =
    CONDITION_LABELS[adjustments.condition || "average"] || CONDITION_LABELS.average;
  const adjustmentFloorArea = adjustments.floorArea;
  const adjustmentRenovationYear = adjustments.renovationYear;
  const hasExistingRefinements = Boolean(
    (currentProfile?.valuationFeatures?.length ?? 0) ||
      (currentProfile?.condition && currentProfile.condition !== "average") ||
      currentProfile?.floorArea ||
      currentProfile?.specialFeatureNotes
  );
  const [refineOpen, setRefineOpen] = useState(hasExistingRefinements);
  const [refinement, setRefinement] = useState(() =>
    createRefinementState(currentProfile, subject)
  );
  const [refinementSavedAt, setRefinementSavedAt] = useState(null);

  useEffect(() => {
    if (!sellerDetails) return;
    setRefinement(createRefinementState(currentProfile, subject));
    if (hasExistingRefinements) setRefineOpen(true);
  }, [sellerDetails, subject, hasExistingRefinements]);

  if (!sellerDetails) return null;

  const toggleFeature = (key) => {
    setRefinement((prev) => {
      const exists = prev.features.includes(key);
      const next = exists ? prev.features.filter((f) => f !== key) : [...prev.features, key];
      return { ...prev, features: next };
    });
  };

  const handleRefinementChange = (field, value) => {
    setRefinement((prev) => ({ ...prev, [field]: value }));
  };

  const handleRefinementSubmit = async (event) => {
    event.preventDefault();
    const uniqueFeatures = Array.from(
      new Set(
        refinement.features.filter((key) =>
          Object.prototype.hasOwnProperty.call(FEATURE_ADJUSTMENTS, key)
        )
      )
    ).sort();
    const conditionValue = Object.prototype.hasOwnProperty.call(
      CONDITION_ADJUSTMENTS,
      refinement.condition
    )
      ? refinement.condition
      : "average";
    const parsedArea = Number.parseFloat(refinement.floorArea);
    const hasArea = Number.isFinite(parsedArea) && parsedArea > 0;
    const parsedRenovation = Number.parseInt(refinement.renovationYear, 10);
    const hasRenovation = Number.isFinite(parsedRenovation) && refinement.renovationYear.length === 4;
    const floorAreaUnit = refinement.floorAreaUnit === "sqm" ? "sqm" : "sqft";

    try {
      const updated = await updateSellerProfileAsync({
        valuationFeatures: uniqueFeatures,
        condition: conditionValue,
        renovationYear: hasRenovation ? parsedRenovation : null,
        floorArea: hasArea ? Number(parsedArea.toFixed(2)) : null,
        floorAreaUnit: hasArea ? floorAreaUnit : null,
        specialFeatureNotes: refinement.specialFeatureNotes
          ? refinement.specialFeatureNotes.trim()
          : null
      });

      setSellerDetails(updated);
      setRefinementSavedAt(Date.now());
    } catch (error) {
      console.error("Failed to update seller profile:", error);
      alert("Failed to save refinements. Please try again.");
    }
  };

  const handleRefinementReset = async () => {
    try {
      const updated = await updateSellerProfileAsync({
        valuationFeatures: [],
        condition: "average",
        renovationYear: null,
        floorArea: null,
        floorAreaUnit: "sqft",
        specialFeatureNotes: null
      });
      setSellerDetails(updated);
      setRefinement(createRefinementState(updated, subject));
      setRefinementSavedAt(Date.now());
    } catch (error) {
      console.error("Failed to reset refinements:", error);
      alert("Failed to reset refinements. Please try again.");
    }
  };

  const handleProceed = async () => {
    try {
      const updated = await updateSellerProfileAsync({
        status: SellerStatus.CHECKOUT,
        planKey: plan.key,
        valuationRange: {
          lower: valuation?.lower,
          upper: valuation?.upper,
          midpoint: valuation?.midpoint
        },
        suggestedListingId: matched?.id || null
      });
      setSellerDetails(updated);
      navigate("/seller/checkout");
    } catch (error) {
      console.error("Failed to proceed to checkout:", error);
      alert("Failed to proceed. Please try again.");
    }
  };

  return (
    <main className="min-h-screen bg-neutral-950 text-white pb-20">
      <HomeButton />
      <div className="max-w-6xl mx-auto px-6 pt-20 space-y-10">
        <button
          onClick={() => navigate("/seller")}
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition"
        >
          <span className="text-lg">←</span>
          Update your property details
        </button>

        <section className="grid gap-6 md:grid-cols-[1.1fr,1fr] items-start">
          <div className="rounded-3xl border border-white/15 bg-white/5 p-6 md:p-8 space-y-6">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-white/50">Estimated valuation</p>
              <h1 className="mt-2 text-3xl md:text-4xl font-semibold">
                {subject?.address || "Your home"}
              </h1>
              <p className="text-white/60 text-sm mt-1">
                Powered by comparable sales, local supply signals, and HOME AI heuristics.
              </p>
            </div>

            {isActiveSeller && (
              <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 text-emerald-100 p-4 text-sm">
                Your property is live with HOME AI. Head to your seller workspace to monitor activity and offers.
              </div>
            )}

            {valuation && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.28em] text-white/45">Likely range</p>
                  <div className="mt-2 text-4xl md:text-5xl font-semibold">
                    {formatAmount(valuation.lower)} – {formatAmount(valuation.upper)}
                  </div>
                  <p className="mt-2 text-sm text-white/65">
                    Median expectation {formatAmount(valuation.midpoint)} • confidence{" "}
                    {valuation.confidence}%
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                    <div>
                      <p className="text-sm font-semibold text-white">Refine valuation inputs</p>
                      <p className="text-xs text-white/60">
                        Tell us more about upgrades, size, and condition to tighten the range.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRefineOpen((v) => !v)}
                      className="rounded-xl border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/10 transition"
                    >
                      {refineOpen ? "Hide details" : "Add details"}
                    </button>
                  </div>
                  {refineOpen && (
                    <form className="px-5 pb-5 space-y-4" onSubmit={handleRefinementSubmit}>
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/50">Overall condition</p>
                        <div className="mt-2 grid sm:grid-cols-3 gap-2">
                          {CONDITION_OPTIONS.map((option) => {
                            const checked = refinement.condition === option.key;
                            return (
                              <label
                                key={option.key}
                                className={
                                  "rounded-xl border px-3 py-2 text-sm transition " +
                                  (checked
                                    ? "border-emerald-400/60 bg-emerald-500/10 text-white"
                                    : "border-white/15 bg-black/30 text-white/70 hover:border-white/30")
                                }
                              >
                                <input
                                  type="radio"
                                  name="valuation-condition"
                                  value={option.key}
                                  checked={checked}
                                  onChange={() => handleRefinementChange("condition", option.key)}
                                  className="hidden"
                                />
                                <span className="font-medium">{option.label}</span>
                                <span className="block text-xs text-white/60 mt-1">
                                  {option.description}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/50">Special features</p>
                        <div className="mt-2 grid sm:grid-cols-2 gap-2">
                          {FEATURE_OPTIONS.map((option) => {
                            const checked = refinement.features.includes(option.key);
                            return (
                              <label
                                key={option.key}
                                className={
                                  "flex gap-2 rounded-xl border px-3 py-2 text-sm transition " +
                                  (checked
                                    ? "border-emerald-400/60 bg-emerald-500/10 text-white"
                                    : "border-white/15 bg-black/30 text-white/70 hover:border-white/30")
                                }
                              >
                                <input
                                  type="checkbox"
                                  className="mt-1"
                                  checked={checked}
                                  onChange={() => toggleFeature(option.key)}
                                />
                                <span>
                                  <span className="font-medium">{option.label}</span>
                                  <span className="block text-xs text-white/60">
                                    {option.description}
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs uppercase tracking-[0.3em] text-white/50">
                            Internal floor area
                          </label>
                          <div className="mt-1 flex gap-2">
                            <input
                              type="number"
                              min={0}
                              step="0.1"
                              className="w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                              placeholder="e.g. 1450"
                              value={refinement.floorArea}
                              onChange={(e) => handleRefinementChange("floorArea", e.target.value)}
                            />
                            <select
                              className="rounded-xl border border-white/15 bg-black/40 px-3 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                              value={refinement.floorAreaUnit}
                              onChange={(e) => handleRefinementChange("floorAreaUnit", e.target.value)}
                            >
                              <option value="sqft">sq ft</option>
                              <option value="sqm">m²</option>
                            </select>
                          </div>
                          <p className="mt-1 text-xs text-white/50">
                            Enter the measured internal area for the best accuracy.
                          </p>
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-[0.3em] text-white/50">
                            Last major renovation
                          </label>
                          <input
                            type="number"
                            className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                            placeholder="2022"
                            value={refinement.renovationYear}
                            onChange={(e) => handleRefinementChange("renovationYear", e.target.value)}
                          />
                          <p className="mt-1 text-xs text-white/50">
                            Year of the most recent substantial refurbishment (leave blank if unsure).
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs uppercase tracking-[0.3em] text-white/50">
                          Additional notes
                        </label>
                        <textarea
                          className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                          rows={3}
                          placeholder="South-facing garden, bespoke joinery, underfloor heating, etc."
                          value={refinement.specialFeatureNotes}
                          onChange={(e) =>
                            handleRefinementChange("specialFeatureNotes", e.target.value)
                          }
                        />
                      </div>

                      <div className="flex flex-wrap justify-end gap-3">
                        <button
                          type="submit"
                          className="rounded-xl bg-white text-black px-5 h-11 text-sm font-medium hover:bg-white/90"
                        >
                          Save details
                        </button>
                        <button
                          type="button"
                          onClick={handleRefinementReset}
                          className="rounded-xl border border-white/20 bg-white/5 px-5 h-11 text-sm font-medium text-white hover:bg-white/10 transition"
                        >
                          Reset
                        </button>
                      </div>
                      <p className="text-xs text-white/50">
                        Saved inputs adjust HOME AI heuristics. Connect a valuation partner to replace
                        this with live market data.
                      </p>
                      {refinementSavedAt && (
                        <p className="text-xs text-emerald-300">
                          Saved {new Date(refinementSavedAt).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      )}
                    </form>
                  )}
                </div>

                <div className="rounded-2xl bg-black/40 border border-white/10 p-4 flex flex-wrap gap-4 text-sm text-white/70">
                  <div>
                    <span className="block text-white/40 text-xs uppercase tracking-wide">Comparables analysed</span>
                    <span className="text-white text-base font-medium">{comparables.length || "—"}</span>
                  </div>
                  <div>
                    <span className="block text-white/40 text-xs uppercase tracking-wide">Bedrooms factored</span>
                    <span className="text-white text-base font-medium">{subject?.beds ?? "—"}</span>
                  </div>
                  <div>
                    <span className="block text-white/40 text-xs uppercase tracking-wide">Last updated</span>
                    <span className="text-white text-base font-medium">
                      {subject?._updatedAt
                        ? new Date(subject._updatedAt).toLocaleString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            day: "numeric",
                            month: "short"
                          })
                        : "Just now"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleProceed}
                className="rounded-xl bg-white text-black px-5 h-11 text-sm font-medium hover:bg-white/90"
              >
                I'm ready to list with HOME AI
              </button>
              <button
                onClick={() => navigate("/seller/how-home-ai-works")}
                className="rounded-xl border border-white/20 bg-white/5 px-5 h-11 text-sm font-medium text-white hover:bg-white/10 transition"
              >
                How HOME AI works
              </button>
              <TalkToHomeAIButton
                onClick={() => setAiChatOpen(true)}
                className="h-11 px-5"
              />
              <button
                onClick={() => navigate("/seller/consultation")}
                className="rounded-xl border border-white/20 bg-white/5 px-5 h-11 text-sm font-medium text-white hover:bg-white/10 transition"
              >
                Talk to an agent
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="overflow-hidden rounded-3xl border border-white/15 bg-black/30">
              <PropertyMap
                latitude={subject?.lat}
                longitude={subject?.lng}
                address={subject?.address}
                postcode={subject?.postcode}
                zoom={16}
              />
            </div>
            <div className="rounded-3xl border border-white/15 bg-white/5 p-5 space-y-4">
              <h3 className="text-lg font-semibold">Property snapshot</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-white/70">
                {propertyFacts.map((fact) => (
                  <div key={fact.label} className="space-y-1">
                    <p className="text-white/60 text-[11px] font-semibold uppercase tracking-[0.2em]">
                      {fact.label}
                    </p>
                    <div className="text-white">{fact.value}</div>
                  </div>
                ))}
              </div>
              {soldNotes.length > 0 && (
                <div className="pt-3 border-t border-white/10">
                  <p className="text-xs uppercase tracking-wide text-white/45">Notable local completions</p>
                  <ul className="mt-2 space-y-1 text-sm text-white/70 list-disc list-inside">
                    {soldNotes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl font-semibold">Comparable sales nearby</h2>
              <p className="text-sm text-white/60">
                Recent transactions that informed the valuation. Tap to open full details.
              </p>
            </div>
          </div>
          <div className="mt-5 flex gap-4 overflow-x-auto pb-4 -mx-6 px-6 md:mx-0 md:px-0">
            {comparables.map((comp) => (
              <button
                key={comp.id}
                onClick={() => setActiveListing(comp)}
                className="min-w-[260px] text-left"
              >
                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden hover:border-white/30 transition">
                  <div className="h-40 relative">
                    <img
                      src={comp.hero || comp.photos?.[0]}
                      alt={comp.title}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-xs">
                      {comp.postcode}
                    </div>
                  </div>
                  <div className="p-4 space-y-2">
                    <h3 className="text-base font-medium line-clamp-2 min-h-[44px]">{comp.title}</h3>
                    <div className="text-sm text-white/70">
                      {comp.beds} beds • {comp.baths ?? "—"} baths
                    </div>
                    <div className="text-lg font-semibold">{formatAmount(comp.price)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-black/40 p-4 text-sm text-white/80 space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-white/50">Recommended package</p>
                <p className="mt-1 text-lg font-semibold text-white">{plan.label}</p>
                <p className="text-sm text-white/70 max-w-md">{plan.description}</p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs uppercase tracking-[0.28em] text-white/45">Commercials</p>
                <p className="mt-1 text-lg font-semibold text-white">
                  £{plan.upfrontFee.toLocaleString()} upfront
                </p>
                <p className="text-sm text-white/70">+ {plan.successFeePercent}% on exchange</p>
              </div>
            </div>
            <ul className="grid gap-2 text-sm sm:grid-cols-2">
              {plan.benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-2 text-white/80">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-white/70">
              <p className="font-semibold text-white/80">Current assumptions</p>
              <ul className="mt-2 space-y-1">
                <li>Condition: {adjustmentConditionLabel}</li>
                <li>Features: {adjustmentFeaturesSummary}</li>
                {adjustmentFloorArea ? (
                  <li>
                    Floor area: {adjustmentFloorArea.value}
                    {adjustmentFloorArea.unit === "sqm" ? " m²" : " sq ft"}
                  </li>
                ) : null}
                {adjustmentRenovationYear ? (
                  <li>Renovation year: {adjustmentRenovationYear}</li>
                ) : null}
              </ul>
            </div>
          </div>
        </section>

        <section className="mt-4 rounded-3xl border border-white/10 bg-gradient-to-r from-white/10 via-white/5 to-white/10 p-8 md:p-10">
          <div className="md:flex md:items-center md:justify-between gap-6 space-y-6 md:space-y-0">
            <div className="space-y-2 max-w-xl">
              <p className="text-xs uppercase tracking-[0.3em] text-white/45">Next steps</p>
              <h3 className="text-3xl font-semibold">Sell my home with HOME AI</h3>
              <p className="text-white/70 text-sm">
                Book a valuation visit, sync your data with the HOME CRM, and unlock automation for viewings,
                offers, and legal progression.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/seller/consultation")}
                className="rounded-xl bg-white text-black px-5 h-12 text-sm font-medium hover:bg-white/90"
              >
                Book an online consultation
              </button>
              <TalkToHomeAIButton
                onClick={() => setAiChatOpen(true)}
              />
            </div>
          </div>
        </section>
      </div>

      <ListingModal
        open={!!activeListing}
        onClose={() => setActiveListing(null)}
        listing={activeListing}
        onBook={() => setShowSignIn(true)}
        currency="GBP"
      />
      <AIChatModal
        open={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        contextTitle="your property valuation"
      />

      <GoogleSignIn
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        onSignedIn={(session) => {
          if (session?.roles?.length) {
            window.location.href = "/crm";
          } else {
            setShowSignIn(false);
          }
        }}
      />
    </main>
  );
}

function SellerHowItWorks() {
  const navigate = useNavigate();
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  
  useEffect(() => {
    async function loadProfile() {
      try {
        const profile = await getSellerProfileAsync();
        setSellerProfile(profile);
      } catch (error) {
        console.error("Failed to load seller profile:", error);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, []);
  
  const insights = useMemo(() => computeSellerInsights(sellerProfile), [sellerProfile]);
  const plan = determineSellerPlan(insights.valuation, insights.matched?.price);

  const steps = [
    {
      title: "1. Remote valuation",
      detail:
        "HOME AI analyses sold comparables, supply, and buyer demand to give you a guided range. Agents sanity-check before launch."
    },
    {
      title: "2. Compliance & onboarding",
      detail:
        "Upload proof of ownership and AML ID. Sign the agency agreement digitally and select your marketing package."
    },
    {
      title: "3. Launch & marketing",
      detail:
        "Our media partners capture photography, floorplans, and video. Listings syndicate to HOME AI, Rightmove, Zoopla, and partner networks."
    },
    {
      title: "4. AI + agent collaboration",
      detail:
        "Buyers book via the portal while AI surfaces hot leads, nudges feedback, and manages documents. Your agent stays in control."
    },
    {
      title: "5. Offers & progression",
      detail:
        "Offers are compared in one workspace with proof-of-funds, timelines, and solicitor details. AI remembers tasks; your agent negotiates."
    },
    {
      title: "6. Exchange & completion",
      detail:
        "We track conveyancing milestones, flag risks, and hand over to completion with weekly accountability updates."
    }
  ];

  const inclusions = plan.key === "premier"
    ? [
        "Full drone cinematography",
        "Overseas buyer syndication",
        "Premium portal positioning",
        "Weekly strategy reviews with lead partner"
      ]
    : [
        "Professional photography & floorplans",
        "Rightmove, Zoopla & HOME AI syndication",
        "Portable virtual viewings",
        "Dedicated sales progression assistant"
      ];

  return (
    <main className="min-h-screen bg-neutral-950 text-white pb-16">
      <HomeButton />
      <div className="max-w-5xl mx-auto px-6 pt-16 space-y-10">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-white/50">Seller playbook</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold">How HOME AI sells your property</h1>
          <p className="mt-3 text-white/70 text-sm md:text-base max-w-3xl">
            Human expertise with machine intelligence: from valuation to exchange, every step blends personal agency oversight with AI-driven execution.
          </p>
        </div>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Your recommended package</p>
              <h2 className="mt-1 text-2xl font-semibold">{plan.label}</h2>
              <p className="text-sm text-white/70 max-w-xl">{plan.description}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Fees</p>
              <p className="mt-1 text-xl font-semibold text-white">
                £{plan.upfrontFee.toLocaleString()} upfront
              </p>
              <p className="text-sm text-white/70">+ {plan.successFeePercent}% on exchange</p>
            </div>
          </div>
          <ul className="grid gap-3 text-sm sm:grid-cols-2">
            {inclusions.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                <span className="text-white/80">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          {steps.map((step) => (
            <div key={step.title} className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-2 text-sm text-white/70">{step.detail}</p>
            </div>
          ))}
        </section>

        <section className="rounded-3xl border border-white/10 bg-gradient-to-r from-white/10 via-white/5 to-white/10 p-8 space-y-4">
          <h2 className="text-2xl font-semibold">What you can expect</h2>
          <ul className="grid gap-3 md:grid-cols-2 text-sm text-white/75">
            <li className="rounded-2xl border border-white/10 bg-black/30 p-4">
              <p className="text-sm font-medium text-white">AI-assisted monitoring</p>
              <p className="mt-1 text-xs text-white/60">
                Real-time alerts on buyer activity, document requests, and conveyancing milestones.
              </p>
            </li>
            <li className="rounded-2xl border border-white/10 bg-black/30 p-4">
              <p className="text-sm font-medium text-white">Agent expertise</p>
              <p className="mt-1 text-xs text-white/60">
                Senior agents strategise pricing, negotiate offers, and manage stakeholder relationships.
              </p>
            </li>
            <li className="rounded-2xl border border-white/10 bg-black/30 p-4">
              <p className="text-sm font-medium text-white">Transparent reporting</p>
              <p className="mt-1 text-xs text-white/60">
                Dashboards synced with your seller workspace and weekly AI summaries to your inbox.
              </p>
            </li>
            <li className="rounded-2xl border border-white/10 bg-black/30 p-4">
              <p className="text-sm font-medium text-white">Concierge-level marketing</p>
              <p className="mt-1 text-xs text-white/60">
                Media, styling, signage, and portal distribution managed end-to-end by our in-house studio.
              </p>
            </li>
          </ul>
        </section>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/seller/checkout")}
            className="rounded-xl bg-white text-black px-5 h-12 text-sm font-medium hover:bg-white/90"
          >
            Proceed to onboarding
          </button>
          <button
            onClick={() => navigate("/seller/valuation")}
            className="rounded-xl border border-white/20 bg-white/5 px-5 h-12 text-sm font-medium text-white hover:bg-white/10 transition"
          >
            Back to valuation
          </button>
          <TalkToHomeAIButton
            onClick={() => setAiChatOpen(true)}
            className="h-12 px-5"
          />
        </div>
      </div>
      <AIChatModal
        open={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        contextTitle="selling your property"
      />
    </main>
  );
}

function SellerCheckout() {
  const navigate = useNavigate();
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentCreated, setPaymentIntentCreated] = useState(false);
  const insights = useMemo(() => computeSellerInsights(sellerProfile), [sellerProfile]);
  const plan = determineSellerPlan(insights.valuation, insights.matched?.price);

  // Load seller profile from API
  useEffect(() => {
    getSellerProfileAsync()
      .then(profile => {
        setSellerProfile(profile);
        setLoading(false);
        if (!profile) {
          navigate("/seller", { replace: true });
        }
      })
      .catch(error => {
        console.error("Failed to load seller profile:", error);
        setLoading(false);
        navigate("/seller", { replace: true });
      });
  }, [navigate]);

  // Create payment intent when profile loads
  useEffect(() => {
    if (!sellerProfile?.id || paymentIntentCreated) return;

    // Check if payment already completed
    if (sellerProfile.paymentStatus === 'SUCCEEDED') {
      navigate("/seller/checkout/success");
      return;
    }

    createPaymentIntent(sellerProfile.id)
      .then(response => {
        setClientSecret(response.clientSecret);
        setPaymentIntentCreated(true);
        
        // Initialize Stripe with publishable key
        if (response.publishableKey) {
          setStripePromise(loadStripe(response.publishableKey));
        }
      })
      .catch(error => {
        console.error("Failed to create payment intent:", error);
        alert("Failed to initialize payment. Please try again.");
      });
  }, [sellerProfile, paymentIntentCreated, navigate]);

  if (loading || !sellerProfile) return null;

  if (!clientSecret || !stripePromise) {
    return (
      <main className="min-h-screen bg-neutral-950 text-white pb-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70">Initializing secure payment...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-neutral-950 text-white pb-16">
      <HomeButton />
      <div className="max-w-5xl mx-auto px-6 pt-16">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-white/50">Seller onboarding</p>
            <h1 className="mt-2 text-3xl font-semibold">Secure your HOME AI marketing package</h1>
            <p className="mt-3 text-sm text-white/70 max-w-xl">
              Complete payment, e-sign the agency agreement, and upload AML documents to progress to launch.
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50">Summary</p>
            <p className="mt-1 font-medium text-white">{plan.label}</p>
            <p>£{plan.upfrontFee.toLocaleString()} upfront • {plan.successFeePercent}% on exchange</p>
            <p className="mt-1 text-xs text-white/60">
              Listing: {insights.matched?.title || sellerProfile?.address || "TBC"}
            </p>
          </div>
        </div>

        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <SellerCheckoutForm 
            sellerProfile={sellerProfile}
            plan={plan}
            insights={insights}
            navigate={navigate}
            clientSecret={clientSecret}
          />
        </Elements>

        <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-6 text-xs text-white/60 space-y-2">
          <p>Payments are processed securely via Stripe and a VAT receipt will be emailed immediately.</p>
          <p>Documents are stored with encrypted access. Your agent will review uploads within 1 business hour.</p>
        </div>
      </div>
    </main>
  );
}

// Separate form component to use Stripe hooks
function SellerCheckoutForm({ sellerProfile, plan, insights, navigate, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();

  const [form, setForm] = useState({
    ownershipDoc: null,
    idDoc: null,
    confirmMatch: false,
    confirmAML: false,
    agreeTerms: false,
    signContract: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onFileChange = (key, file) => {
    setForm((prev) => ({ ...prev, [key]: file }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.ownershipDoc) e.ownershipDoc = "Upload proof of ownership (Land Registry).";
    if (!form.idDoc) e.idDoc = "Upload photo ID for AML.";
    if (!form.confirmMatch) e.confirmMatch = "Confirm that ownership and ID details align.";
    if (!form.confirmAML) e.confirmAML = "Confirm AML documents are provided.";
    if (!form.agreeTerms) e.agreeTerms = "You must agree to the agency terms.";
    if (!form.signContract) e.signContract = "Electronically sign the agency agreement.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate() || !stripe || !elements) return;
    
    setSubmitting(true);
    
    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setErrors({ submit: stripeError.message });
        setSubmitting(false);
        return;
      }

      // Payment succeeded - update onboarding record
      const updated = await updateSellerProfileAsync({
        status: SellerStatus.ACTIVE,
        planKey: plan.key,
        onboardedAt: Date.now(),
        activeListingId: insights.matched?.id || null,
        contractSignedAt: Date.now(),
      });
      
      navigate("/seller/checkout/success");
    } catch (error) {
      console.error("Failed to complete checkout:", error);
      setErrors({ submit: "Failed to process checkout. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
      <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-white">Payment details</h2>
        <div className="rounded-xl border border-white/10 bg-black/40 p-4">
          <CardElement 
            options={{
              style: {
                base: {
                  color: '#ffffff',
                  fontSize: '16px',
                  '::placeholder': {
                    color: 'rgba(255,255,255,0.5)',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
            }}
          />
        </div>
        <p className="text-xs text-white/60">
          Your payment is securely processed by Stripe. We never store your card details.
        </p>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-white">Compliance documents</h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Proof of ownership</label>
            <input
              type="file"
              className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
              onChange={(e) => onFileChange("ownershipDoc", e.target.files?.[0] || null)}
              accept=".pdf,.jpg,.png"
            />
            <p className="mt-1 text-xs text-white/60">Land Registry title deed or completion statement.</p>
            {errors.ownershipDoc && <p className="mt-1 text-xs text-red-400">{errors.ownershipDoc}</p>}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Photo ID (AML)</label>
            <input
              type="file"
              className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
              onChange={(e) => onFileChange("idDoc", e.target.files?.[0] || null)}
              accept=".pdf,.jpg,.png"
            />
            <p className="mt-1 text-xs text-white/60">Passport or driving licence for each beneficiary.</p>
            {errors.idDoc && <p className="mt-1 text-xs text-red-400">{errors.idDoc}</p>}
          </div>
        </div>
        <div className="space-y-2 text-sm text-white/75">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={form.confirmMatch}
              onChange={(e) => setForm((prev) => ({ ...prev, confirmMatch: e.target.checked }))}
            />
            <span>
              I confirm the ownership document matches the ID information provided and authorise HOME AI to run AML checks.
            </span>
          </label>
          {errors.confirmMatch && <p className="text-xs text-red-400 pl-6">{errors.confirmMatch}</p>}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={form.confirmAML}
              onChange={(e) => setForm((prev) => ({ ...prev, confirmAML: e.target.checked }))}
            />
            <span>I have uploaded ID for all legal owners and confirm beneficial ownership.</span>
          </label>
          {errors.confirmAML && <p className="text-xs text-red-400 pl-6">{errors.confirmAML}</p>}
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-white">Agency agreement</h2>
        <div className="space-y-2 text-sm text-white/75">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={form.agreeTerms}
              onChange={(e) => setForm((prev) => ({ ...prev, agreeTerms: e.target.checked }))}
            />
            <span>
              I have read and agree to the HOME AI agency terms including the {plan.successFeePercent}% success fee on exchange of contracts.
            </span>
          </label>
          {errors.agreeTerms && <p className="text-xs text-red-400 pl-6">{errors.agreeTerms}</p>}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={form.signContract}
              onChange={(e) => setForm((prev) => ({ ...prev, signContract: e.target.checked }))}
            />
            <span>
              I electronically sign the sole-agency agreement and authorise HOME AI to commence marketing immediately.
            </span>
          </label>
          {errors.signContract && <p className="text-xs text-red-400 pl-6">{errors.signContract}</p>}
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-3">
        <h2 className="text-xl font-semibold text-white">Order summary</h2>
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-white/80">
          <span>Today&rsquo;s onboarding fee</span>
          <span className="text-2xl font-semibold text-white">
            £{plan.upfrontFee.toLocaleString()}
          </span>
        </div>
        <div className="text-xs text-white/60">
          Success fee of {plan.successFeePercent}% becomes payable on exchange of contracts and is deducted on completion.
        </div>
      </section>

      <div className="flex flex-wrap gap-3 justify-end">
        {errors.submit && (
          <p className="w-full text-right text-sm text-red-400">{errors.submit}</p>
        )}
        <button
          type="submit"
          disabled={submitting || !stripe}
          className="rounded-xl bg-white text-black px-6 h-12 text-sm font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Processing payment..." : `Pay £${plan.upfrontFee.toLocaleString()} & sign agreement`}
        </button>
        <button
          type="button"
          onClick={() => navigate("/seller/consultation")}
          className="rounded-xl border border-white/20 bg-white/5 px-6 h-12 text-sm font-medium text-white hover:bg-white/10 transition"
        >
          Need help? Talk to an agent
        </button>
      </div>
    </form>
  );
}

function SellerCheckoutSuccess() {
  const navigate = useNavigate();
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const insights = useMemo(() => computeSellerInsights(sellerProfile), [sellerProfile]);

  useEffect(() => {
    getSellerProfileAsync()
      .then(profile => {
        setSellerProfile(profile);
        setLoading(false);
      })
      .catch(error => {
        console.error("Failed to load seller profile:", error);
        setLoading(false);
      });
  }, []);

  if (loading) return null;
  if (!sellerProfile) return <Navigate to="/seller" replace />;

  const plan = determineSellerPlan(insights.valuation, insights.matched?.price);

  return (
    <main className="min-h-screen bg-neutral-950 text-white pb-16">
      <HomeButton />
      <div className="max-w-4xl mx-auto px-6 pt-20 space-y-8 text-center">
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.35em] text-white/50">Demo mode</p>
          <h1 className="text-3xl md:text-4xl font-semibold">Welcome to the HOME AI seller network</h1>
          <p className="text-sm text-white/70">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/40 mb-2">
              ⚠ Demo only - No payment processed
            </span>
            <br />
            This is a demonstration of the seller onboarding flow. In production, your payment would be processed and our team would be notified to begin your marketing campaign.
          </p>
        </div>

        <div className="rounded-3xl border border-white/20 bg-white/5 p-6 text-sm text-white/70 space-y-2">
          <p className="text-lg font-semibold text-white">Demo package: {plan.label}</p>
          <p className="text-white/50">Reference: {sellerProfile.paymentReference || "DEMO-MODE"}</p>
          <p>
            Would be: £{plan.upfrontFee.toLocaleString()} upfront • {plan.successFeePercent}% on exchange
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3 text-sm text-white/70">
          {[
            "In production: Media partner contacts you within 24 hours to arrange photography.",
            "Demo workspace shows marketing, viewings, offers with sample data.",
            "Real flow: Upload property documents (EPC, warranties) for compliance."
          ].map((item, idx) => (
            <div key={idx} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              {item}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => navigate("/crm")}
            className="rounded-xl bg-white text-black px-6 h-12 text-sm font-medium hover:bg-white/90"
          >
            Open seller workspace
          </button>
          <button
            onClick={() => navigate("/seller/how-home-ai-works")}
            className="rounded-xl border border-white/20 bg-white/5 px-6 h-12 text-sm font-medium text-white hover:bg-white/10 transition"
          >
            Review HOME AI playbook
          </button>
          <button
            onClick={() => navigate("/seller/consultation")}
            className="rounded-xl border border-white/20 bg-white/5 px-6 h-12 text-sm font-medium text-white hover:bg-white/10 transition"
          >
            Schedule launch call
          </button>
        </div>
      </div>
    </main>
  );
}

function SellerConsultation() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    notes: ""
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Enter your name.";
    if (!isEmail(form.email)) e.email = "Enter a valid email.";
    if (!form.date) e.date = "Pick a preferred date.";
    if (!form.time) e.time = "Select a time slot.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
  };

  return (
    <main className="min-h-screen bg-neutral-950 text-white pb-16">
      <HomeButton />
      <div className="max-w-3xl mx-auto px-6 pt-16 space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-white/50">Developer & seller support</p>
          <h1 className="mt-2 text-3xl font-semibold">Book a strategy call with a HOME agent</h1>
          <p className="mt-2 text-sm text-white/70">
            Choose a slot and we&rsquo;ll confirm a 30-minute video consultation to review your sale and marketing plan.
          </p>
        </div>

        {submitted ? (
          <div className="rounded-3xl border border-emerald-400/30 bg-emerald-500/10 p-6 text-emerald-100 text-sm">
            <p className="text-lg font-semibold text-white">Consultation requested</p>
            <p className="mt-2">
              We&rsquo;ll send a calendar invite to {form.email} with the meeting link. Need to update something?{" "}
              <button
                className="underline decoration-emerald-200"
                onClick={() => setSubmitted(false)}
              >
                Edit details
              </button>
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Name</label>
                <input
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Email</label>
                <input
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
                {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Phone (optional)</label>
                <input
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Preferred date</label>
                <input
                  type="date"
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                />
                {errors.date && <p className="mt-1 text-xs text-red-400">{errors.date}</p>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Time (UK)</label>
                <select
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                  value={form.time}
                  onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))}
                >
                  <option value="">Select slot</option>
                  <option>09:00</option>
                  <option>11:30</option>
                  <option>14:00</option>
                  <option>17:00</option>
                </select>
                {errors.time && <p className="mt-1 text-xs text-red-400">{errors.time}</p>}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.3em] text-white/50">Agenda / notes</label>
              <textarea
                className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none"
                rows={4}
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Tell us about your development, timelines, or anything you want to cover."
              />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="submit"
                className="rounded-xl bg-white text-black px-6 h-12 text-sm font-medium hover:bg-white/90"
              >
                Request consultation
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-xl border border-white/20 bg-white/5 px-6 h-12 text-sm font-medium text-white hover:bg-white/10 transition"
              >
                Back
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

/* ----------------------- Media carousel (swipe + arrows, peek) ----------------------- */
function useSwipe(onLeft, onRight) {
  const startX = useRef(0);
  const endX = useRef(0);
  const onTouchStart = (e) => {
    startX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    endX.current = e.changedTouches[0].clientX;
    const dx = endX.current - startX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) onLeft?.();
      else onRight?.();
    }
  };
  return { onTouchStart, onTouchEnd };
}

function MediaCarousel({ photos = [], video, onOpenFloor, onOpenMap, onOpenGallery }) {
  const slides = [
    ...(video ? [{ kind: "video", src: video }] : []),
    ...photos.map((p) => ({ kind: "img", src: p }))
  ];
  const [i, setI] = useState(0);
  if (!slides.length) return null;

  const next = () => setI((n) => (n + 1) % slides.length);
  const prev = () => setI((n) => (n - 1 + slides.length) % slides.length);
  const swipe = useSwipe(next, prev);

  const peekIdx = (i + 1) % slides.length;
  const peekSrc = slides[peekIdx]?.src;

  return (
    <div className="relative bg-white rounded-2xl overflow-hidden">
      <div className="relative h-[48vw] max-h-[460px] w-full" {...swipe}>
        {slides[i].kind === "video" ? (
          <video src={slides[i].src} controls className="h-full w-full object-cover" />
        ) : (
          <img src={slides[i].src} className="h-full w-full object-cover" alt="" />
        )}

        {peekSrc && (
          <div className="hidden sm:block absolute right-0 top-0 h-full w-20 opacity-70 pointer-events-none">
            <img src={peekSrc} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-white/80 to-transparent" />
          </div>
        )}

        <button
          onClick={prev}
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-black/80"
          aria-label="Previous media"
        >
          ‹
        </button>
        <button
          onClick={next}
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-black/80"
          aria-label="Next media"
        >
          ›
        </button>

        <div className="absolute bottom-3 left-3 flex gap-2">
          <button onClick={onOpenGallery} className="px-2 py-1 text-xs rounded bg-white/90 hover:bg-white">
            Gallery
          </button>
          <button onClick={onOpenFloor} className="px-2 py-1 text-xs rounded bg-white/90 hover:bg-white">
            Floorplans
          </button>
          <button onClick={onOpenMap} className="px-2 py-1 text-xs rounded bg-white/90 hover:bg-white">
            Map
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferModal({ open, onClose, listing }) {
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    amount: "",
    mortgage: "",
    chain: "",
    msg: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const session = getSession();
    if (!session?.email) {
      setError("Please sign in to submit an offer.");
      return;
    }
    if (!f.amount) {
      setError("Please enter your offer amount.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await createOffer({
        listingId: listing?.id,
        amount: parseInt(f.amount),
        conditions: f.msg || undefined,
        chainDetails: {
          mortgage: f.mortgage === "yes",
          chainFree: f.chain === "yes"
        }
      });
      
      setTrackingId(response?.id || "Pending");
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to submit offer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Submit an offer</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800">
            ✕
          </button>
        </div>
        {!submitted ? (
          <form onSubmit={submit} className="mt-3 grid gap-3">
            <div className="text-sm text-neutral-600">
              for <b>{listing?.title}</b>
            </div>
            <input
              className="border border-neutral-300 rounded-xl px-3 py-2"
              placeholder="Your name"
              value={f.name}
              onChange={(e) => update("name", e.target.value)}
            />
            <input
              className="border border-neutral-300 rounded-xl px-3 py-2"
              placeholder="Email"
              value={f.email}
              onChange={(e) => update("email", e.target.value)}
            />
            <input
              className="border border-neutral-300 rounded-xl px-3 py-2"
              placeholder="Phone"
              value={f.phone}
              onChange={(e) => update("phone", e.target.value)}
            />
            <input
              className="border border-neutral-300 rounded-xl px-3 py-2"
              placeholder="Offer amount (£)"
              type="number"
              value={f.amount}
              onChange={(e) => update("amount", e.target.value)}
            />
            <div className="text-sm">
              <label className="block mb-1 text-neutral-700">Do you have a mortgage in principle?</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2">
                  <input type="radio" name="mortgage" value="yes" onChange={(e) => update("mortgage", e.target.value)} />
                  Yes
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="mortgage" value="no" onChange={(e) => update("mortgage", e.target.value)} />
                  No
                </label>
              </div>
            </div>
            <div className="text-sm">
              <label className="block mb-1 text-neutral-700">Are you chain-free?</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2">
                  <input type="radio" name="chain" value="yes" onChange={(e) => update("chain", e.target.value)} />
                  Yes
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="chain" value="no" onChange={(e) => update("chain", e.target.value)} />
                  No
                </label>
              </div>
            </div>
            <textarea
              className="border border-neutral-300 rounded-xl px-3 py-2"
              rows={3}
              placeholder="Additional details or message"
              value={f.msg}
              onChange={(e) => update("msg", e.target.value)}
            />
            <div className="flex gap-2">
              <button 
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-2xl bg-black text-white hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Submitting..." : "Submit offer"}
              </button>
            </div>
            {error && (
              <div className="rounded-xl border border-red-300 bg-red-50 text-red-900 p-3 text-sm">
                {error}
              </div>
            )}
          </form>
        ) : (
          <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-900 p-4">
            <div className="font-medium mb-2">✓ Offer submitted successfully</div>
            <div className="text-sm space-y-1">
              <p>Your offer has been sent to the seller's agent for review.</p>
              <p className="font-medium">Tracking ID: {trackingId}</p>
              <p className="text-xs text-emerald-600">Save this for reference when contacting support or tracking your offer status.</p>
              <p className="text-emerald-700 mt-2">You'll receive email updates when the seller views, accepts, declines, or counter-offers.</p>
            </div>
            <button
              onClick={() => window.location.href = "/buyer/my-offers"}
              className="mt-4 w-full px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 font-medium text-sm"
            >
              View All My Offers
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------- Listing card ----------------------- */
function ListingCard({ l, onOpen, onBook, className = "", currency = "GBP" }) {
  const price = displayPrice(l, currency);
  return (
    <article
      className={
        "w-full rounded-2xl border border-neutral-200 overflow-hidden bg-white hover:shadow-sm flex flex-col " +
        className
      }
      style={{ minHeight: 380 }}
    >
      <div className="relative h-40 cursor-pointer" onClick={() => onOpen(l)}>
        <img src={l.hero || l.photos?.[0]} alt={l.title} className="w-full h-full object-cover" />
        {l.featured && (
          <span className="absolute top-2 left-2 rounded bg-black/70 text-white text-xs px-2 py-1">
            Featured
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-base font-medium line-clamp-2 min-h-[44px]">{l.title}</h3>
        <div className="text-neutral-600 text-sm">
          {l.area} • {l.postcode}
        </div>

        <div className="mt-1 font-semibold">
          <span>{price.text}</span>
        </div>

        <div className="text-neutral-600 text-sm flex items-center gap-4">
          <span className="inline-flex items-center gap-1">
            <Icon path={IBed} />
            {l.beds}
          </span>
          <span className="inline-flex items-center gap-1">
            <Icon path={IBath} />
            {l.baths}
          </span>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2">
          <button
            onClick={() => onOpen(l)}
            className="border border-neutral-300 rounded-xl h-9 px-3 text-sm whitespace-nowrap hover:bg-neutral-100"
          >
            View details
          </button>
          <button
            onClick={() => onBook(l)}
            className="bg-black text-white rounded-xl h-9 px-3 text-sm whitespace-nowrap hover:bg-black/90"
          >
            Book viewing
          </button>
        </div>
      </div>
    </article>
  );
}

function FavButton({ listing }) {
  const [saved, setSaved] = useState(!!loadJSON(KEYS.FAVS, {})[listing.id]);
  const toggle = () => {
    const favs = loadJSON(KEYS.FAVS, {});
    if (favs[listing.id]) {
      delete favs[listing.id];
      setSaved(false);
    } else {
      favs[listing.id] = true;
      setSaved(true);
    }
    saveJSON(KEYS.FAVS, favs);
  };
  return (
    <button
      onClick={toggle}
      className={
        "px-3 py-2 rounded-xl border flex items-center gap-1 " +
        (saved ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-100")
      }
    >
      <Icon path={IHeart} /> {saved ? "Saved" : "Save"}
    </button>
  );
}
function AlertButton({ listing }) {
  const [a, setA] = useState(!!loadJSON(KEYS.ALERTS, {})[listing.id]);
  const toggle = () => {
    const alerts = loadJSON(KEYS.ALERTS, {});
    if (alerts[listing.id]) {
      delete alerts[listing.id];
      setA(false);
    } else {
      alerts[listing.id] = true;
      setA(true);
    }
    saveJSON(KEYS.ALERTS, alerts);
  };
  return (
    <button
      onClick={toggle}
      title={a ? "You'll receive emails if price drops" : "Get email alerts if price drops"}
      className={
        "px-3 py-2 rounded-xl border " +
        (a ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-100")
      }
    >
      {a ? "✓ Email alert on" : "📧 Email me if price drops"}
    </button>
  );
}
function ShareButton({ listing, currency = DEFAULT_CCY }) {
  const share = async () => {
    const price = displayPrice(listing, currency);
    const text = `${listing.title} — ${price.text}`;
    const url = window.location.origin + "/buyer/discover";
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.title, text, url });
        return;
      } catch {}
    }
    await navigator.clipboard.writeText(`${text}\n${url}`);
    // Link copied silently - clipboard API handles it
  };
  return (
    <button
      onClick={share}
      className="px-3 py-2 rounded-xl border border-neutral-300 hover:bg-neutral-100 flex items-center gap-1"
    >
      <Icon path={IShare} /> Share
    </button>
  );
}
function AskAIButton({ listing }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TalkToHomeAIButton onClick={() => setOpen(true)} />
      <AIChatModal 
        open={open} 
        onClose={() => setOpen(false)} 
        contextTitle={listing?.title || "your property search"}
      />
    </>
  );
}

/* ----------------------- Listing modal (media scrolls with page) ----------------------- */
function ListingModal({ open, onClose, listing, onBook, currency = DEFAULT_CCY }) {
  const [saved, setSaved] = useState(false);
  const [alerted, setAlerted] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);

  const galleryRef = useRef(null);
  const floorRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!open || !listing?.id) return;
    const views = loadJSON(KEYS.VIEWS, {});
    const arr = views[listing.id] || [];
    arr.push(Date.now());
    views[listing.id] = arr;
    saveJSON(KEYS.VIEWS, views);

    const favs = loadJSON(KEYS.FAVS, {});
    const alerts = loadJSON(KEYS.ALERTS, {});
    setSaved(!!favs[listing.id]);
    setAlerted(!!alerts[listing.id]);
  }, [open, listing?.id]);

  if (!open || !listing) return null;

  const price = displayPrice(listing, currency);

  const toggleFav = () => {
    const favs = loadJSON(KEYS.FAVS, {});
    if (favs[listing.id]) {
      delete favs[listing.id];
      setSaved(false);
    } else {
      favs[listing.id] = true;
      setSaved(true);
    }
    saveJSON(KEYS.FAVS, favs);
  };
  const toggleAlert = () => {
    const alerts = loadJSON(KEYS.ALERTS, {});
    if (alerts[listing.id]) {
      delete alerts[listing.id];
      setAlerted(false);
    } else {
      alerts[listing.id] = true;
      setAlerted(true);
    }
    saveJSON(KEYS.ALERTS, alerts);
  };
  const share = async () => {
    const text = `${listing.title} — ${price.text}`;
    const url = window.location.origin + "/buyer/discover";
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.title, text, url });
        return;
      } catch {}
    }
    await navigator.clipboard.writeText(`${text}\n${url}`);
    // Link copied silently
  };
  const scrollInto = (ref) => ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const internal =
    listing.internal_m2 ? `${listing.internal_m2} m²` : listing.internal_sqft ? `${listing.internal_sqft} sqft` : null;
  const external =
    listing.external_m2 ? `${listing.external_m2} m²` : listing.external_sqft ? `${listing.external_sqft} sqft` : null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl bg-neutral-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="p-4 bg-white border-b border-neutral-200">
          <h3 className="text-xl font-semibold">{listing.title}</h3>
          <div className="text-neutral-600">
            {listing.area} • {listing.postcode}
          </div>
          <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-neutral-900">
            <Icon path={IPrice} />
            <span>{price.text}</span>
          </div>
        </div>

        {/* media scrolls with content */}
        <div className="p-4">
          <MediaCarousel
            photos={listing.photos || (listing.hero ? [listing.hero] : [])}
            video={listing.video}
            onOpenGallery={() => scrollInto(galleryRef)}
            onOpenFloor={() => scrollInto(floorRef)}
            onOpenMap={() => scrollInto(mapRef)}
          />
        </div>

        {/* actions row */}
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          <button
            onClick={toggleFav}
            title={saved ? "Saved to My Homes" : "Save to My Homes"}
            className={
              "px-3 py-2 rounded-xl border flex items-center gap-1 " +
              (saved ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-100")
            }
          >
            <Icon path={IHeart} />
            <span>{saved ? "✓ Saved to My Homes" : "Save to My Homes"}</span>
          </button>
          <button
            onClick={toggleAlert}
            title={alerted ? "You'll receive emails if price drops" : "Get email alerts if price drops"}
            className={
              "px-3 py-2 rounded-xl border flex items-center gap-1 " +
              (alerted ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-100")
            }
          >
            <Icon path={IPrice} />
            <span>{alerted ? "✓ Email alert active" : "📧 Email if price drops"}</span>
          </button>
          <button onClick={share} className="px-3 py-2 rounded-xl border border-neutral-300 hover:bg-neutral-100">
            Share
          </button>
          <AskAIButton listing={listing} />
          <div className="ml-auto flex gap-2">
            <button 
              onClick={() => setOfferModalOpen(true)} 
              className="px-4 py-2 rounded-2xl border-2 border-black text-black hover:bg-black hover:text-white transition"
            >
              Make an offer
            </button>
            <button onClick={() => onBook(listing)} className="px-4 py-2 rounded-2xl bg-black text-white hover:bg-black/90">
              Book a viewing
            </button>
          </div>
        </div>

        {/* details box */}
        <div className="p-4">
          <section className="rounded-2xl border border-neutral-200 bg-white p-4">
            <h4 className="font-medium mb-3">Details</h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              {listing.type && (
                <div className="flex items-center gap-2">
                  <Icon path={<path d="M4 10h16v10H4z M8 10V6h8v4" />} /> <b>Type:</b> {listing.type}
                </div>
              )}
              {listing.tenure && (
                <div className="flex items-center gap-2">
                  <Icon path={<path d="M6 20h12M12 4v12M8 8h8" />} /> <b>Tenure:</b> {listing.tenure}
                </div>
              )}
              {listing.leaseYearsRemaining != null && <div><b>Lease remaining:</b> {listing.leaseYearsRemaining} years</div>}
              {listing.beds != null && (
                <div className="flex items-center gap-2">
                  <Icon path={IBed} /> <b>Bedrooms:</b> {listing.beds}
                </div>
              )}
              {listing.baths != null && (
                <div className="flex items-center gap-2">
                  <Icon path={IBath} /> <b>Bathrooms:</b> {listing.baths}
                </div>
              )}
              {internal && (
                <div className="flex items-center gap-2">
                  <Icon path={IArea} /> <b>Internal:</b> {internal}
                </div>
              )}
              {external && (
                <div className="flex items-center gap-2">
                  <Icon path={IArea} /> <b>External:</b> {external}
                </div>
              )}
              {listing.council_tax_band && <div><b>Council tax band:</b> {listing.council_tax_band}</div>}
              {listing.parking && <div><b>Parking:</b> {listing.parking}</div>}
              {listing.broadband_mbps && <div><b>Broadband:</b> {listing.broadband_mbps} Mbps</div>}
              {listing.garden && <div><b>Outside space:</b> {listing.garden}</div>}
              {listing.epc_link && (
                <div>
                  <a href={listing.epc_link} target="_blank" className="underline underline-offset-2">
                    View EPC
                  </a>
                </div>
              )}
              {listing.brochure && (
                <div>
                  <a href={listing.brochure} target="_blank" className="underline underline-offset-2">
                    Download brochure
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* description */}
          {listing.description && (
            <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5">
              <h4 className="font-medium mb-3">Description</h4>
              <p className="leading-relaxed">{listing.description}</p>
            </section>
          )}

          {/* features */}
          {listing.features?.length ? (
            <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5">
              <h4 className="font-medium mb-3">Key features</h4>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm list-disc pl-5">
                {listing.features.map((f, idx) => (
                  <li key={idx}>{f}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* gallery */}
          {(listing.photos?.length || listing.hero) && (
            <section ref={galleryRef} className="mt-5 rounded-2xl border border-neutral-200 bg-white p-4" id="gallery">
              <h4 className="font-medium mb-3">Gallery</h4>
              <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                {(listing.photos?.length ? listing.photos : [listing.hero]).map((p, i) => (
                  <img key={i} src={p} alt="" className="h-40 w-64 object-cover rounded-xl snap-start" />
                ))}
              </div>
            </section>
          )}

          {/* floorplans */}
          {listing.floorplans?.length ? (
            <section ref={floorRef} className="mt-5 rounded-2xl border border-neutral-200 bg-white p-4" id="floorplans">
              <h4 className="font-medium mb-3">Floorplans</h4>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {listing.floorplans.map((p, i) => (
                  <img
                    key={i}
                    src={p}
                    alt="Floorplan"
                    className="h-72 object-contain rounded-xl border border-neutral-200 bg-white"
                  />
                ))}
              </div>
            </section>
          ) : null}

          {/* nearby */}
          {(listing.nearest_stations?.length ||
            listing.nearest_schools?.length ||
            listing.sold_nearby?.length) && (
            <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5">
              <h4 className="font-medium mb-3">Nearby</h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                {listing.nearest_stations?.length ? (
                  <div>
                    <div className="font-medium mb-1">Stations</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {listing.nearest_stations.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {listing.nearest_schools?.length ? (
                  <div>
                    <div className="font-medium mb-1">Schools</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {listing.nearest_schools.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {listing.sold_nearby?.length ? (
                  <div>
                    <div className="font-medium mb-1">Sold nearby</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {listing.sold_nearby.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </section>
          )}

          {/* map */}
          {listing.lat && listing.lng ? (
            <section ref={mapRef} className="mt-5 rounded-2xl border border-neutral-200 bg-white p-4" id="map">
              <h4 className="font-medium mb-3">Map</h4>
              <div className="rounded-xl overflow-hidden border border-neutral-200 relative">
                <iframe
                  title="map"
                  width="100%"
                  height="320"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${listing.lng - 0.01}%2C${
                    listing.lat - 0.01
                  }%2C${listing.lng + 0.01}%2C${listing.lat + 0.01}&layer=mapnik&marker=${listing.lat}%2C${
                    listing.lng
                  }`}
                />
              </div>
            </section>
          ) : null}
        </div>

        <button onClick={onClose} className="absolute right-3 top-3 bg-black/70 text-white rounded-full p-2">
          ✕
        </button>
      </div>
      
      <OfferFormModal open={offerModalOpen} onClose={() => setOfferModalOpen(false)} listing={listing} />
    </div>
  );
}

/* ----------------------- Buyer (search hero) ----------------------- */
function BuyerStart() {
  const [openAI, setOpenAI] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);
  const [form, setForm] = useState({ budgetMax: "", areas: "", email: "" });
  const [errors, setErrors] = useState({});
  const [more, setMore] = useState(false);
  const [mapBrowse, setMapBrowse] = useState(false);
  const navigate = useNavigate();
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.budgetMax && !isNumber(form.budgetMax)) e.budgetMax = "Budget must be a number (e.g., 1500000).";
    if (!isEmail(form.email)) e.email = "Enter a valid email.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    saveSection("buyer", form);
    
    try {
      await saveSearch(form);
      navigate("/buyer/discover");
    } catch (error) {
      console.error("Failed to save search:", error);
      // Still navigate even if search save fails
      navigate("/buyer/discover");
    }
  };

  const handleMapConfirm = (postcodes) => {
    setForm((prev) => {
      const existing = (prev.areas || "")
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean);
      const merged = Array.from(new Set([...existing, ...postcodes]));
      return { ...prev, areas: merged.join(", ") };
    });
    setMapBrowse(false);
  };

  return (
    <main className="min-h-screen bg-neutral-50 text-neutral-900 p-6 relative">
      <HomeButton />
      <div className="absolute right-6 top-6">
        <button
          onClick={() => setOpenLogin(true)}
          className="rounded-xl px-4 py-2 border border-neutral-300 bg-white hover:bg-neutral-100"
        >
          Login
        </button>
      </div>

      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="w-full max-w-3xl mt-20 md:mt-28">
          <h2 className="text-center text-3xl font-semibold">Find your next home</h2>
          <p className="text-center text-neutral-600 mt-1">Know your requirements? Select from the following.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <input
                className="bg-white border border-neutral-200 rounded-xl px-4 py-3"
                placeholder="Budget max (£)"
                value={form.budgetMax}
                onChange={(e) => update("budgetMax", e.target.value)}
              />
              <input
                className="bg-white border border-neutral-200 rounded-xl px-4 py-3"
                placeholder="Areas (e.g., W8, SW3, Manchester)"
                value={form.areas}
                onChange={(e) => update("areas", e.target.value)}
              />
              <input
                className="bg-white border border-neutral-200 rounded-xl px-4 py-3"
                placeholder="Email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
              />
            </div>
            {Object.values(errors).length > 0 && (
              <div className="text-red-600 text-sm">{Object.values(errors)[0]}</div>
            )}

            {/* additional filters */}
            <div className="rounded-2xl border border-neutral-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium">Additional filters</div>
                <button type="button" onClick={() => setMore((v) => !v)} className="text-sm underline">
                  {more ? "Hide" : "See more"}
                </button>
              </div>
              {more && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                  <select className="border border-neutral-300 rounded-xl px-3 py-2">
                    <option value="">Property type</option>
                    <option>Apartment</option>
                    <option>House</option>
                    <option>Mews</option>
                    <option>Penthouse</option>
                    <option>Bungalow</option>
                  </select>
                  <select className="border border-neutral-300 rounded-xl px-3 py-2">
                    <option value="">Bedrooms</option>
                    <option>1+</option>
                    <option>2+</option>
                    <option>3+</option>
                    <option>4+</option>
                    <option>5+</option>
                  </select>
                  <select className="border border-neutral-300 rounded-xl px-3 py-2">
                    <option value="">Bathrooms</option>
                    <option>1+</option>
                    <option>2+</option>
                    <option>3+</option>
                    <option>4+</option>
                  </select>
                  <select className="border border-neutral-300 rounded-xl px-3 py-2">
                    <option value="">Outside space</option>
                    <option>Balcony/Terrace</option>
                    <option>Garden</option>
                    <option>Roof terrace</option>
                  </select>
                  <select className="border border-neutral-300 rounded-xl px-3 py-2">
                    <option value="">Tenure</option>
                    <option>Freehold</option>
                    <option>Leasehold</option>
                    <option>Share of Freehold</option>
                  </select>
                  <select className="border border-neutral-300 rounded-xl px-3 py-2">
                    <option value="">Parking</option>
                    <option>On-street</option>
                    <option>Off-street</option>
                    <option>Garage</option>
                    <option>Permit</option>
                  </select>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> New build
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> Lift / step-free
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> Concierge
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> Pets allowed
                  </label>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 items-center justify-center">
              <button className="bg-black text-white rounded-xl px-5 h-12 min-w-[150px] font-medium hover:bg-black/90 flex items-center justify-center">
                Next
              </button>
              <TalkToHomeAIButton onClick={() => setOpenAI(true)} className="min-w-[150px]" />
              <button
                type="button"
                onClick={() => setMapBrowse((v) => !v)}
                className="rounded-xl px-5 h-12 min-w-[150px] border border-neutral-300 bg-white hover:bg-neutral-100 flex items-center justify-center"
              >
                {mapBrowse ? "Hide Map view" : "Map view (beta)"}
              </button>
            </div>
          </form>

          {/* feature cards w/ icons */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {[
              { title: "Save favourites", desc: "Keep track of homes you love and revisit anytime.", icon: "♥" },
              { title: "Price-drop alerts", desc: "We’ll notify you if a saved home reduces in price.", icon: "£" },
              { title: "Shortlist", desc: "Build a shortlist to compare and share.", icon: <path d="M6 9l4 4 8-8M4 19h16" /> },
              { title: "Share easily", desc: "Send a link to family or your buying agent.", icon: IShare },
              { title: "Map browse", desc: "See homes in areas you care about.", icon: IMapPin }
            ].map((f, i) => (
              <div key={i} className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  {typeof f.icon === "string" ? (
                    <span className="text-xl font-semibold text-neutral-900">{f.icon}</span>
                  ) : (
                    <Icon path={f.icon} />
                  )}
                  <div className="font-medium">{f.title}</div>
                </div>
                <div className="text-neutral-600 text-sm mt-1">{f.desc}</div>
              </div>
            ))}
          </div>

          {/* map browse */}
          {mapBrowse && (
            <div className="mt-8">
              <MapSelector onConfirm={handleMapConfirm} onCancel={() => setMapBrowse(false)} />
            </div>
          )}
        </div>
      </div>

      <AIChatModal
        open={openAI}
        onClose={() => setOpenAI(false)}
        contextTitle="your property search"
      />
      <GoogleSignIn
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        onSignedIn={(session) => {
          if (session?.roles?.length) {
            window.location.href = "/crm";
          }
        }}
      />
    </main>
  );
}

function MapSelector({ onConfirm, onCancel }) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const addPoint = async ({ lat, lng }) => {
    setError("");
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.postcodes.io/postcodes?lon=${encodeURIComponent(lng)}&lat=${encodeURIComponent(lat)}`
      );
      const data = await response.json();
      const hit = data?.result?.[0];
      if (!hit || !hit.postcode) {
        throw new Error("No postcode at this location.");
      }
      if (selected.some((item) => item.postcode === hit.postcode)) {
        return;
      }
      setSelected((prev) => [
        ...prev,
        { postcode: hit.postcode, lat: hit.latitude, lon: hit.longitude }
      ]);
    } catch (err) {
      setError("Couldn’t resolve that spot to a postcode yet. Try another location.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 space-y-4">
      <div className="h-[360px] w-full">
        <MapContainer center={[52.3555, -1.1743]} zoom={6} scrollWheelZoom className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClickHandler onClick={addPoint} />
          {selected.map((point) => (
            <Marker key={point.postcode} position={[point.lat, point.lon]}>
              <Popup>{point.postcode}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="space-y-2 text-sm">
        <div className="font-semibold text-neutral-900">Selected postcodes</div>
        <div className="flex flex-wrap gap-2">
          {selected.length === 0 ? (
            <span className="text-xs text-neutral-500">Click on the map to add a postcode.</span>
          ) : (
            selected.map((point) => (
              <span
                key={point.postcode}
                className="flex items-center gap-1 rounded-full border border-neutral-300 bg-neutral-50 px-3 py-1 text-xs"
              >
                {point.postcode}
                <button
                  type="button"
                  onClick={() =>
                    setSelected((prev) => prev.filter((item) => item.postcode !== point.postcode))
                  }
                >
                  ✕
                </button>
              </span>
            ))
          )}
        </div>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!selected.length || loading}
          onClick={() => onConfirm(selected.map((point) => point.postcode))}
          className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {loading ? "Resolving…" : "Apply postcode filter"}
        </button>
        <button
          type="button"
          onClick={() => setSelected([])}
          disabled={!selected.length}
          className="rounded-xl border border-neutral-300 px-4 py-2 text-sm"
        >
          Clear list
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border border-neutral-300 px-4 py-2 text-sm"
        >
          Close
        </button>
      </div>
      <p className="text-xs text-neutral-500">
        Click anywhere on the map to capture that postcode. Confirm to prefill your search filters.
      </p>
    </div>
  );
}

function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng);
    }
  });
  return null;
}


/* ----------------------- Buyer Discover ----------------------- */
function BuyerDiscover() {
  const navigate = useNavigate();
  const [listings, setListings] = useState(LISTINGS);
  const [loadingListings, setLoadingListings] = useState(true);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookListing, setBookListing] = useState(null);
  const [openLogin, setOpenLogin] = useState(false);
  const [showCount, setShowCount] = useState(16);
  const [currency, setCurrency] = useCurrency();
  const [search, setSearch] = useState(null);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  // Load latest saved search
  useEffect(() => {
    async function loadSearch() {
      try {
        const latest = await latestSearch();
        if (latest?.filters) {
          setSearch(latest.filters);
        }
      } catch (error) {
        console.error("Failed to load search:", error);
      }
    }
    loadSearch();
  }, []);

  const budgetMaxValue = Number(search?.budgetMax || 0);
  const displayBudget = formatAmount(
    convertAmount(Number.isFinite(budgetMaxValue) ? budgetMaxValue : 0, "GBP", currency),
    currency
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getPublicListings();
        if (!mounted) return;
        if (Array.isArray(data.items)) {
          setListings(data.items);
        }
      } catch (err) {
        console.error("Failed to load listings:", err);
      } finally {
        if (mounted) setLoadingListings(false);
      }
    })();
    return () => {
      mounted = false;

      // Load AI recommendations
      useEffect(() => {
        let mounted = true;
        (async () => {
          setLoadingRecommendations(true);
          try {
            const data = await getAIRecommendations();
            if (!mounted) return;
            if (Array.isArray(data.recommendations)) {
              setRecommendations(data.recommendations);
            }
          } catch (err) {
            if (mounted) setRecommendations([]);
          } finally {
            if (mounted) setLoadingRecommendations(false);
          }
        })();
        return () => {
          mounted = false;
        };
      }, []);
    };
  }, []);

  const matches = useMemo(() => {
    if (!search) return [];
    return listings.filter((l) => listingMatches(search, l));
  }, [search, listings]);

  const searchTokens = useMemo(() => {
    if (!search) return [];
    return (
      String(search.areas || search.area || "")
        .split(/[,]+/)
        .map((token) => token.trim().toUpperCase())
        .filter(Boolean)
    );
  }, [search]);

  const outsideSorted = useMemo(() => {
    const outside = listings.filter((listing) => !matches.some((match) => match.id === listing.id));
    const score = (listing) => {
      const postcode = (listing.postcode || "").toUpperCase();
      const area = (listing.area || "").toUpperCase();
      return searchTokens.reduce((best, token) => {
        if (!token) return best;
        const candidate =
          postcode.startsWith(token)
            ? 0
            : postcode.includes(token)
            ? 1
            : area.startsWith(token)
            ? 2
            : area.includes(token)
            ? 3
            : 5;
        return Math.min(best, candidate);
      }, 10);
    };
    return outside.sort((a, b) => score(a) - score(b));
  }, [matches, searchTokens]);

  const featured = useMemo(() => {
    const fromMatches = matches.filter((l) => l.featured);
    return fromMatches.length ? fromMatches : listings.filter((l) => l.featured);
  }, [matches]);

  const mostViewed = useMemo(() => {
    const views = loadJSON(KEYS.VIEWS, {});
    const since = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const scores = Object.entries(views).map(([id, arr]) => [id, arr.filter((t) => t >= since).length]);
    const sorted = scores.sort((a, b) => b[1] - a[1]).slice(0, 8);
    const byId = Object.fromEntries(LISTINGS.map((l) => [String(l.id), l]));
    return sorted.map(([id]) => byId[String(id)]).filter(Boolean);
  }, []);

  const openListing = (l) => {
    setActive(l);
    setOpen(true);
  };
  const closeListing = () => {
    setOpen(false);
    setActive(null);
  };

  const openBooking = (l) => {
    setBookListing(l);
    setBookOpen(true);
  };
  const closeBooking = () => {
    setBookOpen(false);
    setBookListing(null);
  };

  const collections = [
    { name: "Airbnb-friendly", filter: (l) => /apartment|penthouse|mews/i.test(l.type || "") },
    { name: "Waterfront", filter: (l) => /harbour|marina|dock|quay|sea/i.test((l.title || "") + " " + (l.area || "")) },
    { name: "New build", filter: (l) => l.features?.some((f) => /new build/i.test(f)) },
    { name: "Family homes", filter: (l) => (l.beds || 0) >= 3 && /house|mews/i.test(l.type || "") }
  ];
  const collectionPicks = collections
    .map((c) => ({
      name: c.name,
      items: listings.filter(c.filter).slice(0, 4)
    }))
    .filter((c) => c.items.length);

  return (
    <main className="min-h-screen bg-neutral-50 text-neutral-900 p-6">
      <HomeButton />
      <div className="max-w-6xl mx-auto mt-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-[220px]">
            <h2 className="text-3xl font-semibold">Discover</h2>
            <p className="text-neutral-600 mt-1">
              {search ? (
                <>
                  Showing matches for <b>{displayBudget}</b> max in <b>{search.areas || "All areas"}</b>
                </>
              ) : (
                <>No saved search yet — set one up to personalise your feed.</>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <CurrencySwitcher value={currency} onChange={setCurrency} />
            <button
              onClick={() => navigate("/buyer")}
              className="border border-neutral-300 rounded-xl px-4 py-2 hover:bg-neutral-100"
            >
              Edit search
            </button>
            <button
              onClick={() => setOpenLogin(true)}
              className="rounded-xl px-4 py-2 border border-neutral-300 bg-white hover:bg-neutral-100"
            >
              Login
            </button>
            <TalkToHomeAIButton onClick={() => setAiChatOpen(true)} />
          </div>
        </div>

        {featured.length > 0 && (
          <section className="mt-6">
            <div className="flex items-baseline justify-between">
              <h3 className="text-xl font-semibold">Featured listings</h3>
              <span className="text-sm text-neutral-500">Swipe →</span>
            </div>
            <div className="mt-3 flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
              {featured.map((l) => (
                <div key={l.id} className="min-w-[260px] max-w-[260px] snap-start">
                  <ListingCard l={l} onOpen={openListing} onBook={openBooking} currency={currency} />

                        {!loadingRecommendations && recommendations.length > 0 && (
                          <section className="mt-6">
                            <div className="flex items-baseline justify-between">
                              <h3 className="text-xl font-semibold">
                                <span className="shine-text">✨ AI Recommendations</span>
                              </h3>
                              <span className="text-sm text-neutral-500">Swipe →</span>
                            </div>
                            <p className="text-sm text-neutral-600 mt-1 mb-3">
                              Based on your search preferences and browsing history
                            </p>
                            <div className="mt-3 flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
                              {recommendations.slice(0, 8).map((rec) => {
                                const listing = listings.find(l => l.id === rec.listingId) || rec.listing;
                                if (!listing) return null;
                                return (
                                  <div key={listing.id} className="min-w-[260px] max-w-[260px] snap-start">
                                    <div className="relative">
                                      <ListingCard l={listing} onOpen={openListing} onBook={openBooking} currency={currency} />
                                      {rec.score && (
                                        <div className="absolute top-2 right-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                                          {Math.round(rec.score * 100)}% match
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        )}
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-6">
          <h3 className="text-lg font-medium">All listings</h3>
          {matches.length === 0 && (
            <div className="mt-3 rounded-xl border border-dashed border-neutral-300 bg-white p-6 text-center text-neutral-700">
              <div className="text-base font-medium">No matches yet</div>
              <div className="text-sm mt-1">
                Try broadening your areas or budget. We'll show nearby options below.
              </div>
            </div>
          )}
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {matches.slice(0, showCount).map((l) => (
              <ListingCard key={l.id} l={l} onOpen={openListing} onBook={openBooking} currency={currency} />
            ))}
          </div>
          {matches.length > showCount && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowCount((n) => n + 16)}
                className="rounded-xl px-4 py-2 border border-neutral-300 bg-white hover:bg-neutral-100"
              >
                Show more
              </button>
            </div>
          )}
        </section>

        {(matches.length === 0 || LISTINGS.some((listing) => !matches.includes(listing))) && (
          <section className="mt-6">
            <h3 className="text-lg font-medium">Outside your criteria</h3>
            <div className="text-sm text-neutral-600">
              These are popular homes that don't yet match your postcode/budget.
            </div>
            <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {outsideSorted.slice(0, 8).map((l) => (
                <ListingCard key={l.id} l={l} onOpen={openListing} onBook={openBooking} currency={currency} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h3 className="text-lg font-medium">Most viewed in the last 7 days</h3>
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(mostViewed.length ? mostViewed : LISTINGS.slice(0, 8)).map((l) => (
              <ListingCard key={l.id} l={l} onOpen={openListing} onBook={openBooking} currency={currency} />
            ))}
          </div>
        </section>

        {collectionPicks.length > 0 && (
          <section className="mt-10">
            <h3 className="text-lg font-medium">Collections</h3>
            {collectionPicks.map((c, idx) => (
              <div key={idx} className="mt-4">
                <div className="text-sm text-neutral-600 mb-2">{c.name}</div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {c.items.map((l) => (
                    <ListingCard key={l.id} l={l} onOpen={openListing} onBook={openBooking} currency={currency} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>

      <ListingModal open={open} onClose={closeListing} listing={active} onBook={openBooking} currency={currency} />
      <ViewingFormModal open={bookOpen} onClose={closeBooking} listing={bookListing} />
      <AIChatModal
        open={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        contextTitle={search?.areas || "your property search"}
      />
      <GoogleSignIn
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        onSignedIn={(session) => {
          if (session?.roles?.length) {
            window.location.href = "/crm";
          }
        }}
      />
    </main>
  );
}

/* ----------------------- Thanks + Admin ----------------------- */
function SellerThanks() {
  return (
    <main className="min-h-screen bg-neutral-950 text-white p-6 grid place-items-center">
      <HomeButton />
      <div className="text-center max-w-md">
        <h2 className="text-3xl font-semibold">Thanks — we’ve got it</h2>
        <p className="mt-2 text-white/70">We’ll review your details and contact you to book an appraisal.</p>
        <a href="/" className="inline-block mt-6 border border-white/20 rounded-xl px-5 py-3 hover:border-white/40">
          Back to home
        </a>
      </div>
    </main>
  );
}

function BuyerThanks() {
  return (
    <main className="min-h-screen bg-neutral-50 text-neutral-900 p-6 grid place-items-center">
      <HomeButton />
      <div className="text-center max-w-md">
        <h2 className="text-3xl font-semibold">You’re on the list</h2>
        <p className="mt-2 text-neutral-600">We’ll send matches in your areas and budget.</p>
        <a href="/" className="inline-block mt-6 border border-neutral-300 rounded-xl px-5 py-3 hover:bg-neutral-100">
          Back to home
        </a>
      </div>
    </main>
  );
}

function AdminCrmApp({ session }) {
  const navItems = [
    { to: "/crm/dashboard", label: "Dashboard", icon: "🏠" },
    { to: "/crm/listings", label: "Listings", icon: "📋" },
    { to: "/crm/contacts", label: "Contacts", icon: "👥" },
    { to: "/crm/pipeline", label: "Pipeline", icon: "📊" },
    { to: "/crm/admin/deleted-contacts", label: "Deleted contacts", icon: "🗑️", adminOnly: true },
    { to: "/crm/viewings", label: "Viewings", icon: "🗓️" },
    { to: "/crm/offers", label: "Offers", icon: "💬" },
    { to: "/crm/sales", label: "Sales", icon: "🔁" },
    { to: "/crm/letters", label: "Letters", icon: "✉️" },
    { to: "/crm/payments", label: "Payments", icon: "💳" },
    { to: "/crm/aml", label: "AML Review", icon: "🔍", adminOnly: true },
    { to: "/crm/audit-log", label: "Audit Log", icon: "📜", adminOnly: true },
    { to: "/crm/ai-logs", label: "AI Logs", icon: "🤖", adminOnly: true },
    { to: "/crm/gdpr", label: "GDPR Tools", icon: "🔒" },
    { to: "/crm/settings", label: "Settings", icon: "⚙️" }
  ];

  const title = sessionHasRole(session, Roles.ADMIN) ? "Admin control centre" : "Agent workspace";

  return (
    <Routes>
      <Route element={<CrmLayout session={session} navItems={navItems} title={title} />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<CrmDashboardPage />} />
        <Route path="listings" element={<CrmListingsPage />} />
        <Route path="listings/:id" element={<CrmListingDetailPage />} />
        <Route path="contacts" element={<CrmContactsPage />} />
        <Route path="contacts/new" element={<CrmContactCreatePage />} />
        <Route path="pipeline" element={<CrmPipelinePage />} />
        <Route path="admin/deleted-contacts" element={<CrmDeletedContactsPage />} />
        <Route path="viewings" element={<CrmViewingsPage />} />
        <Route path="offers" element={<CrmOffersPage />} />
        <Route path="sales" element={<CrmSalesPage />} />
        <Route path="letters" element={<CrmLettersPage />} />
        <Route path="payments" element={<CrmPaymentHistoryPage />} />
        <Route path="aml" element={<CrmAmlReviewPage />} />
        <Route path="audit-log" element={<CrmAuditLogPage />} />
        <Route path="ai-logs" element={<CrmAiLogsPage />} />
        <Route path="gdpr" element={<CrmGdprPage />} />
        <Route path="settings" element={<CrmSettingsPage />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function CrmRouter() {
  const { session, loading } = useCurrentSession();
  
  // Wait for session to load before making routing decisions
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }
  
  if (!session) return <Navigate to="/" replace />;

  if (sessionHasRole(session, Roles.ADMIN) || sessionHasRole(session, Roles.AGENT)) {
    return <AdminCrmApp session={session} />;
  }

  if (sessionHasRole(session, Roles.SELLER) && sessionHasRole(session, Roles.BUYER)) {
    return <BuyerSellerPortal session={session} />;
  }

  if (sessionHasRole(session, Roles.SELLER)) {
    return <SellerPortal session={session} />;
  }

  if (sessionHasRole(session, Roles.BUYER)) {
    return <BuyerPortal session={session} />;
  }

  return <Navigate to="/" replace />;
}

function AdminConsole() {
  const mockUsers = [
    {
      email: "seller@test.com",
      name: "Seller Summit",
      role: "Seller",
      status: "Active",
      listings: ["w8-123"]
    },
    {
      email: "valuation@test.com",
      name: "Valuation Preview",
      role: "Seller",
      status: "Prospect",
      listings: []
    },
    {
      email: "buyer@test.com",
      name: "Buyer Beta",
      role: "Buyer",
      status: "Engaged",
      listings: []
    },
    {
      email: "buyerseller@test.com",
      name: "Hybrid Hero",
      role: "Buyer & Seller",
      status: "Active",
      listings: ["w8-123"]
    },
    {
      email: "agent@test.com",
      name: "Agent Apex",
      role: "Agent",
      status: "Team",
      listings: LISTINGS.slice(0, 2).map((l) => l.id)
    }
  ];

  const listingById = useMemo(
    () => Object.fromEntries(LISTINGS.map((listing) => [listing.id, listing])),
    []
  );

  const [assignForm, setAssignForm] = useState({
    userEmail: mockUsers[0]?.email || "",
    listingId: LISTINGS[0]?.id || ""
  });
  const [assignmentLog, setAssignmentLog] = useState([]);
  const [newListingForm, setNewListingForm] = useState({
    title: "",
    price: "",
    ownerEmail: mockUsers.find((user) => user.role.includes("Seller"))?.email || ""
  });
  const [listingLog, setListingLog] = useState([]);

  const handleAssignListing = () => {
    if (!assignForm.userEmail || !assignForm.listingId) return;
    const entry = {
      userEmail: assignForm.userEmail,
      listingId: assignForm.listingId,
      timestamp: Date.now()
    };
    setAssignmentLog((prev) => [entry, ...prev.slice(0, 9)]);
    console.log("Linked listing to user (demo only).");
  };

  const handleMockUpload = () => {
    if (!newListingForm.title.trim()) return;
    const entry = {
      title: newListingForm.title.trim(),
      price: newListingForm.price.trim(),
      ownerEmail: newListingForm.ownerEmail,
      timestamp: Date.now()
    };
    setListingLog((prev) => [entry, ...prev.slice(0, 9)]);
    setNewListingForm((prev) => ({ ...prev, title: "", price: "" }));
    console.log("Stored draft listing details for agent review (demo only).");
  };

  const userAssignments = useMemo(() => {
    const base = mockUsers.map((user) => ({
      ...user,
      assignedListings: [...(user.listings || [])]
    }));
    assignmentLog.forEach((entry) => {
      const user = base.find((candidate) => candidate.email === entry.userEmail);
      if (user && !user.assignedListings.includes(entry.listingId)) {
        user.assignedListings.push(entry.listingId);
      }
    });
    return base;
  }, [mockUsers, assignmentLog]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <HomeButton />
      <div className="max-w-6xl mx-auto">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-[0.32em] text-slate-500">Home / The Break Co.</p>
          <h1 className="text-3xl font-semibold text-white">Internal CRM Blueprint</h1>
          <p className="text-slate-400 max-w-3xl">
            You are a senior full-stack engineer. Build an end-to-end real-estate CRM inspired by Street/Rex
            that powers agent, seller, and buyer workflows for the HOME collective. This brief outlines the full
            product surface area to plan against.
          </p>
        </header>
        <div className="mt-8 space-y-6">
          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-lg">
            <h2 className="text-xl font-semibold text-white mb-3">1) High-level App Goals</h2>
            <p className="text-slate-200 text-sm mb-3">
              Deliver a connected CRM experience with portals tailored per persona and bi-directional data links
              across listings, contacts, viewings, offers, sales progression, documents, and activity.
            </p>
            <ul className="text-slate-200 text-sm space-y-2 list-disc pl-6">
              <li>Agents: full-funnel control — listings, viewings, offers, sales progression, comms, reporting.</li>
              <li>Sellers: transparent portal with documents, feedback, offers, milestones, availability controls.</li>
              <li>Buyers: discovery portal with search, save/share, viewings, offers, updates.</li>
              <li>Ensure every entity is linkable (e.g., viewing ↔ attendees, offers ↔ sales progression).
              </li>
            </ul>
            <p className="text-xs text-slate-500 mt-4">
              Tech stack: React, React Router, Tailwind, shadcn/ui, Framer Motion · Node.js (Express + TypeScript) ·
              PostgreSQL with Prisma · Google OAuth → JWT sessions · S3-compatible storage · Gmail API · Google Calendar sync ·
              Xero (phase 2) · Monorepo via pnpm workspaces · Docker · Render deploy · CI with GitHub Actions.
            </p>
          </section>

          {/* Additional blueprint sections preserved as in original design */}

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-white">User directory</h2>
              <span className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Internal use
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left text-slate-200">
                <thead className="text-xs uppercase tracking-[0.25em] text-slate-400">
                  <tr>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Linked listings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {userAssignments.map((user) => (
                    <tr key={user.email}>
                      <td className="py-2 pr-4 text-white">{user.name}</td>
                      <td className="py-2 pr-4 text-white/70">{user.email}</td>
                      <td className="py-2 pr-4">{user.role}</td>
                      <td className="py-2 pr-4">
                        <span
                          className={
                            "rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em] " +
                            (user.status === "Active"
                              ? "bg-emerald-500/15 text-emerald-200"
                              : user.status === "Prospect"
                              ? "bg-amber-500/20 text-amber-200"
                              : "bg-white/10 text-white/60")
                          }
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-white/70">
                        {user.assignedListings.length
                          ? user.assignedListings
                              .map((id) => listingById[id]?.title || id)
                              .join(", ")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-white">Link listings to users</h2>
              <span className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Agent tools
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-sm text-white/80">
              <select
                className="rounded-xl border border-white/15 bg-black/30 px-3 py-2 focus:border-white/40 focus:outline-none"
                value={assignForm.userEmail}
                onChange={(e) => setAssignForm((prev) => ({ ...prev, userEmail: e.target.value }))}
              >
                {mockUsers.map((user) => (
                  <option key={user.email} value={user.email}>
                    {user.name} — {user.role}
                  </option>
                ))}
              </select>
              <select
                className="rounded-xl border border-white/15 bg-black/30 px-3 py-2 focus:border-white/40 focus:outline-none"
                value={assignForm.listingId}
                onChange={(e) => setAssignForm((prev) => ({ ...prev, listingId: e.target.value }))}
              >
                {LISTINGS.map((listing) => (
                  <option key={listing.id} value={listing.id}>
                    {listing.title} ({listing.postcode})
                  </option>
                ))}
              </select>
              <button
                onClick={handleAssignListing}
                className="rounded-xl bg-white text-black px-4 py-2 font-medium hover:bg-white/90 transition"
              >
                Link listing
              </button>
            </div>
            {assignmentLog.length > 0 && (
              <ul className="text-xs text-slate-400 space-y-1">
                {assignmentLog.map((entry, idx) => (
                  <li key={`${entry.userEmail}-${entry.listingId}-${idx}`}>
                    {new Date(entry.timestamp).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}{" "}
                    — Linked {listingById[entry.listingId]?.title || entry.listingId} to{" "}
                    {entry.userEmail}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-white">Upload new listing draft</h2>
              <span className="text-xs uppercase tracking-[0.3em] text-slate-400">Internal draft</span>
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-sm text-white/80">
              <input
                className="rounded-xl border border-white/15 bg-black/30 px-3 py-2 focus:border-white/40 focus:outline-none"
                placeholder="Listing headline"
                value={newListingForm.title}
                onChange={(e) => setNewListingForm((prev) => ({ ...prev, title: e.target.value }))}
              />
              <input
                className="rounded-xl border border-white/15 bg-black/30 px-3 py-2 focus:border-white/40 focus:outline-none"
                placeholder="Guide price (£)"
                value={newListingForm.price}
                onChange={(e) => setNewListingForm((prev) => ({ ...prev, price: e.target.value }))}
              />
              <select
                className="rounded-xl border border-white/15 bg-black/30 px-3 py-2 focus:border-white/40 focus:outline-none"
                value={newListingForm.ownerEmail}
                onChange={(e) => setNewListingForm((prev) => ({ ...prev, ownerEmail: e.target.value }))}
              >
                {mockUsers
                  .filter((user) => user.role.toLowerCase().includes("seller"))
                  .map((user) => (
                    <option key={user.email} value={user.email}>
                      {user.name}
                    </option>
                  ))}
              </select>
            </div>
            <button
              onClick={handleMockUpload}
              className="rounded-xl bg-white text-black px-4 py-2 font-medium hover:bg-white/90 transition"
            >
              Save draft listing
            </button>
            {listingLog.length > 0 && (
              <ul className="text-xs text-slate-400 space-y-1">
                {listingLog.map((entry, idx) => (
                  <li key={idx}>
                    {new Date(entry.timestamp).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}{" "}
                    — Draft “{entry.title}” recorded for {entry.ownerEmail || "unassigned"} at {entry.price || "£TBC"}
                  </li>
                ))}
              </ul>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}

function DevRoleToggle() {
  const { session } = useCurrentSession();
  const [_, force] = useState(0);

  const setMockSession = (email) => {
    const account = MOCK_ACCOUNTS.find((acc) => acc.email === email);
    if (account) {
      signInSession({
        email: account.email,
        name: account.name,
        avatar: account.avatar,
        roles: account.roles,
        provider: "dev"
      });
      force((n) => n + 1);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[80]">
      <div className="text-[10px] text-white/60 mb-1">
        dev session: {deriveSessionLabel(session)}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => {
            clearSession();
            force((n) => n + 1);
          }}
        >
          guest
        </button>
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => {
            signInSession({
              email: "dev-admin@home.local",
              name: "Dev Admin",
              roles: [Roles.ADMIN, Roles.AGENT],
              provider: "dev"
            });
            force((n) => n + 1);
          }}
        >
          admin
        </button>
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => setMockSession("buyer@test.com")}
        >
          buyer
        </button>
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => setMockSession("seller@test.com")}
        >
          seller
        </button>
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => setMockSession("buyerseller@test.com")}
        >
          dual
        </button>
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => setMockSession("agent@test.com")}
        >
          agent
        </button>
        <button
          className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20"
          onClick={() => setMockSession("valuation@test.com")}
        >
          valuation
        </button>
      </div>
    </div>
  );
}

function AppInner() {
  const location = useLocation();
  const { session } = useCurrentSession();
  const hideNav =
    !session ||
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname.startsWith("/buyer");
  const showDevControls =
    import.meta.env.DEV || String(import.meta.env.VITE_SHOW_DEV_CONTROLS).toLowerCase() === "true";

  // Clean up legacy localStorage keys on mount
  useEffect(() => {
    cleanupLegacyStorage();
  }, []);

  // Migrate seller localStorage data to database on login
  // DISABLED IN PRODUCTION: Migration only needed during development
  useEffect(() => {
    if (import.meta.env.DEV && session?.email) {
      // Run migration in background (development only)
      migrateSellerDataIfNeeded()
        .then(result => {
          if (result.migrated) {
            console.log("Seller data migrated successfully");
          }
        })
        .catch(error => {
          console.error("Migration failed:", error);
        });
    }
  }, [session?.email]);

  return (
    <>
      {!hideNav && <TopNav />}
      <Routes>
        <Route path="/" element={<ComingSoon />} />
        <Route path="/login" element={<Landing />} />
        <Route path="/seller" element={<SellerStart />} />
        <Route path="/seller/valuation" element={<SellerValuation />} />
        <Route path="/seller/how-home-ai-works" element={<SellerHowItWorks />} />
        <Route path="/seller/checkout" element={<SellerCheckout />} />
        <Route path="/seller/checkout/success" element={<SellerCheckoutSuccess />} />
        <Route path="/seller/consultation" element={<SellerConsultation />} />
        <Route path="/seller/thanks" element={<SellerThanks />} />
        <Route path="/seller/compliance" element={<SellerCompliance />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/buyer" element={<BuyerStart />} />
        <Route path="/buyer/discover" element={<BuyerDiscover />} />
        <Route path="/buyer/my-offers" element={<BuyerMyOffers session={session} />} />
        <Route path="/buyer/thanks" element={<BuyerThanks />} />
        <Route
          path="/console"
          element={
            <RequireRoles allowed={[Roles.ADMIN, Roles.AGENT]}>
              <AdminConsole />
            </RequireRoles>
          }
        />
        <Route path="/crm/*" element={<CrmRouter />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showDevControls && <DevRoleToggle />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
